import { Component, createRef } from 'preact';
import { toast } from 'sonner';
import { getCanvasScale } from '../utils/uiScale';

import { createGameState, shufflePile } from '../utils/game/gameState';
import { mergeRollStates } from '../utils/game/net/rollStateMerge';
import { resolveAvatarRules } from '../utils/game/avatarRules';
import { getGameGrid } from '../utils/game/spawnConfig';
import { getCellAt, getCellNumber, computeGridPositions } from '../utils/game/grid';
import { isDevBuild } from '../utils/devMode';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { inputSource } from '../utils/input/inputSource';
import { loadGameSession } from '../utils/game/sessionStorage';
import { sendMatchCommand, sendMatchIntent, requestMatchFull } from '../utils/game/socketClient';
import * as combatIntents from '../utils/game/net/combatIntents';
import * as resourceIntents from '../utils/game/net/resourceIntents';
import { castPlayIntent } from '../utils/game/net/castIntents';
import { moveIntent } from '../utils/game/net/movementIntents';
import { triggerIntent } from '../utils/game/net/automationIntents';
import { activateAbilityIntent } from '../utils/game/net/abilityIntents';
import { devLog } from '../utils/game/net/devLog';
// Combat-trigger silent ops the SERVER resolves authoritatively (P6). Must stay
// byte-aligned with valkenhall-server/src/match/automationAdapter.js
// SERVER_SILENT_OPS. On the auth path an entry whose ops are ALL in this set is
// resolved via an automation:trigger intent (server mutates the draft, both
// clients render the diff) instead of mutating locally. An entry with any op
// outside this set stays on the client path unchanged.
import { SERVER_SILENT_OPS, SERVER_AUTONOMOUS_TRIGGERS } from '../utils/game/net/automationIntents';
import * as authCombat from './gameBoard/controllers/authCombat';
import { REQUEST_ROLL, SET_FIRST_PLAYER, SET_PILE_REVEALED, SET_UNIT_HANDS } from '../utils/game/net/matchCore';
import * as zoneCmd from './gameBoard/controllers/authZones';
import * as authReveals from './gameBoard/controllers/authReveals';
import * as authChoice from './gameBoard/controllers/authChoice';
import { isAuthMatchEnabled } from '../utils/game/net/authMatchFlag';
import { getControlSettings } from '../utils/game/controlSettings';
import { resolveSoloActingSeat } from '../utils/game/net/soloActingSeat';
import { createCardMesh } from '../utils/game/cardMesh';
import { animateCardToCell, animateCardToPile } from '../utils/game/animations';
import { createCardAura, updateCardAura, disposeCardAura } from '../utils/game/visualEffects/cardAura';
import { howlDurationMs, HOWL_DEAL_MS, HOWL_HOLD_MS, HOWL_RESOLVE_MS } from '../utils/game/engine/automation/ops/cards/howlFromBeyond';
import { GameSyncBridge } from '../utils/game/syncBridge';
import { MatchRecorder } from '../utils/game/replay/MatchRecorder';
// v1.39. Engine split: core/ owns Sorcery rules that exist regardless
// of any card text; automation/ owns the card-text pipeline (parser,
// ledger, prompts, scope filters) and per-op resolvers (ops/*). Two
// imports keep the boundary loud.
import { cellsWithSites, sampleCellGeometry, countsAsUnit, hasBurrowing, hasSubmerge, reachableLocationsInSteps, currentRegion, hasSummoningSickness, PHASE, siteSnapPoint, isAtDeathsDoor, applyLifeLoss, PlayerResources, CardIdentityVault, getEligibleDefenders, makeTimestampGen, getProjectileHits, getRangedValue, hittableUnitsAt, projectilePaths, getMoveAndAttackTargets, getInterceptCandidates, effectivePower, effectiveLife, effectiveMovementBonus, unitFootprintCells, markSkipNextTurn } from '../utils/game/engine/core';
import { resolveActingSeat } from '../utils/game/engine/core/combatEngine';
import { buildAttackTargetDescriptor } from '../utils/game/engine/core/combat';
import { reconcileAuthCombat } from '../utils/game/engine/core/combatReconcile';
import { hasActiveStealth } from '../utils/game/engine/core/stealth';
import { hasKeyword } from '../utils/game/engine/core/keywordQuery';
import CombatMatchupPreview from './gameBoard/CombatMatchupPreview';
import { TrackerLogCoalescer } from '../utils/game/trackerLogCoalescer';
import { EffectLedger, OP, TRIGGER, resolveAuraScope, cellAtWorld, hasActiveWard, isSpellcaster, findValidSpellcasters, cardThresholds, evaluateCondition, getEffectiveStatuses, isDisabled, applyStatusToggle, topAtlasSite, isLegalMaskTarget, cemeteryHasEnoughMagic, cemeteryHasMinion, findDamageReplacements } from '../utils/game/engine/automation';
import { isCardSilenced } from '../utils/game/engine/core/keywordQuery';
import { transientCombatEntries, consumeOneShotTransients } from '../utils/game/engine/automation/ops/strikeLifelink';
import { createPingRateLimiter } from '../utils/game/pingRateLimit';
import { moveKinematicBody, removeCardBody } from '../utils/game/physicsWorld';
import { CardOwnership } from '../utils/game/cardOwnership'
import { perf } from '../utils/perfMonitor';
import { getSoundSettings } from '../utils/arena/soundSettings';
import { getLogbookTab } from '../utils/arena/logbookSettings';
import { getAmbienceState } from '../utils/arena/ambienceManager';
import { getViewportScale } from '../lib/medievalTheme';
import * as THREE from 'three';
import { getDiscordSettings } from '../utils/arena/discordSettings';
import { k, t } from '../localization/t';
import { msg } from '../utils/game/logMessage';
import * as scryController from './gameBoard/controllers/scryController';
import * as mulliganController from './gameBoard/controllers/mulliganController';
import * as gamepadController from './gameBoard/controllers/gamepadController';
import * as devEditors from './gameBoard/controllers/devEditors';
import * as harbingerCeremony from './gameBoard/controllers/harbingerCeremony';
import * as trackerHud from './gameBoard/controllers/trackerHud';
import * as avatarAbilities from './gameBoard/controllers/avatarAbilities';
import * as nameSpellPrompt from './gameBoard/controllers/nameSpellPrompt';
import * as boardRender from './gameBoard/renderBody';
import * as socketListeners from './gameBoard/controllers/socketListeners';
import * as authResources from './gameBoard/controllers/authResources';
import * as authCardState from './gameBoard/controllers/authCardState';
import * as pointerInteractions from './gameBoard/controllers/pointerInteractions';
import * as staticAuras from './gameBoard/controllers/staticAuras';
import * as hotkeys from './gameBoard/controllers/hotkeys';
import * as automationRuntime from './gameBoard/controllers/automationRuntime';
import * as pileMultiSelect from './gameBoard/controllers/pileMultiSelect';
import * as turnPhaseRunner from './gameBoard/controllers/turnPhaseRunner';
import * as sessionPersistence from './gameBoard/controllers/sessionPersistence';
import * as overlayProjections from './gameBoard/controllers/overlayProjections';
import * as cellStackFan from './gameBoard/controllers/cellStackFan';
import * as cellFocus from './gameBoard/controllers/cellFocus';
import * as eventTutorials from './gameBoard/controllers/eventTutorials';
import * as pendingMoves from './gameBoard/controllers/pendingMoves';
import * as cardPileLifecycle from './gameBoard/controllers/cardPileLifecycle';
import * as boardLifecycle from './gameBoard/controllers/boardLifecycle';
import * as cardStateOps from './gameBoard/controllers/cardStateOps';
import * as boardReconciler from './gameBoard/controllers/boardReconciler';
import * as damagePipeline from './gameBoard/controllers/damagePipeline';
import * as spawnPlacement from './gameBoard/controllers/spawnPlacement';
import * as realmTriggers from './gameBoard/controllers/realmTriggers';
import * as gameLifecycle from './gameBoard/controllers/gameLifecycle';
import * as tokensAndSearch from './gameBoard/controllers/tokensAndSearch';
import * as diceTrayDrag from './gameBoard/controllers/diceTrayDrag';
import * as authDice from './gameBoard/controllers/authDice';
import * as viewTransitions from './gameBoard/controllers/viewTransitions';
import * as stackPhysics from './gameBoard/controllers/stackPhysics';
import * as selectionGroups from './gameBoard/controllers/selectionGroups';
import * as handState from './gameBoard/controllers/handState';
import * as logbookChat from './gameBoard/controllers/logbookChat';
import * as menusAndInspector from './gameBoard/controllers/menusAndInspector';
import * as equipActions from './gameBoard/controllers/equipActions';
import * as opponentPresence from './gameBoard/controllers/opponentPresence';






// Combat liveness cadence. The waiting-lock reconcile runs on this interval,
// so a single dropped combat frame can never permanently strand a client.
const COMBAT_RECONCILE_MS = 2000;

export default class GameBoard extends Component {
  constructor(props) {
    super(props);

    this.canvasRef = createRef();
    this.scene = null;
    this.physicsWorld = null; // initialized async in componentDidMount
    this.unsubFrame = null;
    // Tracks per-card ownership (local/remote/free), broadcast throttling,
    // and the remote-side snapshot interpolation buffer.
    this.ownership = new CardOwnership();
    // Remembers the identity (name/cardId/imageUrl/foiling) of every card
    // this client has legitimately known, keyed by stable instance id.
    // Re-hydrates a card when an inbound sync hands back a sanitized
    // face-down copy of something we already know. Without it, a
    // non-owner moving a face-down card into a synced zone (pile/hand)
    // permanently wipes the card's identity on every client. See
    // engine/core/cardIdentityVault.js.
    this.identityVault = new CardIdentityVault();
    // Tracks deterministic per-card automation (e.g. site "Provides X this turn"
    // grants). Mutations are mirrored to the opponent via the sync bridge.
    this.effectLedger = new EffectLedger();
    // Single entry point for every player-resource mutation (life,
    // mana, Death's Door immunity). Engine-owned; this inline host
    // adapter is the only bridge back to broadcast / HUD refresh /
    // game-flow hooks. Adding new resource ops to the engine class
    // lights up every caller without GameBoard changes.
    this.playerResources = new PlayerResources({
      state: () => this.state.gameState,
      broadcastTracker: (player, key, value) => this._broadcastTracker(player, key, value),
      refresh: () => {
        this.updateTrackerTokenPositions();
        this.forceUpdate();
      },
      isGameOver: () => !!this._gameOverWinner,
      onLifeLoss: (player, amount, sourceLabel) => {
        if (sourceLabel) {
          const name = this._displayNameForPlayer(player);
          this.logAction('{name} loses {n} life ({source})', { name, n: amount, source: sourceLabel });
        }
      },
      // Imposter demask. Codex "until damaged" means Avatar damage
      // only, never bare life loss (site hits, pay-life costs route
      // through loseLife and must NOT pop the mask).
      onAvatarDamage: (player) => {
        this._demaskOnAvatarDamage(player);
      },
      // Synced identity of the current turn. Death's Door immunity is
      // derived from "did they reach 0 THIS turn", so both clients
      // agree without a cleanup step.
      turnKey: () => `${this.state.currentTurn}:${this.state.turnNumbers?.[this.state.currentTurn] || 0}`,
      onEnterDeathsDoor: (player) => {
        const who = this._displayNameForPlayer(player);
        this.logSystem("{name} is at Death's Door. Immune to death blows this turn.", { name: who });
        toast.warning(t("{name} is at Death's Door.", { name: who }));
      },
      onClearDeathsDoorImmunity: (player) => {
        const who = this._displayNameForPlayer(player);
        this.logSystem('{name} is no longer immune. Any Avatar damage now ends the game.', { name: who });
      },
      onHeal: (player, gained, sourceLabel) => {
        if (sourceLabel) {
          const prev = this.playerResources.getLife(player) - gained;
          this.logAction('{source} heals {n} ({prev}→{next})', { source: sourceLabel, n: gained, prev, next: prev + gained });
        }
      },
      onHealFailed: (player, sourceLabel) => {
        this.logAction("{source}: heal fails at Death's Door", { source: sourceLabel || msg('Source') });
      },
      attemptDeathReplacement: (player, sourceLabel) =>
        this._attemptPlayerDeathReplacement(player, sourceLabel),
      declareGameOver: (winner, reason) => this._declareGameOver(winner, reason),
      // P4 cutover: on the auth path an AUTOMATED resource flow sends a resource
      // intent so the SERVER computes life/mana + owns the death's-door immunity
      // / death-blow decision (closes the client-authored deathsDoorTurnKey
      // hole). Returns true to suppress the legacy absolute SET_* broadcast.
      emitResourceIntent: (player, kind, amount) => this._emitResourceIntent(player, kind, amount),
    });
    this._activeCombatPrompt = null;
    // Armed during a defend/intercept prompt:
    //   { kind, combatId, contestedCell, onCellIds:Set, moveIds:Set,
    //     eligibleIds:Set, chosen:Set, draggedOrigins:Map }.
    // Drives board highlights + click-to-select + the isolated off-cell
    // defender drag in the pointer handlers.
    this._combatSelect = null;
    // Set when a move opens the move-or-attack prompt, so Cancel can revert.
    this._combatMoveRevert = null;
    // Host adapter for the engine's silent-op dispatcher. Narrow
    // surface. Every method here is something the ops module
    // genuinely needs, nothing more. Adding a new silent op typically
    // means adding ONE method here (or none, if the existing surface
    // suffices); GameBoard never grows new dispatch code.
    this._silentOpsHost = {
      meshesHas:        (id) => this.meshes.has(id),
      routeWildfireMove: (card, controller) => automationRuntime.routeWildfireMove(this, card, controller),
      logAction:        (key, values) => this.logAction(key, values),
      sendToCemetery:   (card) => this.sendCardToPile(card, 'Cemetery'),
      sendToHand:       (card) => this._returnCardToOwnerHand(card),
      turnSiteToRubble: (card, opts) => this.turnToRubble(card, opts),
      setUntapped:      (card) => {
        const mesh = this.meshes.get(card.id);
        if (mesh) this._applyTap(card, mesh, false, { source: 'automation' });
      },
      // These four act on a single target card and look up its mesh.
      // A carried target (tagged `_carrierId` by resolveScope) has no own
      // mesh, so detach it to the field first. Then a location effect
      // (submerge/destroy/damage/teleport everything here) reaches
      // equipped artifacts and carried units like any on-table card.
      forceSubsurface:  (card, region) => this._setSubsurface(this._materializeCarried(card), region),
      resolveScope:     (sourceCard, scope, args) => this._resolveAuraScope(sourceCard, scope, args, {}),
      damageTarget:     (card, amount, opts) => this._applyDamageToTarget(this._materializeCarried(card), amount, opts),
      killTarget:       (card) => { const c = this._materializeCarried(card); return this._executeTargetOp({ op: OP.KILL_TARGET, args: {}, sourceCard: c, controller: c.rotated ? 'p2' : 'p1' }, c); },
      iterateEachOp:    (sourceCard, controller, op, args) => this._iterateEachOp(sourceCard, controller, op, args),
      detachEquippedFromHost: (host, equippedId) => {
        if (!host?.equippedCards?.length) return;
        const idx = host.equippedCards.findIndex((c) => c.id === equippedId);
        if (idx === -1) return;
        host.equippedCards.splice(idx, 1);
        this._clearEquippedArtifactEffects(equippedId);
        this._broadcastCardEquipment(host.id, host.equippedCards);
      },
      findCatalogToken: (name) => this._findCardCatalogToken(name),
      spawnTokenAt:     (cardRow, rotated, point) => this._spawnTokenAtPoint(cardRow, rotated, point),
      getMeshes:        () => this.meshes,
      getCellAt:        () => this._cellAtWorld,
      rng:              () => Math.random(),
      drawSpellsFor:    (controller, count) => {
        // Draw `count` spells into a specific player's hand (Seelie
        // Court fans the Faerie count to BOTH seats). Routes through the
        // unified drawCard funnel so the draw + on_draw watchers + sync
        // all fire normally for each seat.
        const pile = this.findPileByNameForPlayer('Spellbook', controller === 'p2' ? 2 : 1);
        if (!pile) return 0;
        let drawn = 0;
        for (let i = 0; i < count && pile.cards.length > 0; i++) {
          this.drawCard(pile.id, { skipLog: true });
          drawn += 1;
        }
        return drawn;
      },
      cellOfCard:       (card) => {
        const mesh = this.meshes.get(card?.id);
        return mesh ? this._cellAtWorld(mesh.position.x, mesh.position.z) : null;
      },
      moveCardToCell:   (card, cell, label) => this._moveCardToCell(this._materializeCarried(card), cell, label, k('teleports'), 'forced'),
      syncRealmArchive: (card) => {
        if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
        this._sendMatchCommand(authCardState.flagsCommand(card.id, { realmArchive: card._realmArchive ?? null }));
      },
      playTopAtlasAtCell: (controller, cell) => {
        // Pull the controller's atlas top and spawn it at `cell` as a
        // site. No-op when the atlas is empty or the top card isn't a
        // Site. Matches Sorcery's "if able" implicit semantics.
        const controllerRotated = controller === 'p2';
        const { atlas, top } = this._getControllerAtlas(controllerRotated);
        if (!atlas || !top?.fullCard || top.fullCard.type !== 'Site') {
          this.logAction('No atlas-top site available; nothing spawned.');
          return;
        }
        const grid = getGameGrid(this.state.spawnConfig);
        const geo = grid && sampleCellGeometry(grid, cell.col, cell.row);
        const snap = geo && siteSnapPoint(geo, controllerRotated);
        if (!snap) return;
        // Pop the instance out of atlas and re-anchor it at the cell.
        const idx = atlas.cards.indexOf(top.instance);
        if (idx >= 0) atlas.cards.splice(idx, 1);
        const inst = top.instance;
        inst.x = snap.x;
        inst.z = snap.z;
        inst.rotated = controllerRotated;
        this.addCardToTable(inst);
        this.applySitePlayEffects(inst);
        this._fireRealmTrigger(TRIGGER.ON_REALM_ENTRY, inst);
      },
      moveAuraToCell:   (card, dest) => {
        if (!this.meshes.get(card.id) || !dest?.position) return;
        // Keep the aura anchor in lockstep with the new cell. commitCardMove
        // broadcasts the move (body quaternion via the card:release transport)
        // and carries `_visitedCells` so Wildfire's after_each_turn doesn't
        // oscillate when the opponent runs its own pass from a stale list.
        card._auraAnchor = { col: dest.col, row: dest.row };
        const extras = Array.isArray(card._visitedCells)
          ? { _visitedCells: [...card._visitedCells] }
          : null;
        cardStateOps.commitCardMove(this, card, { x: dest.position.x, z: dest.position.z }, { extras });
        this.recomputeStaticAuras();
      },
      skipNextTurnForPlayer: (player) => {
        const avatar = this._findAvatarForPlayer(player === 'p2' ? 2 : 1);
        if (!avatar) return;
        markSkipNextTurn(avatar);
        // Mirror the Waveshaper skip-untap pattern: the flag is set on
        // whichever client resolved the effect, but consumed on the
        // affected player's own client when their START phase runs.
        this._broadcastSkipNextTurn(avatar.id);
        this.logAction('{name} will skip their next turn', { name: avatar.name || (player === 'p1' ? msg('Player 1') : msg('Player 2')) });
      },
      grantKeywordToCard: (card, keyword) => {
        if (!card) return;
        if (!card.statuses) card.statuses = [];
        if (!card.statuses.includes(keyword)) card.statuses.push(keyword);
        // Charge bypasses summoning sickness. Clear the flag so the
        // unit can act on its summoning turn.
        if (keyword === 'charge' && card._hasSummoningSickness) {
          delete card._hasSummoningSickness;
        }
        // Broadcast the status add to the opponent. The receiver's
        // `card:status` handler runs `applyStatusToggle` symmetrically
        // For 'charge' specifically we extended that handler to
        // also clear `_hasSummoningSickness`, mirroring the local
        // side's behavior so both clients agree the minion can act.
        this._broadcastCardStatus(card.id, keyword, true);
        this.rebuildStatusBadges?.(card.id);
        this.recomputeStaticAuras();
      },
      // Sir Perceval Genesis / re-arm: open an OPPONENT cell-pick over
      // every cell holding a Site; the pick stamps _percevalTargetCell.
      openPercevalSitePick: (sourceCard, opponent) =>
        automationRuntime.startPercevalSitePick(this, sourceCard, opponent),
      // Stamp/clear Perceval's remembered cell on BOTH clients: the
      // opponent picks the site on their screen, but the on_stop draw gate
      // runs on the controller's, so the cell must be synced.
      setPercevalTarget: (sourceCard, cell) => this._setPercevalTarget(sourceCard, cell),
      // Buried Treasure / Sunken Treasure: open an OPPONENT cell-pick
      // over the controller's own land/water sites; the chosen cell
      // conjures the Treasure there buried.
      openConjureUnderSitePick: (sourceCard, opponent, element) =>
        automationRuntime.startConjureUnderSitePick(this, sourceCard, opponent, element),
      // 13 Treasures of Britain: detach the Treasures from its bearer
      // and re-spawn it atop a uniformly random board cell. The roll
      // happens here (single firing client); the place broadcast syncs
      // the chosen cell to the opponent.
      teleportSelfToRandomSiteOrVoidTop: (sourceCard) =>
        automationRuntime.teleportSelfToRandomSiteOrVoidTop(this, sourceCard),
    };
    // Coalesces bursts of tracker writes (life, mana, elements) into
    // one log line per burst. Sync to opponent still fires per-write so
    // HUD counters animate live; only the logbook is debounced. Pushed
    // only from the actor's side. Receivers never reformat from state
    // diffs (the actor's logAction is the single source of truth).
    this._trackerLogCoalescer = new TrackerLogCoalescer({
      idleMs: 500,
      emit: (snapshot) => {
        if (snapshot.key !== 'life') return;
        this.logAction(this._lifeLogText(snapshot.player, snapshot.prev, snapshot.next));
      },
    });
    this.meshes = new Map();
    this.pileMeshes = new Map();
    // Departing card meshes mid fly-to-pile flight: out of host.meshes
    // (non-interactable) but tracked so the board reconciler can dispose a
    // stranded one. Keyed by card id → { mesh, queuedAt }.
    this._exitingMeshes = new Map();
    // Live cosmetic Howl reveal: temporary face-up meshes dealt into the
    // voids + their auras, driven per-frame by _syncHowlAuras. Keyed by
    // step index → { mesh, aura }. Never enters gameState.
    this._howlAuras = new Map();
    this._playedHowls = new Set();
    this._howlTimers = [];
    this._boardReconcileDirty = false;
    this.dragging = null;
    this.hoveredMesh = null;
    this.opponentHoveredCardId = null;
    this.lastMouseEvent = null;
    this.inspectorHold = new InspectorHoldTracker();

    // Controller-mode transient state. All inert until a gamepad
    // button or stick actually moves; mouse-only players never touch
    // this state tree.
    this.controllerAim = { x: 0, y: 0 };           // screen-space pixels, canvas-clamped
    this.controllerStick = { lx: 0, ly: 0, rx: 0, ry: 0 }; // latest stick axis values
    this.controllerHeldButtons = new Set();        // current held button indices
    this.controllerGrabbedCard = null;             // cardInstance currently toggle-grabbed
    this.controllerGrabbedOrigin = null;           // { x, y, z, rot, parentId } for cancel restore
    this.controllerRadialOpen = false;             // RT currently held
    this.controllerRadialIndex = -1;               // currently highlighted slice
    this.controllerPoll = null;
    this.controllerRAF = null;
    this.unsubInputSource = null;
    this.controllerListeners = [];
    this.inputSourceState = inputSource.get();
    this.sync = new GameSyncBridge();
    // Match log recorder. Buffers the wire-event stream for replay/analytics.
    // Started only for real, non-spectator arena matches (see registerHandlers).
    this.matchRecorder = new MatchRecorder();
    this.pingRateLimit = createPingRateLimiter();
    this.pingLayer = null;
    this.lightningLayer = null;
    this.pingMode = false;
    this._pingStaleWarned = false;
    // Table-card multi-selection + grouping.
    // selectedCardIds. Cards currently highlighted via marquee drag,
    //                    cleared on click-empty or Escape.
    // groups. GroupId → Set<cardId>; persistent groups that
    //                    drag together even without pre-selection.
    //                    Each member card also carries its groupId on
    //                    cardInstance.groupId so the group survives
    //                    serialization round trips.
    // marquee. Transient rectangle state while the user is
    //                    dragging a selection box on the empty board.
    this.selectedCardIds = new Set();
    this.groups = new Map();
    // Timestamp per groupId marking when the group was created. The
    // per-frame freeze guard in tickPhysicsSync skips groups that are
    // still within the settling grace period so gravity can pull them
    // onto the table before they get frozen.
    this.groupSettleTimes = new Map();
    this.marquee = null;
    this.marqueeOverlayRef = createRef();
    // The "hug rect" is the persistent rounded outline that wraps
    // around every currently-selected card, follows them as they move,
    // and serves as a hit-zone so the user can click anywhere inside
    // the outline (not just on a card) to drag the whole selection.
    // Stored in screen coordinates because the click hit-test is done
    // against event.clientX/clientY.
    this.hugRectRef = createRef();
    // DOM overlay that floats next to the hovered card showing its
    // active status badges as fixed-pixel "pins." Same screen size at
    // any zoom, unlike the world-space 3D badges which shrink with the
    // card. Children come from state.hoverStatusOverlay; position is
    // mutated directly per frame by updateHoverStatusOverlayPosition().
    this.hoverBadgesRef = createRef();
    this.hoverStatPillsRef = createRef();
    // Side-mounted full-thumbnail stack rendered next to whichever
    // card is currently hovered. Position is anchored to the host's
    // top-left each frame by updateHoverStatusOverlayPosition; the
    // inner slide wrapper handles entry/exit animation.
    this.hoverEquipmentRef = createRef();
    // Map<cardId, { outline, label }>. DOM refs for each card in the
    // Ctrl+click equip-pending selection. The per-frame tick projects
    // every member's mesh bounds and mutates these elements directly
    // (no React re-render). Populated by registerEquipOverlayRef as
    // EquipPendingOverlays mounts each pair.
    this.equipOverlayRefs = new Map();
    // Map<cardId, HTMLDivElement>. DOM halo for every locally-controlled
    // Minion / Avatar that is a valid equip target while equipPending is
    // non-empty. Same per-frame mutation pattern as equipOverlayRefs.
    // Map<cellKey, HTMLDivElement>. DOM halo for every grid cell that
    // is a valid pick target while a two-step location effect is pending
    // (v1.13). Position mutated per frame, outside React;
    // cellKey is the canonical 'col,row' string.
    this.cellTargetOverlayRefs = new Map();
    // Map<cellKey, HTMLDivElement> for the replay feed's hover highlight:
    // the cells of the cards a hovered chronicle row mentions.
    this.cellMentionOverlayRefs = new Map();
    // Map<refKey, HTMLDivElement> for Flamecaller's projectile overlay.
    // refKey is "<direction>|col,row" so the same cell appears in two
    // refs when two directions overlap on it (only possible at the
    // shooter's row/col, where the shooter cell is excluded anyway.
    // so in practice this never collides today). Per-frame positioning
    // + hover-by-direction styling lives in updateProjectilePathOverlays.
    this.projectilePathOverlayRefs = new Map();
    // Map<stripKey, HTMLDivElement>. Element pip strips, one per
    // (cell, rotation) pair so multi-site cells with mixed control
    // can have separate upright/inverted strips. Per-frame projector
    // anchors each strip at the cell's bottom-center. v1.29b.
    this.cellPipOverlayRefs = new Map();
    // S3. Map<cellKey, HTMLDivElement> of resting stack count badges,
    // one per cell holding 2+ cards. Per-frame projector anchors each
    // over the cell center; clicking one opens the cell focus strip
    // (see cellFocusStrip.js).
    this.cellStackBadgeOverlayRefs = new Map();
    this.fanChromeRefs = new Map();
    // v1.72. Map<cellId, THREE.Mesh> of subsurface region-view tint
    // meshes, parented to scene.scene and synced from
    // buildRegionViewCells. Mesh-based (not DOM) so the highlight
    // renders BELOW cards / sites in the 3D scene. DOM overlays
    // would always sit above the entire WebGL canvas.
    this.regionViewTintMeshes = new Map();
    // v1.111. Harbinger summoning-circle decals. One mesh per marked
    // cell, keyed by `${ledgerEntryId}:${col},${row}`. Source of truth
    // for the cells is the avatar's MODIFY_CAST_COST ledger entry
    // (filter.cells); the renderer scans the ledger each frame.
    this.harbingerCircleMeshes = new Map();
    // Dragonlord "ready" aura. Map<avatarCardId, effect>. Materialised
    // per-frame by _syncDragonlordReadyGlow while the invoke ability is
    // payable; reaped the moment it isn't.
    this.dragonlordGlowMeshes = new Map();
    // Dragonlord merge visual. Map<avatarCardId, effect>. The spirit
    // echo of the companion Dragon that descends into the avatar on
    // invoke and hovers fanned behind it while the borrowed identity
    // is active. Driven by _syncDragonSpiritEchoes.
    this.dragonSpiritEchoes = new Map();
    // Map<cellKey, HTMLDivElement>. Movement-reach halos. Populated
    // while a card is being dragged; the halo set comes from
    // this.dragging.reach. v1.32c.
    this.movementReachOverlayRefs = new Map();
    // Set<spellCardId>. Spell cards currently mid-resolution. A spell
    // enters on play (v1.14) and exits to its controller's Cemetery
    // once every prompt / target-pick / cell-pick referencing it has
    // resolved (or been cancelled). Tracked here rather than in React
    // state because we mutate it from multiple async resolution paths.
    this._spellPlayInFlight = new Set();
    // v1.46. Per-player count of unused "play a site" grants.
    // Tapping an Avatar's "Play or draw a site" ability and picking
    // "Play a site" increments the grant for that controller. Each
    // site placement consumes one grant. Grants expire at end of
    // turn (cleared in _stepPruneExpiredEffects). Local-only. Not
    // synced; only the active player needs the count.
    this._siteGrants = { p1: 0, p2: 0 };
    // v1.116. Optional land/water restriction on a granted site
    // placement (Landmass: 'land', Overflow: 'water'). Cleared with
    // the grant counter at end of turn. Local-only.
    this._siteGrantRestriction = { p1: null, p2: null };
    // The Apex of Babel. One-shot free-cast window. While true for a
    // player, their next spell from hand costs no mana (waived in
    // payCastingCost, glowed in hand). Cleared at the player's turn end
    // alongside _siteGrants. Local-only. The controller's own action.
    this._freeCastGrant = { p1: false, p2: false };
    // Death's Door immunity now lives on `this.playerResources`
    // (engine class). End-of-turn step calls
    // `this.playerResources.clearDeathsDoorImmunity(player)`.
    // v1.49. Once set, game is over and no further actions should
    // mutate game state. Future: wire into UI to show end-of-game
    // modal; for now just logs + locks combat.
    this._gameOverWinner = null;
    // Map<cardId, HTMLDivElement>. DOM root of every always-visible
    // equipment-peek stack (one per host card with attached cards).
    // The per-frame tick writes the projected size vars, anchors the
    // stack above the host's top-center, and toggles data-hovered to
    // drive the peek slide-out (with HoverEquippedCards slide-in
    // running in parallel). Populated by registerEquipmentOverlayRef
    // as EquipmentStacks mounts each entry.
    this.equipmentOverlayRefs = new Map();
    this.chatInputRef = createRef();
    this.saveDialogInputRef = createRef();
    this.hugRectScreen = null; // { minX, minY, maxX, maxY } | null
    // Reusable THREE scratch instances so the per-frame hug-rect
    // recompute allocates nothing.
    this._hugBox = new THREE.Box3();
    this._hugVec = new THREE.Vector3();
    this.spawnMarkers = new Map();
    this.tokenMeshes = new Map();
    this.lifeHUDs = new Map(); // cardId -> { sprite (ATK), hpSprite (HP) }. Stats are adjusted via the shift-click status ring
    this.statusHUDs = new Map(); // cardId -> [badge meshes]
    this.counterHUDs = new Map(); // cardId -> centred level-counter token mesh (The Immortal Throne)
    // cardId -> Map<statusKey, effectHandle>. Each effect's lifetime
    // parallels the matching entry in cardInstance.statuses. Created
    // on add, torn down on remove, updated every frame so the visual
    // tracks dragging / settle animations / taps / flips. Status keys
    // that don't have a registered effect in CARD_STATUS_EFFECTS are
    // simply ignored.
    this.cardEffects = new Map();
    this.diceMeshes = new Map();
    // Monotonic counter for "most-recently-touched-is-on-top" stacking.
    // Each card on the table has cardInstance.interactionOrder; bumping
    // it on click / drag-start / play places that card at the top of any
    // stack at its XZ. Y is computed per-frame by _applyComputedY from
    // these orders. No physics simulation involved in stacking. The
    // counter is the max of any card's order so freshly-spawned and
    // freshly-touched cards always land above the current pile.
    this._interactionCounter = 0;
    // v1.58. Monotonic timestamp generator for the layer system.
    // Each addCardToTable call stamps cardInstance._enteredRealmAt;
    // each triggered/activated ability output (future) stamps its own
    // _activatedAt. The recompute sorts ongoing effects by layer
    // first, then timestamp asc. So older sources resolve first.
    this._layerTimestampGen = makeTimestampGen();
    // Per-card Y-tween state for the "falls into place" visual feel.
    // Map<cardId, { startY, targetY, startTime }>. _applyComputedY
    // creates an entry whenever a card's computed-stack Y differs
    // from its current mesh.position.y (e.g., right after a drop or
    // after a click bumps the card to top of stack), eases mesh.y
    // toward target over a fixed duration, and deletes the entry on
    // arrival. Falling animations use quadratic ease-in (gravity-like
    // accel); rising animations use quadratic ease-out (snappy
    // selection-feedback).
    this._yTweens = new Map();
    this.trackerPreviewMarkers = [];
    this.trackerCursorPreview = null;
    this.trackerTokenMeshes = new Map();
    this.trackerButtonMeshes = new Map();
    // 3D face-down cards on the table representing the opponent's hand.
    // Purely visual. Not interactive, no physics. Updated whenever the
    // `opponentHand` state changes via the `hand:info` sync message.
    this.opponentHandMeshes = [];
    this._playerBadgeMesh = null;
    this._opponentBadgeMesh = null;
    this.handRetractTimer = null;
    this.autoSaveTimer = null;
    this._socketListenersActive = false;

    this.state = {
      isLoading: true,
      loadingMessage: t('Preparing game table...'),
      // HUD buff/life management. { player, kind: 'life'|'mana' } | null.
      // Drives whether HudBuffPanel or HudLifeAdjustPanel is rendered
      // anchored to the clicked HUD row.
      hudPanel: null,
      // Scroll-wheel hover-adjuster. { player, kind, delta, rect } | null.
      // Mutually exclusive with hudPanel (opening the panel closes the
      // scroll preview).
      hudScroll: null,
      // Starting life is picked in the match-setup dialog and threaded
      // here as a prop. Defaults to the standard 20 when nothing's
      // supplied (e.g. older save resumes or callers that haven't
      // adopted the new field yet). Matches the pre-Quick-Match
      // behaviour exactly.
      gameState: createGameState(props.startingHp || 20),
      // Queue of pending card-effect prompts. Only the head renders in
      // the EffectPromptDrawer; confirming/cancelling advances the queue.
      pendingPrompts: [],
      // Magics currently played to the storyline (cast, resolving). Shown
      // in the storyline area while they dwell, then they land on their
      // square via the standard addCardToTable path. Cosmetic. Separate
      // from pendingPrompts so it never blocks the drawer or turn-pass.
      castingSpells: [],
      // v1.105. Disconnect-survival index for every gameplay-critical
      // modal/prompt. JSON-safe descriptors: { id, kind, controller,
      // payload }. The runtime state fields (pendingPrompts,
      // pendingCellTarget, scry, ...) stay the source of truth for what
      // components render; the stack is the persisted projection that
      // gets serialised into the autosave + rehydrated on reconnect so
      // a critical prompt that vanished with the browser tab comes
      // straight back. See _openPrompt / _closePrompt / _rehydratePromptStack.
      pendingPromptStack: [],
      // Generic single-target effect waiting on a click. Shape:
      //   { sourceCard, controller, op, args, filter }
      // When set, the existing equip-target halo overlays render on
      // matching cards; clicking one fires the effect via _resolveTarget.
      // ESC clears it.
      pendingTarget: null,
      // Server-authoritative automation choice (P6 cutover). Folded from the
      // mirror's pendingChoice for the chooser; { promptId, candidates, seat,
      // optional }. Drives the target halo; a click sends chooseIntent.
      pendingServerChoice: null,
      // Two-step location targeting (v1.13). Shape:
      //   { sourceCard, controller, op, args, validCells: Set<'col,row'> }
      // When set, CellTargetOverlays renders a halo over each valid
      // cell; the next mousedown on a cell in `validCells` fires the
      // op against every unit in that cell matching `args.target.filter`.
      // ESC clears it.
      pendingCellTarget: null,
      // v1.30c. Double-click action menu. Shape:
      //   { cardId, cardName, x, y, items: [{ id, label, onClick, disabled }] }
      // Replaces the old double-click-to-tap. Tap is still available
      // as one of the menu items.
      cardActionMenu: null,
      // v1.43. Modal anchored above a target cell showing valid
      // attack targets after a Move-and-Attack or Attack action.
      // Null when no attack is being declared.
      attackTargetModal: null,
      // Multi-defender damage-split modal (CombatEngine ASSIGNING phase).
      // Shape: { power, defenders: [{ id, name, def }] } or null.
      combatScene: null,
      // The persistent combat progress panel's model (authCombat
      // applyCombatProgressFromMirror): steps, effect history, choice owner.
      combatProgress: null,
      // player -> timestamp of their last direct life hit; keys the
      // one-shot red flash over the HUD profile avatar.
      hudDamageFlash: {},
      // player -> timestamp releasing a combat-held HUD life countdown
      // (bumped by the overlay on the impact frame).
      hudLifeRelease: {},
      // v1.51. Active floating damage numbers. Each entry is
      // { id, x, y, value, color } in viewport pixels. Entries are
      // pushed by _spawnFloatingDamage and dropped by the floater's
      // onAnimationComplete callback (~900ms after spawn).
      floatingDamage: [],
      showDeckPicker: false,
      showExitConfirm: false,
      isPlacingSpawns: false,
      activeSpawnKey: null,
      isEditingGrid: false,
      // Dev-only UI-inversion editor for layered playmats. Authoring
      // mode for the regions where the UI overlay is RGB-inverted at
      // render time (see uiInversionEditor.js + tableScene's shader
      // injection). Gated behind isDevBuild() in the menu, but the
      // state itself is harmless if it ever flips in production.
      isEditingUiInversion: false,
      isEditingComponents: false,
      spawnConfig: {},
      contextMenu: null,
      ringMenu: null,
      // { cardId, statuses: string[] } | null. Drives the DOM hover
      // status badge overlay. Set when the hovered card has 1+ active
      // statuses; cleared on hover-out or when the last status is
      // removed. Statuses are snapshot-copied so re-renders trigger
      // even though cardInstance.statuses is mutated in place.
      hoverStatusOverlay: null,
      // Stores the resolved crown-title string per-player for the HUD.
      // Drives both the trailing comma on the username (truthy check)
      // and the dynamic font-size step-down for long titles so the
      // title fits on one line without widening the HUD panel.
      hudPlayerTitle: {},
      handCards: [],
      // Casual sandbox mode runs a "play both sides" single-player
      // experience: both decks get their own hand, and the local user
      // can Tab between them. handCards2 is the Player 2 hand; only
      // populated when isCasualSandbox is set. In online/multiplayer
      // mode this stays empty and handCards remains the local-player
      // hand as before.
      handCards2: [],
      // Which player the local user is currently controlling. In
      // multiplayer this is pinned (1 for host, 2 for guest); in
      // casual sandbox Tab swaps it. Drives hand rendering, opening-
      // draw, mulligan banner, and HUD indicator.
      activePlayerNum: 1,
      // Casual sandbox game-lifecycle flag. false until the user
      // clicks "Start Game"; drives the button label + blocks opening
      // draws / mulligan banner from firing before the match begins.
      // Flipping it back to false resets every in-match value
      // (trackers, turn, hands, mulligan) via endGame().
      gameStarted: false,
      // Mulligan phase. 'active' while the player is choosing cards to
      // return to deck bottoms after the opening draw. 'done' after
      // either executeMulligan or keepHand has fired. null before the
      // opening draw completes (or in sessions that skip auto-draw).
      // The cap comes from the resolved avatar rules (see
      // src/utils/game/avatarRules), not a hardcoded constant, so
      // avatars that modify the mulligan cap can do so declaratively.
      mulliganPhase: null,
      // Set of card.id entries flagged for return-to-bottom. Using a Set
      // for O(1) toggle; React state handles it via new Set(prev) on
      // every update to keep the reference fresh.
      mulliganFlaggedIds: new Set(),
      // Per-player mulligan state in casual sandbox. Each side has its
      // own opening-draw decision. Banner reads from whichever
      // activePlayerNum points to. Unused outside casual mode.
      mulliganPhase2: null,
      mulliganFlaggedIds2: new Set(),
      // Remember the deck object spawned into each slot so End Game
      // can re-spawn a fresh shuffled copy (same deck, shuffled anew,
      // avatar back at its spawn point, no table residue). null until
      // a deck lands in that slot. Persisted across save/resume.
      spawnedDecks: { 1: null, 2: null },
      inspectedCard: null,
      logbookMentionPreview: null,
      // Inspector navigation breadcrumb. Each entry is the cardInstance
      // we drilled out of via the equipment panel. Empty for a normal
      // Space-key inspect; non-empty when the user has clicked through
      // an attached card and can use the back button to return.
      inspectorStack: [],
      // { source: cardInstance, targets: [...] } | null. Drives the
      // centered Equip Target modal opened from the card context menu.
      equipModal: null,
      // Set<cardId>. Cards the user has Ctrl+clicked, queued to be
      // attached to the next plain-clicked target. Re-rendered as the
      // marching-ants overlay; cleared on commit, Escape, or empty-
      // board click.
      equipPending: new Set(),
      // Array<{ cardId, equippedCards }>. Every host card on the
      // table that currently has attached equipment. Drives the
      // permanent EquipmentStacks overlay. Rebuilt by
      // syncEquipmentOverlays whenever equipment identity changes.
      equipmentOverlays: [],
      opponentHand: [],
      roomCode: '',
      // In-log chat. Ephemeral per-session; pressing Enter anywhere
      // outside an input field focuses the chat bar, pressing Enter
      // inside it sends the message (if non-empty) and blurs.
      chatInputValue: '',
      connectionStatus: 'offline',
      isHost: true,
      showSaveDialog: false,
      saveDialogName: '',
      savedSessions: [],
      currentSessionId: null,
      currentSessionName: '',
      showGameMenu: false,
      allowSpectators: getDiscordSettings().allowSpectators,
      showSpawnConfirm: false,
      pendingSpawnDeckId: null,
      pendingSpawnPlayerNum: 1,
      // Effective .app-canvas scale, mirrored into state so the hand
      // (which sizes its cards + arc geometry off it) re-renders in
      // lockstep with the cqw-driven UI on resize / aspect change.
      canvasScale: getCanvasScale(),
      hoveredHandIndex: -1,
      // Last card the local player hovered, fed to the streamer-facing
      // top-right CardPreviewDrawer. Sticky. Keeps showing the last
      // hovered card after the cursor leaves it, until another card is
      // hovered. Face-down cards do NOT update this state so the
      // preview stays informative.
      focusedPreviewCard: null,
      handRetracted: true,
      handDragIndex: -1,
      searchPile: null,
      searchQuery: '',
      // v1.86. Set while a SEARCH_DECK op is awaiting the player's
      // pick in the PileSearchDialog. Shape: { sourceCard, controller,
      // pileId, filter, shuffleAfter, sourceName }. While set, clicking
      // a card in the dialog routes through _resolveSearchPick (which
      // gates the click on the filter + shuffles + clears this state)
      // instead of the plain takeCardFromPile path.
      pendingSearchEffect: null,
      // Imposter Mask. Set while the (3) Mask ability is waiting for
      // the player to pick an Avatar from their Collection. Shape:
      // { sourceCard, controller, manaCost }. Click in the PileSearchDialog
      // routes through `_resolveMaskPick` which pays the mana, banishes
      // the picked card, and swaps the imposter's identity. ESC / close
      // clears this without paying. The cost is deferred until pick.
      pendingMaskPick: null,
      // Archimago cast-a-copy. Two-phase picker state. Phase 1 collects
      // three Magic spells from the controller's cemetery (banishing
      // each as it's picked). Phase 2 picks one of those three to cast
      // a copy of. Both live as `pendingArchimagoBanish` (phase 1) /
      // `pendingArchimagoCopy` (phase 2); only one is non-null at a
      // time. Click in the PileSearchDialog routes through
      // `_resolveArchimagoBanishPick` / `_resolveArchimagoCopyPick`.
      pendingArchimagoBanish: null,
      pendingArchimagoCopy: null,
      // Deathspeaker cast-a-copy. Single-phase Minion picker over the
      // controller's cemetery. Click routes through
      // `_resolveDeathspeakerPick` (pays the copy's cost, banishes the
      // dead minion, click-to-places the copy).
      pendingDeathspeakerPick: null,
      // Dragonlord set-aside. Unique-Dragon picker over the
      // controller's Collection. Click routes through
      // `_resolveDragonlordSetAsidePick`.
      pendingDragonPick: null,
      // v1.88. Queue of pending summon placements. Each entry:
      // { tokenRow, controllerRotated, validCells: Set<'col,row'>,
      //   sourceLabel, imageUrl }. The HEAD of the queue is the active
      // placement; click on a valid cell spawns + dequeues; ESC cancels
      // the whole queue. While non-empty, CellTargetOverlays draws the
      // halos and TokenSpawnPreview follows the cursor.
      pendingSummonPlacement: null,
      scry: null,
      showShortcutHelp: false,
      trackerEditing: null,
      showDiceMenu: false,
      currentTurn: 'p1',
      // v1.38. Sorcery turn structure. Active player is always in
      // 'main' between passTurn invocations; 'start'/'end' are
      // transient phases that fire synchronously inside passTurn.
      // Kept in state so a future phase-button UI (or AI hooks) can
      // read which phase the engine is in without subscribing to
      // imperative side-effects.
      currentPhase: PHASE.MAIN,
      // Per-player turn counters. Sorcery turns are per-player, not
      // shared, so both players can simultaneously be on their own
      // "turn 3." p1 starts at 1 (first active player); p2 at 0 until
      // their first turn begins.
      turnNumbers: { p1: 1, p2: 0 },
      showMatchResult: false,
      // Engine-determined winner, set by _declareGameOver on death
      // blow. Local-relative ('me' | 'opponent' | null). Passed to
      // ArenaMatchResult as autoWinner so the modal can submit the
      // server outcome report straight away. Also re-set when a
      // match:settled push arrives without a prior local death-blow
      // (lone-reporter timeout path).
      autoMatchWinner: null,
      // Spectator end-of-match marker. Set when match:settled
      // arrives while we're spectating. Drives the spectator-only
      // "Match Ended" overlay so we never render ArenaMatchResult
      // (which would try to file a report) for a viewer.
      spectatorMatchEnded: false,
      // v1.72. Region-view toggle. 'surface' (default) renders the
      // playmat as before; 'subsurface' fades the above-ground card
      // meshes so only burrowed/submerged units stay visible, and
      // overlays highlight connected water + underground bodies so
      // players can see traversable subsurface paths at a glance.
      regionView: 'surface',
      // D5/S3. The FOCUSED cell ('col,row' or null). Cells are the board's
      // interactive unit: clicking one fans its card meshes out for
      // individual interaction (see controllers/cellFocus.js).
      focusedCell: null,
      matchStartTime: null,
      pingMode: false,
      // Set to true once the first join attempt has failed and we're
      // retrying. Drives the Cancel button on the "Connecting to host
      // (attempt N)..." loading overlay so the user can bail out
      // instead of waiting through all retries.
      canCancelJoin: false,
      opponentDisconnected: false,
      opponentDisconnectTimer: null,
      gameLog: [],
      showGameLog: true,
      logbookTab: getLogbookTab(),
      chatUnread: false,
      showCodex: false,
      // Tracked at the parent so the compendium and the
      // status-token drawer can enforce mutual exclusion. Only
      // one of them is allowed open at a time.
      statusTokenDrawerOpen: false,
      showSoundSettings: false,
      soundSettings: getSoundSettings(),
      showAmbienceMenu: false,
      ambienceState: getAmbienceState(),
      viewScale: getViewportScale(),
      showBoardTutorial: false,
      // Active one-shot event tutorial, { key, ctx } or null. Fed by
      // the eventTutorials queue (first unit played, cell-stack expand
      // button). Never rendered alongside the main walkthrough.
      eventTutorial: null,
      // Latched flag. Flips true the first time the local player
      // toggles to subsurface view during the board tutorial. Gates
      // the Continue button on the region-view tutorial step so the
      // user actually experiences the layer switch before moving on.
      tutorialSubsurfaceVisited: false,
      // Force-opens the status token drawer while the tutorial is on
      // its `status-tokens` step, so the spotlight points at a stable
      // visible ledge instead of the off-screen closed position.
      tutorialOpenStatusTokens: false,
      // First-player dice roll: appears at the start of every fresh
      // match (sandbox, 1v1, draft) and gates the opening draw on its
      // result. Persisted across resume via matchPhase below. Set
      // back to true on restoreSession when matchPhase === 'rollOff'
      // so a session saved mid-roll re-opens the modal in place.
      showFirstPlayerDice: false,
      // Match lifecycle phase, serialised into the save blob:
      //   'rollOff'. Dice modal is active. On resume we re-open the
      //               modal seeded with firstPlayerRollState.
      //   'playing'. Past the dice (default). Includes mulligan +
      //               regular play. No special resume handling.
      // The 'rollOff' phase is sticky through the whole SERVER-driven
      // start choreography (roll → preGame → openingDraw); the fold
      // flips it to 'playing' when the server does.
      matchPhase: 'playing',
      // v1.111. Harbinger pre-game ceremony. Non-null while a
      // Harbinger avatar is divining its three marked cells; carries
      // { player, ownedByLocal, portraitUrl, phase: 'rolling' | 'reveal' }.
      // Cleared once the marks land and the ledger entry is broadcast.
      harbingerCeremony: null,
      // Slot-machine sweep state for the active Harbinger ceremony.
      // `sweepCell` is the cell currently being highlighted as the
      // RNG cycles (replaces every ~70ms during a round); `lockedCells`
      // accumulates one cell per round as each pick locks in. Three
      // rounds per Harbinger, one cell locked per round. Cleared once
      // the persistent ledger entry takes over the rendering. Shape:
      //   { sweepCell: {col,row} | null, lockedCells: [...], playerKey }
      harbingerSweep: null,
      // Mirrors the FirstPlayerDiceModal's persistable state so the
      // dice can be saved + restored. The modal emits updates via
      // onRollStateChange; we force-save on every change so a
      // sudden app close at any point in the roll-off can resume
      // into the exact same state.
      firstPlayerRollState: {
        hostRoll: null,
        guestRoll: null,
        winnerSeat: null,
        firstPlayerSeat: null,
      },
    };
  }


  // Auth roll-off emit handlers (RO3). The modal calls these instead of the
  // legacy symmetric P2P broadcasts when isAuthMatch. REQUEST_ROLL is the wire
  // intent the server substitutes for a server-arbitrated roll;
  // SET_FIRST_PLAYER commits the winner's pick (seat 1|2 → 'p1'/'p2').
  // `seatNum` names the die being rolled. The solo pilot rolls BOTH dice, so
  // the payload carries the target seat (the envelope seat provider forwards
  // it; the server derives the seat from identity for everyone else).
  _requestServerRoll = (seatNum) => {
    const payload = this.props.isCasualSandbox && (seatNum === 1 || seatNum === 2)
      ? { seat: seatNum === 2 ? 'p2' : 'p1' }
      : {};
    sendMatchCommand(this._matchKey(), { type: REQUEST_ROLL, payload });
  };

  _commitFirstPlayer = (seatNum) => {
    sendMatchCommand(this._matchKey(), {
      type: SET_FIRST_PLAYER,
      payload: { seat: seatNum === 1 ? 'p1' : 'p2' },
    });
  };

  // Server-authoritative pile-level reveal (the Collection "eye" share). The
  // server flips revealedPiles[seat][pileName]; the projection then shares the
  // pile's identities with the opponent, who renders it from the mirror. So the
  // share survives reconnect. No-op when the flag is off (legacy pile:sync).
  _emitPileRevealed = (pileName, revealed) => {
    if (!isAuthMatchEnabled()) return;
    const seat = this._localSeat();
    sendMatchCommand(this._matchKey(), { type: SET_PILE_REVEALED, payload: { seat, pileName, revealed } });
  };

  // The dice modal mounts asynchronously, so an inbound seat-roll
  // message can arrive before the modal subscribes. We queue any
  // unhandled messages here and replay them on subscribe so the peer
  // never misses a roll, even if both seats broadcast in quick
  // succession during the subscription window.
  _pendingFirstPlayerRolls = [];
  _firstPlayerRollSubscriber = null;
  _subscribeToFirstPlayerRoll = (handler) => gameLifecycle.subscribeToFirstPlayerRoll(this, handler);

  // Same queue-and-replay pattern as seat rolls. The winner's pick
  // can arrive before the loser's modal has wired up its subscriber,
  // especially on a slow renderer or when the winner clicks fast.
  _pendingFirstPlayerChoice = null;
  _firstPlayerChoiceSubscriber = null;
  _subscribeToFirstPlayerChoice = (handler) => gameLifecycle.subscribeToFirstPlayerChoice(this, handler);

  // Merge two firstPlayerRollState objects, preferring non-null
  // values. Each seat is authoritative for its own roll, so the
  // pragmatic rule "if we already have a value, keep it; else take
  // theirs" produces a correct union. A peer that already has both
  // rolls fills in the one we're missing, and vice versa. winnerSeat
  // and firstPlayerSeat follow the same rule: once set on either
  // side, the value is locked in.
  // Pure merge extracted to net/rollStateMerge (unit-tested); kept as a thin
  // method so existing call sites (this._mergeRollStates) stay unchanged.
  _mergeRollStates = (peer, local) => mergeRollStates(peer, local);

  _replayInitiativeFields = (mergedRollState, previousRollState) => gameLifecycle.replayInitiativeFields(this, mergedRollState, previousRollState);

  _broadcastTap = (cardId, tapped) => {
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    sendMatchCommand(this._matchKey(), authCardState.tapCommand(cardId, tapped));
  };

  _broadcastSkipNextUntap = (cardId) => {
    if (!this._authZonesEnabled()) return; // sandbox: local flag only, no wire
    sendMatchCommand(this._matchKey(), authCardState.flagsCommand(cardId, { skipNextUntap: true }));
  };

  _broadcastSkipNextTurn = (cardId) => {
    if (!this._authZonesEnabled()) return; // sandbox: local flag only, no wire
    sendMatchCommand(this._matchKey(), authCardState.flagsCommand(cardId, { skipNextTurn: true }));
  };

  // Counters had no GameBoard wrapper (automationRuntime called host.sync
  // directly); add one so the auth gate has a single chokepoint.
  _broadcastCardCounters = (cardId, counters) => {
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    sendMatchCommand(this._matchKey(), authCardState.countersCommand(cardId, counters));
  };

  // Shared chokepoint for the bespoke D4 card-state commands (animate / identity
  // transforms) issued from controllers that hold `host` but not _matchKey.
  _sendMatchCommand = (command) => {
    // While the presentation is behind the authoritative state (a strike
    // window is playing), a turn pass would act on a board the player has
    // not seen yet. The server refuses it too; this keeps the click honest.
    if (command?.type === 'PASS_TURN' && this._presentationBehind()) {
      this.addAdvisoryLog('{what} was refused. {why}', undefined, { what: msg('End turn'), why: msg('The fight is still playing out.') });
      return;
    }
    sendMatchCommand(this._matchKey(), command);
  };

  _sendMatchIntent = (intent) => {
    // Ordinary gameplay stays gated until the presentation catches up; a
    // displayed, still-current effect choice (and a dice setting) passes.
    if (this._presentationBehind() && intent?.domain && intent.domain !== 'automation' && intent.domain !== 'dice') {
      this.addAdvisoryLog('{what} was refused. {why}', undefined, { what: msg('That action'), why: msg('The fight is still playing out.') });
      return;
    }
    sendMatchIntent(this._matchKey(), intent);
  };

  _presentationBehind = () => !!this._combatPresentation?.isBehind?.();

  // The state the scene currently shows: the presentation queue's displayed
  // commit while a fight plays, the live mirror otherwise. Parity checks
  // compare the picture against this, never against a version the player
  // has not been shown yet.
  _displayedMirrorState = () => this._combatPresentation?.displayedState?.() ?? this._authMirror?.getState?.() ?? null;

  // Server-authoritative once-per-turn ability gate (P7). On the auth path the
  // activation goes through an ability INTENT: the server owns the once-per-turn
  // GATE + the durable used-marker (card.abilitiesUsedThisTurn. The same field
  // the legacy flags command writes), so a tampered client cannot activate a
  // once-per-turn ability twice. Returns true when it sent the intent so the
  // caller suppresses the legacy SET_CARD_FLAGS abilities-used broadcast for the
  // once-per-turn marker (the opponent renders it from the intent diff). Off the
  // auth path / sandbox it returns false and the legacy broadcast runs unchanged.
  _emitAbilityIntent = (card, abilityKey, oncePerTurn, extras) => {
    if (!card || !this._authZonesEnabled()) return false;
    this._sendMatchIntent(activateAbilityIntent(card.id, abilityKey, oncePerTurn, extras));
    return true;
  };

  // Promote the per-card abilities-used Sets to server-owned state (they were
  // local-only → lost on reconnect). Absolute arrays; idempotent on the server.
  _broadcastAbilitiesUsed = (card) => {
    if (!card || !this._authZonesEnabled()) return;
    sendMatchCommand(this._matchKey(), authCardState.flagsCommand(card.id, {
      abilitiesUsedThisTurn: [...(card._abilitiesUsedThisTurn || [])],
      abilitiesUsedThisGame: [...(card._abilitiesUsedThisGame || [])],
    }));
  };

  _broadcastChat = (message) => {
    this.sync.sendChatMessage(message);
  };

  // v1.111. Mirror an activated ability onto the opponent's storyline
  // as a read-only acknowledge. Async/informational: the caster never
  // waits on it.
  _broadcastAbilityNotice = (card, fullCard, entry, controller) => {
    this.sync.abilityActivatedNotice({
      cardId: card.id,
      cardName: fullCard?.name || card.name || 'an ability',
      imageUrl: card.imageUrl || null,
      description: entry?.description || '',
      controller,
    });
  };

  _broadcastTracker = (playerId, key, value) => {
    // Online: the authoritative SET_LIFE/SET_MANA/SET_THRESHOLDS command owns
    // resource sync (resourceCommandForTracker covers every tracker key). The
    // actor already wrote gameState.trackers + the HUD locally; the diff
    // confirms it and propagates to the opponent.
    if (!isAuthMatchEnabled()) return;
    const cmd = authResources.resourceCommandForTracker(this, playerId, key, value);
    if (cmd) sendMatchCommand(this._matchKey(), cmd);
  };

  // P4 cutover: route an AUTOMATED resource flow (loseLife / gainLife /
  // dealAvatarDamage / payMana / grantMana) through a server-authoritative
  // resource INTENT. The server re-computes the value from its own state and
  // owns the death's-door immunity + death-blow decision (the client-authored
  // deathsDoorTurnKey anti-cheat hole closes). Returns true when it sent the
  // intent so PlayerResources suppresses its legacy absolute SET_* broadcast;
  // the opponent renders from the intent diff (applyResourcesFromMirror).
  // Off the auth path / sandbox it returns false and the legacy broadcast runs.
  _emitResourceIntent = (player, kind, amount) => {
    if (!this._authZonesEnabled() || !amount || amount <= 0) return false;
    const builder = {
      loseLife: resourceIntents.loseLifeIntent,
      gainLife: resourceIntents.gainLifeIntent,
      dealAvatarDamage: resourceIntents.dealAvatarDamageIntent,
      payMana: resourceIntents.payManaIntent,
      grantMana: resourceIntents.grantManaIntent,
    }[kind];
    if (!builder) return false;
    this._sendMatchIntent(builder(player, amount));
    return true;
  };

  _broadcastRemoveCard = (cardId, toPileId = null) => {
    this.sync.removeCard(cardId, toPileId);
  };

  // Zones domain R2c: in an auth match (flag on. Online AND solo) drawing +
  // every pile transition is an authoritative command. The server reveals the
  // moved card to the entitled seat only and propagates the COUNT to the
  // opponent. The actor mutates locally first, THEN emits; the diff confirms
  // it. Flag-off keeps the legacy P2P pile:sync / spell:todraw path.
  _authZonesEnabled = () => isAuthMatchEnabled();

  // Session-wide automation mode. `false` is a manual (tabletop) session: the
  // client resolves nothing on the players' behalf. No card triggers, no
  // attack modal, no auto-pay/auto-tap/death-sweep, no reach or playable
  // highlights. And the server refuses the same work on its side.
  //
  // The authority is MatchState (the server stamps it from the owning instance
  // at deal time, so every mount path. Create, join, resume, draft round,
  // spectate. Sees the same value). The prop is the pre-sync fallback for the
  // window before the first fold. Undefined on both means a session predating
  // the field, which only ever played automated.
  _automationsEnabled = () => {
    const synced = this._authMirror?.getState?.()?.automations;
    if (synced !== undefined) return synced !== false;
    return this.props.automations !== false;
  };

  // Free card placement: a dropped non-site card RENDERS exactly where it was
  // dropped instead of snapping into its cell's cascade. Always on in a manual
  // (automations-off) session; otherwise governed by the local "Snap Cards to
  // Squares" control setting (snapping is opt-in). Render-side only: every
  // client always emits the drop's world coords (which persist as card.pos),
  // so both snap modes see the same server state and each picks its own
  // representation. A snapping client renders the derived cell pose; a
  // free-placing client renders the stored pos, for every card on the table.
  _freeCardPlacement = () => !this._automationsEnabled() || !getControlSettings().snapToGrid;

  // Map a client pile NAME to its authoritative zone key. Returns null for a
  // pile that has no authoritative zone (e.g. the table) so callers can skip.
  _authZoneForPileName = (name) => {
    if (!name) return null;
    if (name === 'Spellbook') return 'spellbook';
    if (name === 'Atlas') return 'atlas';
    if (name.startsWith('Cemetery')) return 'cemetery';
    if (name.startsWith('Banished')) return 'banished';
    if (name === 'Collection') return 'collection';
    return null;
  };

  _emitDraw = (seat, pileName, count = 1) => {
    if (!this._authZonesEnabled()) return;
    sendMatchCommand(this._matchKey(), zoneCmd.drawCommand(seat, pileName, count));
  };

  // P5-cast-move: cards whose realm entry + resting position are being committed
  // by a single cast / move INTENT this drop. The intent owns the atomic
  // MOVE_TO_ZONE('play') + SET_POSITION commit, so the legacy per-result
  // MOVE_TO_ZONE + SET_POSITION commands for the same card are suppressed (a
  // single authoritative writer, no redundant diff). Consumed once on emit.
  _intentOwnedCards = new Set();

  // P5-cast-move: emit a server-VALIDATED cast intent for a realm play (the
  // server checks owner-lock + positional and commits zone + position in one
  // diff). Flags the card so its legacy MOVE_TO_ZONE('play') + SET_POSITION
  // commands are suppressed (single writer). Mana is owned by the P4 resource
  // intent (payCastingCost routed it), so the cast intent charges 0 here. The
  // local actor already applied the play optimistically; the server diff is the
  // authoritative reconcile and the opponent renders from it. The in-flight cast
  // is registered as a pending cast (S5): the mirror keeps the card in zone
  // 'hand' until the cast diff folds, and the pending-cast sweep reverts it if
  // the server rejects the cast (no confirming diff ever lands).
  _emitCastIntent = (card, m) => {
    if (!card || !m?.mesh || !this._authZonesEnabled()) return;
    const seat = card.rotated ? 'p2' : 'p1';
    const x = m.mesh.position.x;
    const z = m.mesh.position.z;
    const cell = this._cellAtWorld(x, z);
    this._intentOwnedCards.add(card.id);
    this._beginPendingCast(card);
    // W3: the debit is server-computed off this intent's commit; autoPay:false
    // carries the manual-mana / free-cast opt-out payCastingCost stamped.
    const waived = cardStateOps.consumeCastAutoPayWaiver(this, card.id);
    // World coords ride every non-site intent and persist as card.pos, so a
    // free-placing client (either seat, either snap setting) can render the
    // play exactly where it was dropped. The committed cell stays the rules
    // truth; a snapping client ignores the pos. Sites keep cell placement:
    // the realm grid IS the map, even across a real table.
    const isAura = card.type === 'Aura';
    const freePlace = isAura || (!card.isSite && card.type !== 'Site');
    this._sendMatchIntent(castPlayIntent(card.id, seat, {
      col: cell?.col ?? null, row: cell?.row ?? null,
      ...(freePlace ? { x, z } : {}),
      manaCost: 0, faceDown: card.faceDown || undefined,
      autoPay: waived ? false : undefined,
    }));
  };

  // P5-cast-move: emit a server-VALIDATED unit move intent (the server re-runs
  // the reach engine and commits SET_POSITION + subsurface + tap). Flags the card
  // so the redundant per-result SET_POSITION command is suppressed (single
  // writer). `toRegion` is the resolved landing region; `autoTap` reflects the
  // local auto-tap-on-move preference so the server taps in lockstep.
  _emitMoveIntent = (card, toCell, toRegion, autoTap, dropPos) => {
    if (!card || !toCell || !this._authZonesEnabled()) return;
    this._intentOwnedCards.add(card.id);
    // The drop's world coords ride every non-site move intent and persist as
    // card.pos, so a free-placing client (either seat) renders the card
    // exactly where it was dropped. Snapping clients ignore the pos. Sites
    // keep cell placement. The realm grid IS the map, even across a real table.
    const freePlace = !card.isSite && card.type !== 'Site' && dropPos;
    this._sendMatchIntent(moveIntent(card.id, {
      toCol: toCell.col, toRow: toCell.row,
      toRegion: toRegion || 'surface',
      autoTap: !!autoTap,
      ...(freePlace ? { x: dropPos.x, z: dropPos.z } : {}),
    }));
  };

  // S4. Server-confirmed moves. `_pendingMoves` maps a mid-flight card to its
  // requested cell; `beginPendingMove` registers it on drop (no visual),
  // `resolvePendingMovesFromMirror` clears it when the diff commits the cell,
  // and the TTL sweep reverts to server truth if the server rejected the move.
  _pendingMoves = new Map();

  _beginPendingMove = (card, toCell) => pendingMoves.beginPendingMove(this, card, toCell);

  _resolvePendingMovesFromMirror = (m) => pendingMoves.resolvePendingMovesFromMirror(this, m);

  // In-flight optimistic-movement guards for the mirror folds: a card whose
  // move intent / SET_POSITION echo hasn't landed yet must not have its
  // optimistic tap or pose reverted by a stale intermediate diff.
  _isMoveInFlight = (cardId, mirrorState) => pendingMoves.isMoveInFlight(this, cardId, mirrorState);

  _isNudgeInFlight = (cardId, mirrorState) => pendingMoves.isNudgeInFlight(this, cardId, mirrorState);

  _snapCardToServerCell = (id, opts) => zoneCmd.snapCardToServerCell(this, id, opts);

  _clearAllPendingMoves = () => pendingMoves.clearAllPendingMoves(this);

  // S5. Server-confirmed casts. `_pendingCasts` marks a fresh play whose cast
  // intent is in flight (optimistic mesh on the table, mirror still zone 'hand');
  // the mirror confirming zone 'play' clears it, and the TTL sweep reverts a
  // server-rejected cast (removes the phantom mesh, returns the card to hand).
  _beginPendingCast = (card) => pendingMoves.beginPendingCast(this, card);

  _resolvePendingCastsFromMirror = (m) => pendingMoves.resolvePendingCastsFromMirror(this, m);

  _clearAllPendingCasts = () => pendingMoves.clearAllPendingCasts(this);

  _emitMoveToZone = (cardId, toZone, opts) => {
    if (!this._authZonesEnabled()) return;
    // A cast/move intent already committed this card's zone+position atomically.
    if (toZone === 'play' && this._intentOwnedCards.has(cardId)) return;
    sendMatchCommand(this._matchKey(), zoneCmd.moveToZoneCommand(cardId, toZone, opts));
  };

  // Authoritative pile ARRANGEMENT (a scry outcome). The sibling of
  // _emitShuffle, and the reason the two commands are distinct: a shuffle's
  // order is the server's to draw, but a scry's order is the player's decision
  // and has to survive as such. Without this the render's post-scry order was
  // reverted by the very next diff. The card scried to the bottom came back as
  // the next draw (valkenhall-qa#53). The server accepts it only as a
  // permutation of the pile it already holds.
  _emitPileReorder = (pile) => {
    if (!this._authZonesEnabled() || !pile) return;
    const zone = this._authZoneForPileName(pile.name);
    if (!zoneCmd.isTopIndexedZone(zone)) return;
    const seat = pile.rotated ? 'p2' : 'p1';
    // Render order is bottom-to-top; the server's order is top-to-bottom (GH-76).
    sendMatchCommand(this._matchKey(), zoneCmd.pileReorderCommand(seat, zone, zoneCmd.shuffleOrderFromRender(pile.cards)));
  };

  // Authoritative pile reorder (shuffle). Only the hidden order-bearing piles
  // (spellbook/atlas/collection) need a server-recorded order; the client
  // asserts the new order (trust-manual gradient) so a reconnect restores it.
  _emitShuffle = (pile) => {
    if (!this._authZonesEnabled() || !pile) return;
    const zone = this._authZoneForPileName(pile.name);
    // Same predicate that owns the inversion below. A drawable zone added to
    // TOP_INDEXED_ZONES but not to this guard would emit an un-reversed order
    // and flip that deck on every shuffle.
    if (!zoneCmd.isTopIndexedZone(zone)) return;
    const seat = pile.rotated ? 'p2' : 'p1';
    // Render order is bottom-to-top; the server's order is top-to-bottom (GH-76).
    sendMatchCommand(this._matchKey(), zoneCmd.shuffleCommand(seat, zone, zoneCmd.shuffleOrderFromRender(pile.cards)));
  };

  // A stale dice modal over an already-resolved roll blocks nothing anymore
  // (the server drives the whole start), but it still looks broken. Close it
  // once the mirror shows the roll decided. Runs on every fold.
  _maybeCloseStaleDiceModal = () => {
    if (!this.state.showFirstPlayerDice) return;
    if (!this._authMirror?.getState?.()?.rollOff?.firstPlayerSeat) return;
    const phase = this._authMirror?.getState?.()?.phase;
    if (phase?.startStep && phase.startStep !== 'roll') {
      this.setState({ showFirstPlayerDice: false });
    }
  };

  // Open the mulligan prompt once the SERVER has dealt this seat's opening
  // hand (the start director owns preGame auto-advance + the opening DRAW; the
  // client renders the dealt hand via applyHandAdditionsFromMirror).
  _maybeRunOpeningDraw = () => {
    const m = this._authMirror?.getState?.();
    if (m?.phase?.startStep !== 'openingDraw') return;
    if (this._authMulliganOpened) return;
    // Only open the mulligan prompt once every own seat's rendered hand has
    // caught up to the mirror (all staggered server draws landed), so the
    // prompt shows a full hand. A seat the server already marked done (a
    // reconnect after mulligan) never re-prompts (openMulliganPrompt is also
    // guarded by the fold's 'done' phase).
    const views = this.props.isCasualSandbox
      ? [['p1', 'handCards', 1], ['p2', 'handCards2', 2]]
      : [[this.state.isHost ? 'p1' : 'p2', 'handCards', this.state.isHost ? 1 : 2]];
    for (const [seat, key] of views) {
      if (m.phase?.mulliganDone?.[seat]) continue;
      if (!m.phase?.openingDealt?.[seat]) return;
      const mirrorHand = (m.hands?.[seat] || []).filter((id) => !this.meshes?.has?.(id));
      if ((this.state[key] || []).length < mirrorHand.length) return;
    }
    this._authMulliganOpened = true;
    for (const [seat, , n] of views) {
      if (!m.phase?.mulliganDone?.[seat]) mulliganController.openMulliganPrompt(this, n);
    }
  };

  _cellNumberAt = (x, z) => {
    const grid = getGameGrid(this.state.spawnConfig);
    if (!grid) return null;
    return getCellNumber(getCellAt(grid, x, z));
  };

  // Broadcast unit hands to the peer, sanitized FOR THE OPPONENT seat: they
  // receive real faces for cards they control or have already seen (sticky
  // per-seat reveal), backs for everything else. Sanitizing for the recipient
  // Not '__public__'. Is what lets a new controller actually see and play
  // the hand they just gained.
  // `unitHands` may be passed explicitly by callers that just built a fresh
  // map inside a setState updater. Preact's setState is async, so reading
  // this.state right after it returns the STALE map (missing the just-added
  // card), which broadcast an empty/old hand to the opponent. Callers that
  // mutate entries in place (reconcileUnitHandControllers) can omit it and
  // fall back to this.state, which already reflects their mutation.
  _syncUnitHands = (unitHands) => {
    const src = unitHands || this.state.gameState.unitHands || {};
    // Authoritative path: send the FULL, unsanitized map; the server stores it
    // and projectForSeat redacts each hand per recipient. Never sanitize before
    // sending here. That would strip the server's own (controller's) truth.
    sendMatchCommand(this._matchKey(), { type: SET_UNIT_HANDS, payload: { unitHands: src } });
  };

  _broadcastGroup = (groupId, ids) => {
    this.sync.setGroup(groupId, ids);
  };

  _broadcastClearGroup = (groupId) => {
    this.sync.clearGroup(groupId);
  };

  _presenceClaim = (cardId, interactionOrder) => {
    // EPHEMERAL presence (server-relayed via game:action, NOT peer-to-peer).
    // Tells the opponent a card is being dragged so they render the live motion.
    // Cosmetic only. The authoritative position is the SET_POSITION commit.
    this.sync.claimCard(cardId, interactionOrder);
  };

  _presenceRelease = (cardId, pos, quat, extras = null) => {
    // Two things on drop: (1) the cosmetic server-relayed final pose so the
    // opponent's card lands smoothly (settled Y via _applyComputedY), and
    // (2) the AUTHORITATIVE SET_POSITION commit. The server's record of truth,
    // reconciled on reconnect. _presenceRelease is the single rest/commit funnel
    // (drag-drop + every commitCardMove), so this covers play / move / teleport /
    // push / pull / defend.
    this.sync.releaseCard(cardId, pos, quat, extras);
    this._emitPosition(cardId, pos[0], pos[2]);
  };

  _emitPosition = (cardId, x, z) => {
    if (!this._authZonesEnabled()) return;
    // P5: a cast/move intent already committed this card's authoritative
    // SET_POSITION atomically. Consume the one-shot flag and skip the legacy
    // per-result position command so the intent is the single writer.
    if (this._intentOwnedCards.has(cardId)) { this._intentOwnedCards.delete(cardId); return; }
    if (typeof x !== 'number' || typeof z !== 'number') return;
    const cell = this._cellAtWorld(x, z);
    // World coords go on the wire for every non-site card (plus auras'
    // intersection anchors) so free-placing clients on either seat can render
    // the exact drop point. A cell-only commit would erase the stored pos
    // server-side and the next fold would snap the card into the cell cascade.
    const inst = this.meshes.get(cardId)?.userData?.cardInstance;
    const isAura = inst?.type === 'Aura';
    const keepCoords = isAura || (!inst?.isSite && inst?.type !== 'Site');
    sendMatchCommand(this._matchKey(), zoneCmd.positionCommand(
      cardId, cell?.col ?? null, cell?.row ?? null,
      keepCoords ? x : undefined, keepCoords ? z : undefined,
    ));
    // Hold the live position folds off this card until the echo lands: the
    // mirror still carries the pre-drop pos, and folding it back over the
    // resting mesh was the release → bounce-back → glide-forward bug.
    if (keepCoords) pendingMoves.beginPendingNudge(this, cardId, x, z);
  };

  _presenceStreamCardPose = (cardId, pos, quat) => {
    // Live drag stream. High-frequency, server-RELAYED (game:action), cosmetic.
    // The opponent sees the card move in real time. Ephemeral + non-authoritative
    // (never persisted, never validated); the SET_POSITION commit on drop is the
    // source of truth. This is the industry-standard presence channel. NOT
    // peer-to-peer, and NOT a validated transaction (that would be the April lag).
    this.sync.streamCardPose(cardId, pos, quat);
  };

  _presenceHover = (cardId) => {
    this.sync.hoverCard(cardId);
  };

  _setFocusedPreviewCard = (cardInstance) => menusAndInspector.setFocusedPreviewCard(this, cardInstance);

  _openInspectorForFocusedCard = () => menusAndInspector.openInspectorForFocusedCard(this);

  // What the dice need from the board: the surfaces a die can rest on (cards
  // and piles), the card meshes by id (a die dropped on a card is drawn from
  // that card), the viewer's camera (so its numeral turns toward this seat),
  // and who to tell when a die's card has left the table.
  _diceContext = () => ({
    surfaces: () => [...this.meshes.values(), ...this.pileMeshes.values()],
    cardMesh: (id) => this.meshes.get(id) ?? null,
    camera: this.scene?.camera ?? null,
    onOrphaned: this._onDieOrphaned,
  });

  _onDieOrphaned = (mesh) => authDice.reportOrphanedDie(this, mesh);

  _broadcastPlaceCard = (cardInstance, { staged = false, silent = false, logVerb = null } = {}) => {
    this.sync.placeCard(cardInstance, { staged, silent, logVerb });
  };

  _broadcastDeckSpawn = (deck, playerNum) => {
    this.sync.emit('deck:spawn', { deck, playerNum });
  };

  // v1.75. Broadcast a subsurface state change so the opponent
  // mirrors burrow / submerge / surface transitions. Without this,
  // attack-target / defend-eligibility filters that read
  // card.subsurface diverge: my burrowed minion looks surface to
  // them and they can target it in violation of region rules.
  _broadcastCardSubsurface = (cardId, region) => {
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    sendMatchCommand(this._matchKey(), authCardState.subsurfaceCommand(cardId, region));
  };

  _broadcastCardStat = (cardId, stat, value, opts) => {
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    sendMatchCommand(this._matchKey(), authCardState.statsCommand(this, cardId, stat, value, opts));
  };

  // Red pulse over a player's HUD avatar when their life total drops.
  // fired by HudLifeTicker the moment it starts counting down.
  _flashHudDamage = (player) => {
    this.setState((s) => ({ hudDamageFlash: { ...s.hudDamageFlash, [player]: Date.now() } }));
  };

  _broadcastCardStatus = (cardId, statusKey, active) => {
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    // The local toggle already mutated card.statuses; send the absolute array.
    const card = this.meshes.get(cardId)?.userData?.cardInstance;
    sendMatchCommand(this._matchKey(), authCardState.statusesCommand(cardId, card?.statuses || []));
  };

  // Sync the equippedCards array on a card to the opponent. Fired
  // from every mutation site (equipCardTo, detachEquipment, the auto-
  // detach inside removeCardFromTable) so both clients keep a
  // matching cardInstance. Needed for the hover overlay and the
  // inspector to look right on both screens AND for both side's local
  // save blobs to capture the same equipment state on autoSave.
  _broadcastCardEquipment = (cardId, equippedCards) => {
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    sendMatchCommand(this._matchKey(), authCardState.equipmentCommand(cardId, equippedCards));
  };

  _broadcastFlip = (cardInstance, faceDown) => {
    // Auth mode (R2d): a face flip is an authoritative reveal mutation. Flipping
    // face-up reveals the identity to the opponent (added to revealedTo); the
    // per-seat projected diff carries the identity to them. Flipping face-down
    // just mirrors the flag (revealedTo is sticky. The opponent keeps the name
    // it already learned, matching the legacy P2P behavior). Replaces card:flip.
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    const opp = this._localSeat() === 'p1' ? 'p2' : 'p1';
    const seats = faceDown ? undefined : [opp];
    sendMatchCommand(this._matchKey(), authReveals.revealCommand(cardInstance.id, seats, faceDown));
  };

  _broadcastOwnership = (cardId, rotated) => {
    if (!this._authZonesEnabled()) return; // sandbox: local state only, no wire
    sendMatchCommand(this._matchKey(), authCardState.controllerCommand(cardId, rotated));
  };


  handleBoardTutorialDismiss = () => boardLifecycle.handleBoardTutorialDismiss(this);

  handleBoardTutorialStepChange = (stepKey) => boardLifecycle.handleBoardTutorialStepChange(this, stepKey);

  _scheduleBoardTutorialCheck = (delayMs) => boardLifecycle.scheduleBoardTutorialCheck(this, delayMs);

  _maybeArmFirstPlayerDice = () => boardLifecycle.maybeArmFirstPlayerDice(this);

  _maybeAutoPlayBoardTutorial = () => boardLifecycle.maybeAutoPlayBoardTutorial(this);

  _queueEventTutorial = (key, ctx) => eventTutorials.queueEventTutorial(this, key, ctx);

  handleEventTutorialDismiss = () => eventTutorials.handleEventTutorialDismiss(this);

  _onWindowClick = (e) => boardLifecycle.onWindowClick(this, e);

  componentDidMount = () => boardLifecycle.componentDidMount(this);

  componentWillUnmount = () => boardLifecycle.componentWillUnmount(this);

  componentDidUpdate = (prevProps, prevState) => boardLifecycle.componentDidUpdate(this, prevProps, prevState);

  handleResize = () => boardLifecycle.handleResize(this);

  findStackHeightAt = (x, z, exclude = null) => stackPhysics.findStackHeightAt(this, x, z, exclude);


  _nextInteractionOrder = () => stackPhysics.nextInteractionOrder(this);

  _cardOBB2D = (mesh) => stackPhysics.cardOBB2D(this, mesh);

  _obbsOverlap2D = (A, B) => stackPhysics.obbsOverlap2D(this, A, B);

  _dumpStacks = () => stackPhysics.dumpStacks(this);

  _applyComputedY = () => stackPhysics.applyComputedY(this);

  _applyRegionViewVisibility = () => viewTransitions.applyRegionViewVisibility(this);

  _computeCardViewMotion = (trans, cardId, elapsed) => viewTransitions.computeCardViewMotion(this, trans, cardId, elapsed);

  _setViewVisualState = (mesh, cardId, opacity, yOff) => viewTransitions.setViewVisualState(this, mesh, cardId, opacity, yOff);

  _clearViewVisualState = (mesh, cardId) => viewTransitions.clearViewVisualState(this, mesh, cardId);

  _fadeChildOverlays = (mesh, factor, opts = {}) => viewTransitions.fadeChildOverlays(this, mesh, factor, opts);

  _fadeCardEffects = (cardId, factor, opts = {}) => viewTransitions.fadeCardEffects(this, cardId, factor, opts);

  _ensureShaderOpacityUniform = (material) => viewTransitions.ensureShaderOpacityUniform(this, material);

  _beginViewTransition = (target) => viewTransitions.beginViewTransition(this, target);

  _setCardEffectsVisible = (cardId, visible) => viewTransitions.setCardEffectsVisible(this, cardId, visible);

  tickPhysicsSync = () => stackPhysics.tickPhysicsSync(this);


  _handStateKeyForCard = (cardInstance) => handState.handStateKeyForCard(this, cardInstance);

  addToHand = (cardInstance) => handState.addToHand(this, cardInstance);

  addToUnitHand = (ownerId, cardInstance, ownerArt = null, index = null) => handState.addToUnitHand(this, ownerId, cardInstance, ownerArt, index);

  removeFromUnitHand = (ownerId, spellId) => handState.removeFromUnitHand(this, ownerId, spellId);

  _reconcileUnitHandControllers = () => handState.reconcileUnitHandControllers(this);

  _flushUnitHandOnLeave = (ownerId, opts) => handState.flushUnitHandOnLeave(this, ownerId, opts);

  beginUnitHandDrag = (event, spell, ownerId) => handState.beginUnitHandDrag(this, event, spell, ownerId);

  returnDragToUnitHand = (cardInstance) => handState.returnDragToUnitHand(this, cardInstance);

  _setUnitHandGlow = (ownerId) => handState.setUnitHandGlow(this, ownerId);

  removeFromHand = (cardInstance) => handState.removeFromHand(this, cardInstance);

  _isDisplayedHandKey = (key) => handState.isDisplayedHandKey(this, key);

  _activeHandKey = () => handState.activeHandKey(this);

  _mulliganPhaseForCard(cardInstance) {
    return mulliganController.mulliganPhaseForCard(this, cardInstance);
  }

  getActiveHandCards = () => handState.getActiveHandCards(this);

  _handCountFor = (player) => handState.handCountFor(this, player);

  handleGameHotkey = (event) => hotkeys.handleGameHotkey(this, event);

  dispatchHotkeyAction = (actionId, event) => hotkeys.dispatchHotkeyAction(this, actionId, event);

  hotkeyFlip = () => hotkeys.hotkeyFlip(this);

  hotkeyTap = () => hotkeys.hotkeyTap(this);

  drawCardsStaggered = (pileId, count) => hotkeys.drawCardsStaggered(this, pileId, count);

  requestExit = () => gameLifecycle.requestExit(this);

  // --- Session & Multiplayer ---

  // Build the semantic adapter MatchConnection drives. The orchestrator owns
  // the handshake/seat/sync decisions; this maps its intent onto GameBoard's
  // setState + scene + socket-wiring. The post-join game setup (deck spawn vs.
  // restore, dice/draw gating) stays in componentDidMount's then-block.
  _makeConnectionHost = () => ({
    connecting: (loadingMessage, opts = {}) => this.setState({
      loadingMessage,
      ...(opts.canCancel !== undefined ? { canCancelJoin: opts.canCancel } : {}),
    }),
    roomReady: ({ roomCode, isHost, roomInfo, connectionStatus, matchStartTime, loadingMessage, canCancelJoin }) => {
      const patch = { roomCode, isHost, roomInfo, connectionStatus };
      if (matchStartTime !== undefined) patch.matchStartTime = matchStartTime;
      if (loadingMessage !== undefined) patch.loadingMessage = loadingMessage;
      if (canCancelJoin !== undefined) patch.canCancelJoin = canCancelJoin;
      this.setState(patch);
    },
    persistActiveSession: ({ roomCode, isHost, isArenaMatch }) => {
      try {
        localStorage.setItem('valkenhall-active-session', JSON.stringify({
          roomCode, isHost, isArenaMatch, startedAt: Date.now(),
        }));
      } catch { /* localStorage unavailable. Non-fatal */ }
    },
    wireSocket: () => this.setupSocketListeners(),
    applySeatPerspective: (isHost) => this.applySeatPerspective(isHost),
    connectionFailed: ({ error, final } = {}) => {
      console.error('Match connection failed:', error);
      this.setState(final
        ? { connectionStatus: 'offline', connectionFailed: true, loadingMessage: t('Connection failed'), canCancelJoin: false }
        : { connectionStatus: 'offline' });
    },
    isUnmounted: () => this._unmounted,
    // Pull the authoritative per-seat (or spectator) Full from the server's
    // actor. The ONLY resume/rebuild source since the legacy P2P + blob
    // channels were retired. The mirror fold rebuilds the scene from it.
    pullAuthoritativeState: (roomCode) => {
      sessionPersistence.armAuthStateGate(this);
      requestMatchFull(this._matchKey(roomCode));
    },
  });

  initSession = () => gameLifecycle.initSession(this);

  cancelJoinAttempts = () => gameLifecycle.cancelJoinAttempts(this);

  setupSocketListeners = () => socketListeners.setupSocketListeners(this);

  _serializeHandForOpponent = (hand) => sessionPersistence.serializeHandForOpponent(this, hand);

  serializeTableState = () => sessionPersistence.serializeTableState(this);

  restoreSession = (session) => sessionPersistence.restoreSession(this, session);

  // The DURABLE match key for the server-authoritative snapshot channel: the
  // instance id, which is STABLE across pause/resume and the room re-mint that
  // follows (the room code is re-minted every resume, which orphaned room-keyed
  // snapshots). Falls back to the room code for non-instance matches (friend /
  // quick) that don't durably resume. instanceId comes from PROPS, so it's
  // available synchronously even before the roomReady setState flushes. Which
  // is also why the host-resume snapshot pull is reliable. An explicit room-code
  // override is still accepted for the non-instance host-resume race.
  _matchKey = (roomCodeOverride) => this.props.instanceId || roomCodeOverride || this.state.roomCode || null;

  // Re-pull the authoritative Full with the connection's retry-with-backoff
  // loop. Kept as a method so socket handlers can request a fresh state
  // without reaching into this.conn.
  requestAuthoritativeState = (roomCodeOverride) => {
    if (this.conn) this.conn.requestFullWithRetry(roomCodeOverride || this.state.roomCode);
    else requestMatchFull(this._matchKey(roomCodeOverride));
  };

  clearLocalGameState = () => sessionPersistence.clearLocalGameState(this);

  _flushSharedStateOnNextSave = () => sessionPersistence.flushSharedStateOnNextSave(this);


  scheduleLocalSave = () => sessionPersistence.scheduleLocalSave(this);

  _tagWithInstance = (state) => sessionPersistence.tagWithInstance(this, state);

  _instanceSaveId = () => sessionPersistence.instanceSaveId(this);

  autoSave = (opts = {}) => sessionPersistence.autoSave(this, opts);

  _doAutoSave = (opts = {}) => sessionPersistence.doAutoSave(this, opts);

  manualSave = (name, existingId = null) => sessionPersistence.manualSave(this, name, existingId);

  quickSave = () => sessionPersistence.quickSave(this);

  openSaveDialog = () => sessionPersistence.openSaveDialog(this);


  isOwnedByLocalPlayer = (obj) => selectionGroups.isOwnedByLocalPlayer(this, obj);

  isCardOnLocalSide = (cardInstance) => selectionGroups.isCardOnLocalSide(this, cardInstance);

  isPileLocallyOwned = (pile) => selectionGroups.isPileLocallyOwned(this, pile);

  _canFreeformDraw = () => selectionGroups.canFreeformDraw(this);

  applyOwnershipOutline = (mesh, settings) => selectionGroups.applyOwnershipOutline(this, mesh, settings);

  refreshOwnershipOutlines = () => selectionGroups.refreshOwnershipOutlines(this);


  getInteractableMeshes = () => selectionGroups.getInteractableMeshes(this);


  collectHighlightedCardIds = () => selectionGroups.collectHighlightedCardIds(this);

  collectDragGroupForCard = (primaryCardId) => selectionGroups.collectDragGroupForCard(this, primaryCardId);

  updateSelectionHighlights = () => selectionGroups.updateSelectionHighlights(this);

  worldPositionToScreen = (worldPos) => overlayProjections.worldPositionToScreen(this, worldPos);

  _localDrawPhaseChoicePrompt = () => turnPhaseRunner.localDrawPhaseChoicePrompt(this);

  _registerStartDrawRefs = (refs) => overlayProjections.registerStartDrawRefs(this, refs);

  updateStartDrawOverlays = () => overlayProjections.updateStartDrawOverlays(this);

  updateHugRect = () => overlayProjections.updateHugRect(this);

  syncHoverStatusOverlay = () => overlayProjections.syncHoverStatusOverlay(this);

  syncEquipmentOverlays = () => overlayProjections.syncEquipmentOverlays(this);

  registerEquipmentOverlayRef = (cardId, el) => overlayProjections.registerEquipmentOverlayRef(this, cardId, el);

  updateHoverStatusOverlayPosition = () => overlayProjections.updateHoverStatusOverlayPosition(this);

  updateEquipmentOverlaysPosition = () => overlayProjections.updateEquipmentOverlaysPosition(this);

  /** True if the given client-space point is inside the current hug rect. */
  isPointInsideHugRect = (clientX, clientY) => overlayProjections.isPointInsideHugRect(this, clientX, clientY);

  isPointOverHand = (clientX, clientY) => overlayProjections.isPointOverHand(this, clientX, clientY);

  isPointOverUnitBadge = (ownerId, clientX, clientY) => overlayProjections.isPointOverUnitBadge(this, ownerId, clientX, clientY);

  freezeGroup = (groupId) => selectionGroups.freezeGroup(this, groupId);

  unfreezeGroup = (groupId) => selectionGroups.unfreezeGroup(this, groupId);

  assignGroup = (groupId, cardIds) => selectionGroups.assignGroup(this, groupId, cardIds);

  /** Dissolve a group. Unfreezes members back to DYNAMIC. */
  dissolveGroup = (groupId) => selectionGroups.dissolveGroup(this, groupId);

  evictCardFromGroup = (cardId) => selectionGroups.evictCardFromGroup(this, cardId);

  findHitObject = (hitObject) => selectionGroups.findHitObject(this, hitObject);

  handleMouseDown = (event) => pointerInteractions.handleMouseDown(this, event);

  /**
   * Pick up a single card OR a whole selection/group starting from the
   * given hit mesh. Builds this.dragging.members with one entry per card
   * to drag; the single-card case falls out naturally as a members array
   * of length 1.
   */
  _computeDragReach = (members, freshPlayIds) => pointerInteractions.computeDragReach(this, members, freshPlayIds);

  startCardDrag = (opts) => pointerInteractions.startCardDrag(this, opts);

  beginCardDragCandidate = (opts) => pointerInteractions.beginCardDragCandidate(this, opts);

  onDragCheckHandoff = (event) => pointerInteractions.onDragCheckHandoff(this, event);

  handleMouseMove = (event) => pointerInteractions.handleMouseMove(this, event);

  handleMouseUp = (event) => pointerInteractions.handleMouseUp(this, event);

  renderMarqueeOverlay = () => boardRender.renderMarqueeOverlay(this);

  handleWindowMouseUp = (event) => pointerInteractions.handleWindowMouseUp(this, event);

  handleWindowKeyDown = (event) => pointerInteractions.handleWindowKeyDown(this, event);

  tryOpenInspectorFromArmed = () => pointerInteractions.tryOpenInspectorFromArmed(this);

  handleWindowKeyUp = (event) => pointerInteractions.handleWindowKeyUp(this, event);

  handleWindowBlur = () => pointerInteractions.handleWindowBlur(this);

  firePingAtMouse = (event) => pointerInteractions.firePingAtMouse(this, event);

  toggleGroupForSelection = () => pointerInteractions.toggleGroupForSelection(this);

  _openCardActionMenu = (cardInstance, event) => menusAndInspector.openCardActionMenu(this, cardInstance, event);

  // v1.43. Move and Attack flow entry point. The intent flag is
  // an instance field (not state) because it's transient. Only
  // matters between the action-menu click and the next drop. State
  // would force re-renders we don't want here.
  //
  // intent ∈ 'move' | 'move_and_attack' | 'attack' | 'shoot'
  _beginCombatAction = (cardInstance, intent) => {
    this._pendingCombatIntent = { cardId: cardInstance.id, intent };
    if (intent === 'shoot') {
      this._openShootTargetPrompt(cardInstance);
      return;
    }
    if (intent === 'attack') {
      // Attack-only: no movement spent, so the full step budget is
      // available for a same-cell burrow/submerge-to-strike transition.
      this._openCombatTargetPrompt(cardInstance, 1 + effectiveMovementBonus(cardInstance));
    }
    // For 'move' and 'move_and_attack' the player picks up the card
    // and drags as normal. _applyMovementGating consumes the intent
    // on commit (drop), opening the attack modal for 'move_and_attack'.
  };

  // v1.70. Combat eligibility predicates shared by the action menu
  // (double-click) and the right-click context menu. Returns:
  //   combatant. Show the combat items at all (Minion/Avatar on local side)
  //   canMove. Move / Move&Attack are enabled (untapped, not sick,
  //               not disabled, my turn)
  //   canAttack. Attack / Move&Attack are enabled (same as canMove)
  // A 0-power unit may still Move and Attack: the attack deals no damage but
  // is a legal action (on_attack triggers, forcing a defend, contesting a
  // site). Power is NOT an eligibility gate. Mirrors the gating logic in
  // _openCardActionMenu so both menus surface the same item state.
  _canBeginCombat = (cardInstance) => {
    if (!cardInstance) return { combatant: false, canMove: false, canAttack: false };
    const isCombatant = countsAsUnit(cardInstance);
    if (!isCombatant) return { combatant: false, canMove: false, canAttack: false };
    const isLocal = this.isCardOnLocalSide(cardInstance);
    if (!isLocal) return { combatant: false, canMove: false, canAttack: false };
    const controller = cardInstance.rotated ? 'p2' : 'p1';
    const isMyTurn = this.state.currentTurn === controller;
    const canAct = isMyTurn
      && !cardInstance.tapped
      && !isDisabled(cardInstance)
      && !hasSummoningSickness(cardInstance);
    return {
      combatant: true,
      canMove: canAct,
      // A manual session resolves no combat: there is no attack action to
      // offer, so the menu item and the move-and-attack prompt both drop out.
      // Moving stays available. Players still push their units around.
      canAttack: canAct && this._automationsEnabled(),
    };
  };

  // v1.76. Compute the Set of subsurface regions the attacker can
  // transition to from its current cell given remaining step budget.
  // A burrow/submerge transition consumes 1 step. Returns a Set with
  // at most 'underground' and/or 'underwater'. Empty when the attacker
  // has neither keyword or has no step budget left.
  _computeAttackTransitions = (attacker, remainingSteps) => {
    const out = new Set();
    if (!attacker || remainingSteps < 1) return out;
    if (hasBurrowing(attacker)) out.add('underground');
    if (hasSubmerge(attacker))  out.add('underwater');
    return out;
  };

  _closeAttackModal = () => {
    this._pendingCombatIntent = null;
    this.setState({ attackTargetModal: null });
  };

  // Backdrop-dismiss of the attack picker. For the engine flow after a move,
  // abandoning the attack means "moved without attacking" → the opponent's
  // on-cell units may intercept.
  _onAttackModalCancel = () => {
    const modal = this.state.attackTargetModal;
    this._closeAttackModal();
    if (modal?._afterMove) {
      const attacker = this._cardInstanceById(modal.attackerId);
      const mesh = attacker && this.meshes.get(modal.attackerId);
      const cell = mesh ? this._cellAtWorld(mesh.position.x, mesh.position.z) : null;
      if (attacker && cell) this._maybeOfferInterceptAfterMove(attacker, cell);
    }
  };

  _cardInstanceById = (id) => this.meshes.get(id)?.userData?.cardInstance || null;

  // Like _cardInstanceById but ALSO finds CARRIED cards (equipped relics.
  // the Omphaloi. Render on their bearer and have no standalone mesh). The
  // owner-locked cast gate reads presence through this; a meshes-only check
  // made every carried owner register as "left play" and fizzled its spells.
  _inPlayInstanceById = (id) => {
    if (!id) return null;
    const direct = this.meshes.get(id)?.userData?.cardInstance;
    if (direct) return direct;
    for (const [, mesh] of this.meshes) {
      const eq = mesh.userData?.cardInstance?.equippedCards;
      const hit = eq?.find?.((c) => c.id === id || c.instanceId === id);
      if (hit) return hit;
    }
    return null;
  };

  // Fully-computed combat stats for resolveFight. `def` mirrors
  // findDeadMinions (baseLife + auraStatDelta) so predicted deaths match
  // realized ones. Lance grants +1 power and first strike; Disabled → 0.
  _readCombatStats = (id) => {
    const card = this._cardInstanceById(id);
    if (!card) {
      return { atk: 0, def: 0, firstStrike: false, lethal: false, projectile: false, isSite: false, type: 'Minion' };
    }
    const disabled = isDisabled(card);
    // Codex Lance: each Lance adds +1 damage (vs units); any Lance grants
    // first strike ("More than one Lance confers no extra speed benefit").
    const lanceCount = (card.equippedCards || []).filter(
      (eq) => ((eq.name || this._getFullCard(eq.cardId)?.name || '').toLowerCase() === 'lance'),
    ).length;
    // Effective Power/Life both fold in the static-aura delta (Jihad's
    // +1, Gift of the Serpent, …). The SAME numbers the HUD and the
    // death sweep use, so a buff that shows on the card also strikes.
    const power = effectivePower(card);
    const def = effectiveLife(card);
    return {
      atk: disabled ? 0 : power + lanceCount,
      def,
      firstStrike: !disabled && (lanceCount > 0 || hasKeyword(card, 'first_strike')),
      lethal: hasKeyword(card, 'lethal'),
      projectile: false,
      isSite: !!card.isSite,
      type: card.type,
      // A defending AVATAR routes its assigned damage to its controller's
      // life (resolveFight), so the seat has to ride along with the stats.
      seat: this._seatOf(card),
      lanceBonus: disabled ? 0 : lanceCount,
      _hasLance: lanceCount > 0,
    };
  };

  // Lance breaks (is removed) after the bearer fights a unit (codex Lance).
  _breakLanceOn = (id) => {
    const card = this._cardInstanceById(id);
    if (!card?.equippedCards?.length) return;
    const remaining = [];
    let broke = false;
    for (const eq of card.equippedCards) {
      const name = (eq.name || this._getFullCard(eq.cardId)?.name || '').toLowerCase();
      if (name === 'lance') { broke = true; continue; }
      remaining.push(eq);
    }
    if (broke) {
      card.equippedCards = remaining;
      this._broadcastCardEquipment(card.id, card.equippedCards);
      this.rebuildStatusBadges(card.id); // clear the LNC status badge
      this.logAction("{card}'s Lance breaks.", { card: card.name || msg('Unit') });
    }
  };

  _seatOf = (card) => (card?.rotated ? 'p2' : 'p1');

  // ── Combat seat helpers ───────────────────────────────────────────
  // The server is the sole combat resolver; `pendingCombat` mirrors the
  // authoritative record and survives reconnect via the server save.
  _amHost = () => this.props.isCasualSandbox || this.state.isHost;
  // The seat the local user is ACTING as. Online this is pinned by isHost; a
  // solo pilot owns both seats and _localSeat follows the piloted side
  // (activePlayerNum, which the turn-flip fold keeps on the current turn).
  _localSeat = () => {
    if (this.props.isCasualSandbox) return this.state.activePlayerNum === 2 ? 'p2' : 'p1';
    return this.state.isHost ? 'p1' : 'p2';
  };

  // Contextual acting-seat resolution for the solo pilot (registered as the
  // socketClient envelope-seat provider while a solo board is mounted).
  // Resolution order: explicit seat on the intent/command → combat-specific
  // derivation (defend/intercept answer FOR the seat opposite the attacker)
  // → the subject card's live controller from the authoritative mirror →
  // the piloted seat. This lets the pilot answer an opponent-addressed
  // prompt (defend, choose) without tabbing. Pure logic in
  // net/soloActingSeat.js (unit-tested).
  _soloActingSeat = (msg, kind) => {
    if (!this.props.isCasualSandbox) return null;
    return resolveSoloActingSeat({
      msg,
      kind,
      mirror: this._authMirror?.getState?.(),
      pilotSeat: this.state.activePlayerNum === 2 ? 'p2' : 'p1',
    });
  };

  // Which seat must answer this prompt: the defending player for
  // defend/intercept, the attacker for damage assignment. The defender
  // seat derives from the prompt's own SUBJECT. The attacked card's
  // controller (defend) or an intercept candidate's controller
  // (intercept). So an unresolvable attacker instance or a desynced
  // attacker controller flag can never route the defend window back to
  // the attacker. Precedence rules are pure (resolveActingSeat).
  _combatActingSeat = (prompt, fight) => {
    const attackerCard = this._cardInstanceById(fight?.attacker?.instanceId);
    const attackerSeat = attackerCard ? this._seatOf(attackerCard) : null;
    const defenderSeat = this._combatDefenderSeat(prompt, fight);
    if (!attackerCard && !defenderSeat) {
      console.warn('[combat] acting seat unresolvable. Attacker missing and no target/candidates', prompt?.kind);
    }
    return resolveActingSeat({ kind: prompt.kind, attackerSeat, defenderSeat });
  };

  // Seat of the defending player, derived from the fight's subject. The
  // host's own instance lookup is authoritative; the descriptor's `seat`
  // (stamped at declare time for site/avatar/unit targets) only fills in
  // when the instance can't be resolved (e.g. swept mid-resolution).
  // Intercept fights carry no target. Any candidate's controller works,
  // interceptors are always the defending player's units.
  _combatDefenderSeat = (prompt, fight) => {
    const target = prompt?.target ?? fight?.target;
    const targetCard = target?.instanceId ? this._cardInstanceById(target.instanceId) : null;
    if (targetCard) return this._seatOf(targetCard);
    if (target?.seat === 'p1' || target?.seat === 'p2') return target.seat;
    const candidateIds = prompt?.candidates?.length ? prompt.candidates : (fight?.defenders || []);
    for (const id of candidateIds) {
      const card = this._cardInstanceById(id);
      if (card) return this._seatOf(card);
    }
    return null;
  };

  _clearPendingCombat = () => {
    clearTimeout(this._combatClearTimer);
    this._combatClearTimer = null;
    this.setState(
      (s) => ({ gameState: { ...s.gameState, pendingCombat: null } }),
      () => {
        this._drainPileDepartures();
        this._maybePresentPendingGameOver();
      },
    );
  };

  // ── Combat liveness loop ───────────────────────────────────────────
  // Runs every COMBAT_RECONCILE_MS on both seats (and immediately on
  // reconnect). The server's mirror is the sole truth for whether a fight is
  // live; a single lost terminal frame (combat: null diff) used to strand the
  // non-acting client forever behind its waiting lock. The auth reconcile
  // re-derives the corrective action from the mirror so divergence self-heals.
  _combatReconcileTick = () => {
    if (this._unmounted) return;
    this._authCombatReconcileTick();
  };

  // Auth-path combat self-heal. The non-acting client (the attacker locked behind
  // "Waiting for opponent...") that dropped the terminal `combat:null` diff stays
  // bricked forever. No later diff exposes the version gap, so the mirror's
  // requestFull never fires. Reconcile the locally-shown waiting lock against the
  // authoritative mirror and apply the one corrective action.
  _authCombatReconcileTick = () => {
    const lock = this._activeCombatPrompt;
    if (!lock || lock.kind !== 'waiting') return;
    const mirror = this._authMirror?.getState?.();
    // No mirror yet (pre-Full): nothing authoritative to reconcile against.
    if (!mirror) return;

    const action = reconcileAuthCombat({
      lock,
      mirrorCombat: mirror.combat || null,
      localSeat: this._localSeat(),
      lockShownAt: this._combatLockShownAt ?? null,
      now: Date.now(),
      stuckAfterMs: COMBAT_RECONCILE_MS,
    });
    if (action === 'clear') {
      // The mirror already resolved (or replaced) this fight; re-fold it so the
      // null record tears the waiting prompt down. Idempotent.
      authCombat.applyCombatFromMirror(this, mirror);
      authCombat.applyGameOverFromMirror(this, mirror);
    } else if (action === 'request-full') {
      // The mirror still shows this fight but it has stalled. Pull a fresh Full
      // so a pure lost-frame (no socket reconnect) self-heals.
      requestMatchFull(this._matchKey());
    }
  };

  _fightFromPending = (pc) => ({
    id: pc.combatId,
    attacker: { instanceId: pc.attacker?.instanceId },
    target: pc.target || null,
    fromCell: pc.attacker?.cell || null,
    defenders: pc.chosenDefenders || [],
    isIntercept: pc.phase === 'intercepting',
    projectile: !!pc.projectile,
  });

  // Map a persisted pendingCombat phase to the prompt kind the acting seat
  // re-opens on resume. Mirrors the engine's phase → prompt mapping.
  _promptKindForPendingPhase = (phase) => {
    if (phase === 'assigning') return 'assign';
    return phase === 'intercepting' ? 'intercept' : 'defend';
  };

  // ── Combat prompts (reuse the double-click CardActionMenu) ─────────
  // move_or_attack is the attacker's own pre-declare action choice. Always
  // local to the acting client, no fight/pendingCombat yet. (Target selection
  // is a separate modal above the square, not an action-menu prompt. Defend /
  // intercept / assign prompts open from the authoritative record via
  // authCombat.applyCombatFromMirror.)
  _openCombatPrompt = (prompt) => {
    if (prompt.kind !== 'move_or_attack') return;
    this._activeCombatPrompt = prompt;
    this._renderCombatMenu(prompt);
  };

  // AAA polish: when the acting seat has NO legal defenders (including the
  // stealth-attacker case where defending is impossible) or an intercept
  // record with no candidates, the window offers no choice. Auto-submit the
  // decline so neither player stares at a dead prompt. Returns true when it
  // declined (caller skips rendering).
  _combatAutoDecline = (prompt) => {
    if (prompt.kind === 'defend') {
      const ctx = this._combatActorCtx();
      const attacker = this._cardInstanceById(ctx.attackerId);
      const targetCard = ctx.target?.instanceId ? this._cardInstanceById(ctx.target.instanceId) : null;
      const defMesh = targetCard && this.meshes.get(targetCard.id);
      const defCell = defMesh ? this._cellAtWorld(defMesh.position.x, defMesh.position.z) : null;
      // Unresolvable board (meshes still settling after a reconnect
      // restore) is NOT "no choices." Keep the prompt rather than
      // silently declining a fight the player never saw.
      if (!attacker || !targetCard || !defCell) return false;
      const eligible = this._collectEligibleDefenders(attacker, targetCard, defCell);
      if (eligible.length === 0) {
        // A silent decline is a legality judgement. Record it so a stale
        // eligibility input (the GH-67 sickness-flag divergence) is visible
        // in the devlogs instead of looking like the prompt never opened.
        devLog('combat_auto_decline', {
          combatId: this._activeCombatPrompt?.combatId ?? null,
          attackerId: attacker.id,
          targetId: targetCard.id,
          targetKind: ctx.target?.kind ?? null,
        });
        this._combatSubmitDefenders([]);
        return true;
      }
      return false;
    }
    if (prompt.kind === 'intercept') {
      const ids = (prompt.candidates && prompt.candidates.length)
        ? prompt.candidates : (this._activeCombatPrompt?.candidates || []);
      if (ids.length === 0) { this._combatSubmitInterceptors([]); return true; }
      return false;
    }
    return false;
  };

  // Tear down the local combat UI only (prompt, defend-drag, assign modal).
  // Does NOT touch the durable record. Safe to call when reacting to a null
  // record without re-broadcasting (avoids a host echo loop).
  // A selection still armed here was never submitted (_combatConfirmSelect
  // nulls it on submit). The fight ended out from under it, so refund the
  // preview taps and snap dragged-in defenders home.
  _closeCombatUI = ({ keepScene = false } = {}) => {
    const sel = this._combatSelect;
    if (sel?.chosen?.size) {
      for (const id of sel.chosen) this._setCombatSelectionTap(id, false);
      for (const [id, origin] of (sel.draggedOrigins || [])) {
        this._snapCardTo(id, origin);
        // The drag's release committed the contested cell. Re-commit the
        // origin or the server strands the un-declared defender there.
        this._emitPosition(id, origin.x, origin.z);
      }
    }
    this._activeCombatPrompt = null;
    this._combatSelect = null;
    // A scene in playback owns its own dismissal (onDone / skip). The
    // record clearing right after resolution must not cut the show. A staged
    // fight also keeps its assignment panel up (`keepScene`) so the first
    // strike window morphs into it instead of flashing an empty board.
    this.setState((s) => ({
      cardActionMenu: null,
      combatScene: (s.combatScene?.mode === 'playback' || keepScene) ? s.combatScene : null,
    }));
  };

  // Close the prompt AND clear the durable record (host). Used when the local
  // player ends/cancels a fight. A pending linger timer means a resolved
  // record was just published; the timer owns the clear in that case.
  _closeCombatPrompt = () => {
    if (this._amHost() && !this._combatClearTimer) this._clearPendingCombat();
    this._closeCombatUI();
  };

  // ── Submit routing ──
  // The server is the sole resolver: every submit sends an intent and the
  // authoritative diff / match:combat events drive the UI from there.
  _combatSubmitDefenders = (ids, removeTarget = false) => {
    // A dragged defender already synced its move onto the contested cell
    // through the SHARED drag pipeline (claim → stream → release, same as any
    // card), so the attacker has already seen it live and landed. The server
    // only needs the defender ids + the withdraw choice.
    this._sendMatchIntent(combatIntents.defendIntent(ids || [], removeTarget));
    this._combatSelect = null;
    this._activeCombatPrompt = { kind: 'waiting', combatId: this.state.gameState?.pendingCombat?.combatId };
    this._renderCombatMenu({ kind: 'waiting', label: t('Resolving...') });
  };

  _combatSubmitInterceptors = (ids) => {
    this._sendMatchIntent(combatIntents.interceptIntent(ids || []));
  };

  _combatSubmitAssign = (map) => {
    this._sendMatchIntent(combatIntents.assignIntent(Array.from(map.entries())));
  };

  // Guest acting seat: a host-driven combat record arrived for this client
  // (defend / intercept phases. Assignment routes through _syncAssignScene).
  // Render the same prompt UI; submits route back to the host as messages.
  _openActorPrompt = (msg) => {
    this._activeCombatPrompt = {
      kind: msg.kind, combatId: msg.combatId, actingSeat: msg.actingSeat,
      attackerId: msg.attacker?.instanceId, target: msg.target || null,
      candidates: msg.candidates || [],
    };
    // No legal choice → resolve silently instead of a dead prompt.
    // _combatAutoDecline is evaluation-safe: an unresolvable board
    // (meshes settling after a reconnect restore) keeps the prompt
    // instead of declining, so this is safe on rehydrate too. And it
    // MUST run here: a Stealth attacker's attack cannot be defended
    // (codex), so the defender would otherwise stare at a dead prompt.
    if (this._combatAutoDecline({ kind: msg.kind, candidates: msg.candidates || [] })) return;
    this._renderCombatMenu({
      kind: msg.kind,
      attackerId: msg.attacker?.instanceId,
      target: msg.target || null,
      candidates: msg.candidates || [],
    });
  };

  // ── Howl from Beyond cosmetic reveal ───────────────────────────────
  //
  // SANDBOX-ONLY entry point. Online the reveal is server-orchestrated: the
  // storyline-cast hook deals + appends a boardFx manifest, and BOTH clients
  // render it via the boardFx fold (controllers/boardFx.js). No caster relay.
  // In sandbox resolveHowlFromBeyond resolves locally and plays back here.
  // The reveal meshes are temporary visual-only meshes created at the void
  // cells and disposed when the reveal ends. They never touch gameState.
  _publishHowl = (spellbook, plan) => {
    try {
      if (!this.scene?.scene || !Array.isArray(plan) || plan.length === 0) return;
      const seat = spellbook?.rotated ? 'p2' : 'p1';
      this._playHowlScene({
        howlId: `howl-${this._matchKey() || 'local'}-${Date.now()}`,
        seat,
        originX: spellbook?.x ?? 0,
        originZ: spellbook?.z ?? 0,
        steps: plan.map((s) => ({
          cardId: s.cardId,
          name: s.name,
          imageUrl: s.imageUrl,
          x: s.x,
          z: s.z,
          classification: s.classification,
          accent: s.accent,
        })),
      });
    } catch (err) {
      console.warn('[howl] reveal publish failed (cosmetic, ignored)', err);
    }
  };

  // THE single Howl playback entry point. Sandbox (publish) + the online
  // boardFx fold both land here. Idempotent: one procession per howlId.
  _playHowlScene = (rec) => {
    try {
      if (!rec?.steps?.length || !this.scene?.scene) return;
      const key = rec.howlId || `howl-${Date.now()}`;
      if (this._playedHowls.has(key)) return;
      this._playedHowls.add(key);

      const sceneRoot = this.scene.scene;
      const accentColor = (accent) => {
        if (accent === 'warning') return 0xc45050;
        return rec.seat === 'p2' ? 0xff9070 : 0xffd070;
      };
      const timers = this._howlTimers;
      const after = (ms, fn) => timers.push(setTimeout(fn, ms));

      const baseY = 0.05 + 0.06; // sit a hair above the table like a board card

      // ── Deal phase: fly each card face-up into its void, staggered ──
      rec.steps.forEach((step, i) => {
        after(HOWL_DEAL_MS * i, () => {
          const card = {
            id: `${rec.howlId}-reveal-${i}`,
            cardId: step.cardId,
            name: step.name,
            imageUrl: step.imageUrl,
            x: rec.originX,
            z: rec.originZ,
            faceDown: false,
            // Dealt from the caster's spellbook. Orient like the caster's
            // cards so a p2 cast doesn't read as p1-owned on either client.
            rotated: rec.seat === 'p2',
          };
          const mesh = createCardMesh(card);
          // Cosmetic overlay, not board state: keep the reconciler from
          // reaping it as an untracked "ghost" card mesh when an
          // authoritative snapshot lands mid-reveal on the opponent.
          mesh.userData.cosmetic = true;
          mesh.position.y = baseY;
          sceneRoot.add(mesh);
          const aura = createCardAura(accentColor(step.accent));
          sceneRoot.add(aura.points);
          this._howlAuras.set(i, { mesh, aura });
          animateCardToCell(mesh, rec.originX, rec.originZ, step.x, step.z);
        });
      });

      // ── Resolve phase: after the reveal hold, route each card out ──
      const n = rec.steps.length;
      const resolveStart = HOWL_DEAL_MS * n + HOWL_HOLD_MS;
      rec.steps.forEach((step, i) => {
        after(resolveStart + HOWL_RESOLVE_MS * i, () => {
          const live = this._howlAuras.get(i);
          if (!live) return;
          this._howlAuras.delete(i);
          disposeCardAura(live.aura);
          if (step.classification === 'draw' || step.classification === 'reveal') {
            // Drawn cards and undrawn reveals return toward their spellbook.
            animateCardToPile(live.mesh, rec.originX, rec.originZ, sceneRoot);
          } else {
            // Banish: the normal banish flight, off toward the caster edge.
            const banishX = rec.originX * 1.25;
            const banishZ = rec.originZ * 1.25;
            animateCardToPile(live.mesh, banishX, banishZ, sceneRoot);
          }
        });
      });

      // Teardown: dispose anything still standing (e.g. the player tabbed
      // away mid-reveal), so the temp meshes/auras can never leak.
      after(howlDurationMs(n), () => this._clearHowlScene());
    } catch (err) {
      console.warn('[howl] reveal playback failed (cosmetic, ignored)', err);
      this._clearHowlScene();
    }
  };

  // Per-frame: breathe each reveal aura's pulse + fade-in and keep it
  // glued to its (animating) reveal mesh. No-op when no reveal is active.
  _syncHowlAuras = (dt) => {
    if (!this._howlAuras.size) return;
    for (const { mesh, aura } of this._howlAuras.values()) {
      updateCardAura(aura, dt, mesh);
    }
  };

  // Dispose every standing reveal mesh + aura and clear the timers. Safe
  // to call repeatedly; animateCardToPile already disposed meshes whose
  // flight finished, so only stragglers are torn down here.
  _clearHowlScene = () => {
    for (const t of this._howlTimers) clearTimeout(t);
    this._howlTimers = [];
    for (const { mesh, aura } of this._howlAuras.values()) {
      disposeCardAura(aura);
      if (mesh) {
        mesh.parent?.remove(mesh);
        mesh.geometry?.dispose();
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((m) => m?.dispose());
      }
    }
    this._howlAuras.clear();
  };

  // Card instance for the combat overlay: live mesh first, then the
  // piles. A card killed this very fight is already in its cemetery
  // but still needs to be shown (and its keywords read) by playback.
  _combatCardInstance = (id) => {
    if (!id) return null;
    const live = this._cardInstanceById(id);
    if (live) return live;
    for (const pile of (this.state.gameState?.piles || [])) {
      const card = (pile.cards || []).find((c) => c.id === id);
      if (card) return card;
    }
    return null;
  };

  _combatCardInfo = (id) => {
    const card = this._combatCardInstance(id);
    return card ? { name: card.name, imageUrl: card.imageUrl, foiling: card.foiling || null, isSite: !!card.isSite } : null;
  };

  // Stamp each raw resolution event with the display fields the CombatOverlay
  // reads. Killed cards are already swept off the board by the time this runs,
  // so identity is resolved against the piles too (_combatCardInstance). The
  // overlay must render a card that no longer exists anywhere else. Single
  // source of enrichment (the pure enrichCombatEvents) shared by the local
  // publishEvents hook AND the server-authoritative onAuthCombatEvents path.
  // `fight` carries { attacker:{instanceId}, defenders:[{instanceId}], target }.
  _enrichCombatEvents = (events, fight, opts = {}) => authCombat.enrichCombatEvents(events, fight, {
    cardInstance: (id) => this._combatCardInstance(id),
    isCaster: (id) => hasKeyword(this._combatCardInstance(id), 'spellcaster'),
    isMeshGone: (id) => !this.meshes.get(id),
    seatOf: (card) => this._seatOf(card),
    findAvatar: (n) => this._findAvatarForPlayer(n),
    gameOverWinner: this._gameOverWinner,
    deaths: opts.deaths || null,
  });

  // THE single playback entry point. Every resolved fight, every client,
  // every path (the resolving client's publishEvents, the broadcast
  // 'resolved' record, a reconnect re-reading it) lands here. Idempotency
  // is one rule: one playback per combatId. No arrival-order heuristics.
  // whether a fight is shown can never depend on which packet got there
  // first. If a scene for this fight is already up (assign / spectate /
  // locked), it morphs in place: same mounted panel, same portraits, the
  // beats land on the roster the player was just staring at.
  // `stageKey` identifies ONE strike window of a staged fight (both windows
  // of the same combatId play, each once); `window` names it for the panel.
  // The first window morphs the assignment panel (same panel key); the normal
  // window opens a fresh panel with the participants that still qualify.
  _playCombatScene = ({ combatId, stageKey, window, attackerId, attackerInfo, attackerDied, events, defenders, instant }) => {
    const list = events || [];
    // The animation is MANDATORY for any RESOLVED fight (a combatId is present),
    // even a zero-damage resolution; only stale reconnect replays are skipped.
    if (!authCombat.shouldStageResolvedFight({ combatId, events: list })) {
      if (combatId || list.length) console.warn('[combat] playback skipped. Stale', combatId);
      return false;
    }
    const key = combatId || `local-${Date.now()}`;
    const dedupeKey = stageKey || key;
    if (!this._playedFights) this._playedFights = new Set();
    if (this._playedFights.has(dedupeKey)) return false;
    this._playedFights.add(dedupeKey);
    const panelKey = window === 'normal' ? `${key}:normal` : key;
    console.log('[combat] playback', dedupeKey, `${list.length} event(s)`);
    this.setState((s) => {
      const carry = (s.combatScene?.panelKey ?? s.combatScene?.combatId) === panelKey ? s.combatScene : null;
      return {
        combatScene: {
          combatId: key,
          panelKey,
          stageKey: stageKey || null,
          window: window || null,
          mode: 'playback',
          power: carry?.power ?? null,
          defenders: defenders || carry?.defenders || null,
          liveAmounts: null,
          attacker: carry?.attacker || {
            id: attackerId || null,
            name: attackerInfo?.name || 'Attacker',
            imageUrl: attackerInfo?.imageUrl || null,
            foiling: attackerInfo?.foiling || null,
            isSite: !!attackerInfo?.isSite,
          },
          attackerDied: !!attackerDied,
          events: list,
          // Replay result mode: the overlay applies every hit and death at
          // once and holds, instead of playing the strike beats.
          instant: !!instant,
        },
      };
    });
    return true;
  };

  // Closing the scene (finished or skipped) releases any HUD life
  // countdown still held for the playback. A skip must not leave the
  // number frozen waiting for an impact frame that will never play.
  // It can also be the moment a deferred game-over presents.
  _closeCombatScene = () => {
    const stageKey = this.state.combatScene?.stageKey || null;
    this.setState({
      combatScene: null,
      hudLifeRelease: { p1: Date.now(), p2: Date.now() },
    }, () => {
      // The strike window is off stage: the presentation queue may display
      // the commits that landed behind it (effects, the next window).
      this._combatPresentation?.sceneDone?.(stageKey);
      this._afterCombatSceneClosed();
    });
  };

  _afterCombatSceneClosed = () => {
    // Our playback ended (or was skipped). The resolved record's
    // remaining linger window only existed for clients still receiving
    // it, and the broadcast went out long ago. Fast-forward the clear
    // so the casualty procession isn't held behind a timer for a show
    // that's over. (_clearPendingCombat drains + presents in its own
    // callback; the direct calls below cover the no-linger paths.)
    if (this._amHost() && this._combatClearTimer
        && this.state.gameState?.pendingCombat?.phase === 'resolved') {
      this._clearPendingCombat();
    }
    // Stage clear: casualties fly to the cemetery (staggered), then a
    // deferred game-over may present.
    this._drainPileDepartures();
    this._maybePresentPendingGameOver();
  };

  // HUD life countdowns hold while a fight is in flight; the combat
  // overlay releases a seat's countdown on the exact impact frame.
  _releaseLifeTick = (player) => {
    this.setState((s) => ({ hudLifeRelease: { ...s.hudLifeRelease, [player]: Date.now() } }));
  };

  // Re-drive the combat UI from the authoritative mirror. The server is the
  // sole resolver; the combat prompt renders from the mirror
  // (applyCombatFromMirror in the diff fold), so every caller (reconnect
  // restore, reconcile tick) routes there. Called on reconnect.
  _rehydrateCombat = () => {
    authCombat.applyCombatFromMirror(this, this._authMirror?.getState?.());
  };

  // Open / update the assignment scene from the combat record. The ONE
  // builder both seats use. Mode derives from the record: the acting
  // seat assigns ('locked' once its decision is in the record), the
  // other seat spectates the same panel, fed live by
  // combat:assign_preview broadcasts. Same combatId + same mode →
  // no-op, so record re-broadcasts never reset in-progress local
  // amounts or received live amounts. On a reconnect mid-locked, the
  // chosen split is recovered from the decision payload itself.
  // Read-only board query: does an eligible tank (Sir Gawain) cover the
  // combat damage assigned to this defender? Reuses findDamageReplacements so
  // the {adjacent, allied, exclude_self} scope filter on the tank's
  // would_take_damage entry decides eligibility, and only a redirect_damage
  // entry counts. Returns the tank's instanceId, or null. Shared by the combat
  // adapter (resolution) and _syncAssignScene (the assign-step indicator).
  _tankRedirectFor = (defenderId, attackerId) => {
    const defender = this._cardInstanceById(defenderId);
    if (!defender) return null;
    const attacker = attackerId ? this._cardInstanceById(attackerId) : null;
    const matches = findDamageReplacements(
      defender, attacker, this.meshes, this._getFullCard,
      { cellAt: this._cellAtWorld },
    );
    for (const { watcher, entry } of matches) {
      if (watcher.id === defenderId) continue;
      if ((entry.effect || []).some((eff) => eff.op === OP.REDIRECT_DAMAGE)) return watcher.id;
    }
    return null;
  };

  _syncAssignScene = (pc, localActs) => {
    const mode = localActs ? (pc.decision ? 'locked' : 'assign') : 'spectate';
    const cur = this.state.combatScene;
    if (cur?.combatId === pc.combatId && cur.mode === mode) return;
    const decisionAmounts = pc.decision?.kind === 'assign'
      ? Object.fromEntries(pc.decision.payload?.assignment || []) : null;
    const liveAmounts = mode === 'assign'
      ? null
      : decisionAmounts || (cur?.combatId === pc.combatId ? cur.liveAmounts : null) || {};
    const attackerCard = this._cardInstanceById(pc.attacker?.instanceId);
    this.setState({
      cardActionMenu: null,
      combatScene: {
        combatId: pc.combatId,
        mode,
        power: this._readCombatStats(pc.attacker?.instanceId).atk,
        defenders: (pc.assignDefenders || []).map((id) => {
          const c = this._cardInstanceById(id);
          // A defending avatar has no defence to beat: its share of the power
          // is life loss on its controller, so it shows no "Defense N" and no
          // LETHAL forecast (null def also tells autoAssign it can't be killed).
          const isAvatar = c?.type === 'Avatar';
          // …and no tank caption either: applyTankRedirect only rewrites
          // dmgToDefenders, so an avatar's share reaches its controller's life
          // whatever Sir Gawain is doing. Claiming otherwise would describe a
          // resolution that never happens.
          const tankId = isAvatar ? null : this._tankRedirectFor(id, pc.attacker?.instanceId);
          const tank = tankId ? this._cardInstanceById(tankId) : null;
          return {
            id,
            name: c?.name || 'Unit',
            imageUrl: c?.imageUrl || null,
            foiling: c?.foiling || null,
            isSite: !!c?.isSite,
            isAvatar,
            def: isAvatar ? null : this._readCombatStats(id).def,
            // A tank (Sir Gawain) absorbs this ally's assigned hit. The assign
            // UI shows the tank's card + a "absorbed by" caption so the player
            // sees where the damage actually lands.
            redirectTo: tank ? { id: tankId, name: tank.name, imageUrl: tank.imageUrl || null, foiling: tank.foiling || null, isSite: !!tank.isSite } : null,
          };
        }),
        liveAmounts,
        attacker: {
          id: pc.attacker?.instanceId || null,
          name: attackerCard?.name || 'Attacker',
          imageUrl: attackerCard?.imageUrl || null,
          foiling: attackerCard?.foiling || null,
          isSite: !!attackerCard?.isSite,
        },
        attackerDied: false,
        events: null,
      },
    });
  };

  // Acting seat streams its in-progress allocation. Ephemeral
  // presentation state on the P2P channel. Losing one is harmless,
  // the durable decision at Strike is the only truth.
  _broadcastAssignPreview = (amounts) => {
    const rec = this.state.gameState?.pendingCombat;
    if (!rec?.combatId) return;
    try { this.sync?.emit?.('combat:assign_preview', { combatId: rec.combatId, amounts }); } catch { /* ignore */ }
  };

  _refreshCombatPrompt = () => {
    if (this._activeCombatPrompt && this._activeCombatPrompt.kind !== 'assign') {
      this._renderCombatMenu(this._activeCombatPrompt);
    }
  };

  // Bounds for anchoring a combat menu to a card mesh, with a safe fallback
  // when the mesh isn't projectable (off-screen / not yet placed).
  _combatMenuBounds = (anchorId) => {
    const mesh = anchorId && this.meshes.get(anchorId);
    return (mesh && this._projectCardRightEdge(mesh))
      || { cardLeft: 0, cardRight: 0, cardTop: 0, cardBottom: 0 };
  };

  _renderCombatMenu = (prompt) => {
    const anchorId = this._combatPromptAnchorId(prompt);
    // Defend / intercept need the player to click + drag units ON the board,
    // so the panel must NOT sit over the contested cell (its eligible
    // defenders are the adjacent cells. Anchoring the panel there would
    // cover the very units to be picked). Pin it to a fixed screen edge and
    // drop the blocking backdrop. Other prompts anchor to the card as usual.
    const boardSelect = prompt.kind === 'defend' || prompt.kind === 'intercept';
    // The defend / intercept prompt carries a side preview to its
    // right: attacker card art → medieval arrow (crossed swords) →
    // attacked card art, so the player sees WHO is attacking WHAT
    // without scanning the board. Intercept has no attack target yet
    // (the enemy is mid-move), so it shows the mover alone.
    let sideContent = null;
    if (boardSelect) {
      const ctx = this._combatActorCtx();
      const attacker = this._combatCardInfo(ctx.attackerId);
      const target = prompt.kind === 'defend' && ctx.target?.instanceId
        ? this._combatCardInfo(ctx.target.instanceId) : null;
      if (attacker) {
        sideContent = () => <CombatMatchupPreview attacker={attacker} target={target} />;
      }
    }
    this.setState({
      cardActionMenu: {
        cardId: anchorId, cardName: '', cardBounds: this._combatMenuBounds(anchorId),
        items: this._combatPromptItems(prompt), combatModal: true,
        noBackdrop: boardSelect,
        fixedPlacement: boardSelect ? 'top' : undefined,
        sideContent,
      },
      hoverStatusOverlay: null,
    });
  };

  _combatPromptAnchorId = (prompt) => {
    if (prompt.kind === 'defend') return prompt.target?.instanceId || prompt.attackerId;
    if (prompt.kind === 'intercept') return (prompt.candidates && prompt.candidates[0]) || prompt.attackerId;
    if (prompt.kind === 'move_or_attack') return prompt.attackerId;
    if (prompt.kind === 'waiting') return prompt.anchorId;
    return prompt.attackerId;
  };

  _combatPromptItems = (prompt) => {
    switch (prompt.kind) {
      case 'move_or_attack': return this._combatMoveOrAttackItems(prompt);
      case 'defend': return this._combatSelectItems(prompt);
      case 'intercept': return this._combatSelectItems(prompt);
      case 'waiting': return [{ id: 'waiting', label: prompt.label || t('Waiting...'), disabled: true, onClick: () => {} }];
      default: return [];
    }
  };

  // After a qualifying move, the attacker chooses what the move meant. The
  // move already committed (mesh moved + tapped); Cancel reverts both.
  _openMoveOrAttackPrompt = (card, cell) => {
    this._openCombatPrompt({ kind: 'move_or_attack', attackerId: card.id, cell });
  };

  _combatMoveOrAttackItems = (prompt) => {
    const card = this._cardInstanceById(prompt.attackerId);
    return [
      {
        id: 'attack', label: t('Attack'), disabled: false,
        onClick: () => { this._combatMoveRevert = null; this._openCombatTargetPrompt(card, effectiveMovementBonus(card), true); },
      },
      {
        id: 'move', label: t('Move only'), disabled: false,
        onClick: () => { this._combatMoveRevert = null; this._maybeOfferInterceptAfterMove(card, prompt.cell); },
      },
      {
        id: 'cancel', label: t('Cancel'), disabled: false,
        onClick: () => this._revertCombatMove(),
      },
    ];
  };

  // Undo the move that opened the move-or-attack prompt: snap the unit back
  // to its origin cell/region and untap if the move auto-tapped it.
  _revertCombatMove = () => {
    const r = this._combatMoveRevert;
    this._combatMoveRevert = null;
    this._closeCombatPrompt();
    if (!r) return;
    const card = this._cardInstanceById(r.cardId);
    const mesh = card && this.meshes.get(r.cardId);
    if (!card || !mesh) return;
    mesh.position.x = r.originX; mesh.position.z = r.originZ;
    card.x = r.originX; card.z = r.originZ;
    const body = mesh.userData?.body;
    if (body) moveKinematicBody(body, r.originX, mesh.position.y, r.originZ);
    const restoreSub = (r.fromRegion === 'surface' || r.fromRegion === 'void') ? null : r.fromRegion;
    if (card.subsurface !== restoreSub) {
      card.subsurface = restoreSub;
      this._broadcastCardSubsurface?.(card.id, card.subsurface);
    }
    if (r.wasTappedByMove && card.tapped) this._applyTap(card, mesh, false, { source: 'move-revert', log: false });
  };

  // Unified actor context. Reads the server-driven prompt stored in
  // _activeCombatPrompt.
  _combatActorCtx = () => {
    const p = this._activeCombatPrompt;
    return {
      combatId: p?.combatId,
      attackerId: p?.attackerId,
      target: p?.target ?? null,
    };
  };

  // Unified defend / intercept selection. Eligible units are HIGHLIGHTED on
  // the board (teal card-ring halos via getEquipTargetIds → syncTargetHaloMeshes); the
  // player picks them directly on the board, NOT from a button list:
  //   • on-cell eligible unit → single left-click to toggle-select (it gains
  //     the bold gold halo);
  //   • off-cell eligible unit (defend only) → drag onto the contested square.
  // This compact prompt just shows a hint + the running count + a confirm
  // button. The actual tap/move/resolve runs on the host (locally if host,
  // via the durable record if guest). Intercept has no movement, so its
  // candidates are always on-cell.
  _combatSelectItems = (prompt) => {
    const kind = prompt.kind === 'intercept' ? 'intercept' : 'defend';
    const ctx = this._combatActorCtx();

    // Compute the eligible set + contested cell for this phase.
    let eligible = [];
    let contestedCell = null;
    if (kind === 'defend') {
      const attacker = this._cardInstanceById(ctx.attackerId);
      const targetCard = ctx.target?.instanceId ? this._cardInstanceById(ctx.target.instanceId) : null;
      const defMesh = targetCard && this.meshes.get(targetCard.id);
      contestedCell = defMesh ? this._cellAtWorld(defMesh.position.x, defMesh.position.z) : null;
      eligible = (attacker && targetCard && contestedCell)
        ? this._collectEligibleDefenders(attacker, targetCard, contestedCell) : [];
    } else {
      const ids = (prompt.candidates && prompt.candidates.length)
        ? prompt.candidates : (this._activeCombatPrompt?.candidates || []);
      const attMesh = ctx.attackerId && this.meshes.get(ctx.attackerId);
      contestedCell = attMesh ? this._cellAtWorld(attMesh.position.x, attMesh.position.z) : null;
      // Interceptors are always already on the mover's cell (no movement).
      eligible = ids.map((id) => ({ card: this._cardInstanceById(id), mustMove: false })).filter((e) => e.card);
    }

    // Arm / refresh the selection state. `chosen` + `draggedOrigins` persist
    // across re-renders within the same fight/phase; eligibility is recomputed.
    if (!this._combatSelect || this._combatSelect.combatId !== ctx.combatId || this._combatSelect.kind !== kind) {
      this._combatSelect = {
        kind, combatId: ctx.combatId, contestedCell,
        onCellIds: new Set(), moveIds: new Set(), eligibleIds: new Set(),
        chosen: new Set(), draggedOrigins: new Map(),
        // Codex Attack 3.b: when another defender joins, the defending
        // player chooses whether the original UNIT target stays in the
        // fight. It starts in (gold ring); clicking it withdraws it.
        // allowed only while at least one other defender is chosen.
        targetId: null, targetStays: true,
      };
    } else {
      this._combatSelect.contestedCell = contestedCell;
    }
    const sel = this._combatSelect;
    sel.onCellIds = new Set(eligible.filter((e) => !e.mustMove).map((e) => e.card.id));
    sel.moveIds = new Set(eligible.filter((e) => e.mustMove).map((e) => e.card.id));
    // Chosen units stay in the halo set even though the preview tap has
    // made them ineligible for a FRESH selection (canDefend excludes
    // tapped units). Without this their halo vanishes the moment the
    // declaration tap lands.
    sel.eligibleIds = new Set([...sel.onCellIds, ...sel.moveIds, ...sel.chosen]);
    if (isDevBuild()) {
      console.debug('[combat] select armed', {
        kind,
        contestedCell,
        onCell: [...sel.onCellIds],
        draggable: [...sel.moveIds],
        chosen: [...sel.chosen],
      });
    }
    if (kind === 'defend') {
      const t = ctx.target;
      sel.targetId = (t && t.kind === 'unit' && t.instanceId) ? t.instanceId : null;
      if (sel.targetId) sel.eligibleIds.add(sel.targetId); // render its halo
      // Codex: a defended unit is protected by default. The joining
      // defender steps in front and the original is pulled out of the
      // fight, so the attacker can't assign damage to it. The defending
      // player may click it to keep it in (sets `targetStaysTouched`).
      // With no other defender the target IS the fight, so it must stay.
      if (sel.chosen.size === 0) {
        sel.targetStays = true;
        sel.targetStaysTouched = false;
      } else if (!sel.targetStaysTouched) {
        sel.targetStays = false;
      }
    }

    const verb = kind === 'intercept' ? t('Intercept') : t('Defend');
    // Heading announces the required action loudly (medieval display
    // face + radial vignette); the caption below carries the how-to.
    // Instructions never belong in a button.
    const title = kind === 'intercept' ? t('Choose Interceptors') : t('Choose Defenders');
    const hint = (kind === 'defend' && sel.moveIds.size)
      ? t('Click a highlighted ally to defend; it moves to the square')
      : t('Click highlighted units to select');
    const n = sel.chosen.size;
    const items = [];
    items.push({ id: 'title', render: () => <div className="combat-prompt-title">{title}</div> });
    if (sel.eligibleIds.size || n) {
      items.push({ id: 'hint', render: () => <div className="combat-prompt-hint">{hint}</div> });
    }
    // Confirm. Short. Nothing selected = decline the ability.
    items.push({
      id: 'confirm',
      label: n ? t('{verb} ({n})', { verb, n }) : t('Decline'),
      disabled: false,
      onClick: () => this._combatConfirmSelect(),
    });
    if (n) {
      items.push({ id: 'clear', label: t('Clear'), disabled: false, onClick: () => this._combatClearSelect() });
    }
    return items;
  };

  // Confirm the current defend / intercept selection (empty = decline).
  // The selection is consumed by the submit. Null it so the teardown
  // path can tell a SUBMITTED selection (taps are paid for real) from an
  // abandoned one (preview taps must be refunded).
  _combatConfirmSelect = () => {
    const sel = this._combatSelect;
    if (!sel) return;
    const ids = [...sel.chosen];
    const removeTarget = !sel.targetStays && ids.length > 0;
    const kind = sel.kind;
    // Decision line, attributed to the acting player. Both clients see
    // the identical broadcast text, so the combat audit trail reads the
    // same on both screens.
    if (ids.length) {
      const names = ids.map((id) => this._cardInstanceById(id)?.name || msg('a unit'));
      // Joined pairwise so the separator itself is a translatable key.
      const units = names.reduce((acc, name) => msg('{a}, {b}', { a: acc, b: name }));
      if (kind === 'intercept') this.logAction('Intercepts with {units}', { units });
      else this.logAction('Defends with {units}', { units });
      if (removeTarget) {
        const t = sel.targetId ? this._cardInstanceById(sel.targetId) : null;
        this.logAction('{card} withdraws from the fight', { card: t?.name || msg('The original target') });
      }
    } else if (kind === 'intercept') {
      this.logAction('Declines to intercept');
    } else {
      this.logAction('Declines to defend');
    }
    // Consume the selection BEFORE submitting. On the host seat the
    // submit resolves the entire fight synchronously, and the engine's
    // closePrompt fires _closeCombatUI mid-resolve. If the selection
    // were still armed, the teardown refund would untap the confirmed
    // defenders and snap dragged ones back to origin right after the
    // damage applied. Confirmed picks are paid for; never refundable.
    this._combatSelect = null;
    if (kind === 'intercept') this._combatSubmitInterceptors(ids);
    else this._combatSubmitDefenders(ids, removeTarget);
  };

  // Halo display set: chosen defenders + the original target while it stays
  // in the fight (gold ring on all of them).
  _combatSelectChosenDisplay = () => {
    const sel = this._combatSelect;
    if (!sel) return null;
    const out = new Set(sel.chosen);
    if (sel.kind === 'defend' && sel.targetId && sel.targetStays) out.add(sel.targetId);
    return out;
  };

  // Clear the selection: refund every preview tap and snap any dragged-in
  // defenders back to origin.
  _combatClearSelect = () => {
    const sel = this._combatSelect;
    if (!sel) return;
    for (const id of sel.chosen) this._setCombatSelectionTap(id, false);
    for (const [id, origin] of sel.draggedOrigins) {
      this._snapCardTo(id, origin);
      this._emitPosition(id, origin.x, origin.z);
    }
    sel.draggedOrigins.clear();
    sel.chosen.clear();
    this._refreshCombatPrompt();
  };

  // Toggle one unit in the current defend / intercept selection. Off-cell
  // defenders are added via drag (handleMouseUp), so this only ever toggles
  // an on-cell eligible unit, or deselects an already-chosen one (snapping a
  // dragged-in defender back to where it came from). The original target
  // toggles its stay/withdraw choice instead (codex Attack 3.b). Withdraw
  // is only legal while at least one other defender is chosen.
  // Preview tap for a defend / intercept declaration. Codex: "it may tap
  // to defend" / "tap to intercept." The tap is the cost, paid when the
  // unit is declared (one at a time), not at fight resolution. Deselecting
  // refunds it: an undone declaration never paid its cost. Only eligible
  // (= untapped) units can enter `chosen`, so every refund untap is ours.
  _setCombatSelectionTap = (cardId, tapped) => {
    const card = this._cardInstanceById(cardId);
    const mesh = this.meshes.get(cardId);
    if (!card || !mesh || !!card.tapped === !!tapped) return;
    // VISUAL-only preview: the defend/intercept tap is a rules COST the
    // SERVER pays when it commits the defenders (combatEngine
    // commitDefenders/pickInterceptors). Broadcasting it here raced the
    // defend intent. The server saw an already-tapped unit and dropped
    // every chosen defender, so the attack struck the site anyway.
    this._applyTap(card, mesh, tapped, { source: 'defend', log: false, broadcast: false });
  };

  _toggleCombatSelection = (cardId) => {
    const sel = this._combatSelect;
    if (!sel) return;
    if (sel.kind === 'defend' && cardId === sel.targetId) {
      if (sel.targetStays && sel.chosen.size === 0) return; // sole defender. Must stay
      sel.targetStays = !sel.targetStays;
      sel.targetStaysTouched = true; // explicit choice survives re-arm defaults
    } else if (sel.chosen.has(cardId)) {
      sel.chosen.delete(cardId);
      this._setCombatSelectionTap(cardId, false);
      const origin = sel.draggedOrigins.get(cardId);
      if (origin) {
        this._snapCardTo(cardId, origin);
        this._emitPosition(cardId, origin.x, origin.z);
        sel.draggedOrigins.delete(cardId);
      }
      if (sel.chosen.size === 0) sel.targetStays = true; // withdraw needs another defender
    } else if (sel.onCellIds.has(cardId)) {
      sel.chosen.add(cardId);
      this._setCombatSelectionTap(cardId, true);
    } else {
      return;
    }
    this._refreshCombatPrompt();
  };

  _snapCardTo = (id, pos) => {
    const mesh = id && this.meshes.get(id);
    if (!mesh || !pos) return;
    mesh.position.x = pos.x; mesh.position.z = pos.z;
    if (mesh.userData?.body) moveKinematicBody(mesh.userData.body, pos.x, mesh.position.y, pos.z);
  };

  // Open the attack-target picker MODAL above the unit's square. Choosing
  // WHICH card to attack belongs in this modal (card images, hover preview);
  // the action menu only ever picks the action (Attack vs Move only).
  _openCombatAttackModal = (card, cell, recs, afterMove) => {
    // Single funnel for every attack path (menu, move-and-attack, ranged shot,
    // avatar abilities). A manual session never opens it. The server refuses
    // combat intents too, so a leaked call would resolve nothing anyway.
    if (!this._automationsEnabled()) return;
    this._activeCombatPrompt = null;
    this._combatSelect = null;
    const grid = getGameGrid(this.state.spawnConfig);
    if (!grid || !this.scene?.camera || !this.canvasRef.current) return;
    const sample = sampleCellGeometry(grid, cell.col, cell.row);
    if (!sample) return;
    const camera = this.scene.camera;
    const rect = this.canvasRef.current.getBoundingClientRect();
    const v = this._hugVec;
    const Y = 0.2;
    const corners = [[sample.tl.x, sample.tl.z], [sample.tr.x, sample.tr.z], [sample.br.x, sample.br.z], [sample.bl.x, sample.bl.z]];
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const [wx, wz] of corners) {
      v.set(wx, Y, wz).project(camera);
      const sx = rect.left + ((v.x + 1) / 2) * rect.width;
      const sy = rect.top + ((-v.y + 1) / 2) * rect.height;
      if (sx < minX) minX = sx;
      if (sx > maxX) maxX = sx;
      if (sy < minY) minY = sy;
      if (sy > maxY) maxY = sy;
    }
    const getFullCard = this._getFullCard;
    const targets = recs.map((rec) => ({
      ...rec.target,
      fullCard: getFullCard(rec.target.card?.cardId),
      // displayStatusKeys, not getEffectiveStatuses: the board surfaces
      // add the equipment-derived Lance pill, and the prompt has to match.
      effectiveStatuses: equipActions.displayStatusKeys(rec.target.card),
      statChips: equipActions.displayStatChips(rec.target.card),
      requiresRegionTransition: rec.requiresRegionTransition || null,
      _rec: rec,
    }));
    this.setState({
      cardActionMenu: null,
      attackTargetModal: {
        attackerId: card.id, attackerName: card.name || '',
        cellRect: { left: minX, right: maxX, top: minY, bottom: maxY },
        targets, _engine: true, _afterMove: !!afterMove,
      },
    });
  };

  // Move-and-attack where the player declined the attack: the opponent's
  // units already on the destination cell may intercept.
  _maybeOfferInterceptAfterMove = (mover, cell) => {
    this._closeCombatPrompt();
    // Intercept candidacy is positional (client physics knowledge), so the
    // candidates are computed locally and carried in the intent; the server
    // opens the intercept prompt from them.
    const ids = getInterceptCandidates(mover, cell, { meshes: this.meshes, cellAt: this._cellAtWorld }).map((c) => c.id);
    if (ids.length) this._sendMatchIntent(combatIntents.offerInterceptIntent(mover.id, cell, ids));
  };

  _declareAttackFromRec = (attackerId, rec) => {
    const attacker = this._cardInstanceById(attackerId);
    if (!attacker) { this._closeCombatPrompt(); return; }
    const tc = rec.target.card;
    // Declaration line, attributed to the declaring player (this method only
    // runs on the initiator). Resolution mechanics (damage, deaths, Lance,
    // Stealth) are logged by the server-authored, per-seat-redacted action log.
    const targetName = tc.name || (tc.isSite ? msg('a site') : msg('a unit'));
    const fightValues = { attacker: attacker.name || msg('Unit'), target: targetName };
    if (rec.shot) this.logAction('{attacker} shoots {target}', fightValues);
    else this.logAction('{attacker} attacks {target}', fightValues);
    if (rec.requiresRegionTransition && !attacker.subsurface) {
      attacker.subsurface = rec.requiresRegionTransition;
      this._broadcastCardSubsurface(attacker.id, attacker.subsurface);
    }
    // Every descriptor carries the target's controller seat. Site/avatar
    // need it for life-loss routing; for units it backstops the defend
    // window's acting-seat derivation when the instance can't be
    // resolved at prompt time (e.g. removed by an on_attack trigger). The
    // ownerless-site rule (a Rubble "is controlled by no one") lives in the
    // pure engine helper, which nulls the seat so resolveFight suppresses
    // life loss while the attack stays legal (triggers still fire).
    const descriptor = buildAttackTargetDescriptor(tc, this._seatOf);
    this._activeCombatPrompt = null;
    this.setState({ cardActionMenu: null });
    // The server is the sole resolver. Send the intent and let the diff
    // (state.combat + tap + deaths) + match:combat events drive the UI.
    this._sendMatchIntent(rec.shot
      ? combatIntents.shotIntent(attacker.id, descriptor)
      : combatIntents.declareIntent(attacker.id, descriptor, rec.fromCell));
  };

  // Compute legal move-and-attack targets and open the choose-target
  // prompt. The drag already performed the move, so the attack is from
  // the unit's current cell (stepBudget 0); transitionSteps drives
  // same-cell burrow/submerge-to-strike availability.
  _openCombatTargetPrompt = (card, transitionSteps = 0, afterMove = false) => {
    const mesh = this.meshes.get(card.id);
    if (!mesh) return;
    const cell = this._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cell) return;
    const recs = this._combatTargetsFor(card, cell, transitionSteps);
    if (!recs.length) {
      // No legal attack from here. If this was a move-and-attack, the
      // unit still moved, so the opponent may intercept.
      if (afterMove) this._maybeOfferInterceptAfterMove(card, cell);
      else toast.message(t('No legal targets in range.'));
      return;
    }
    // Target selection happens in the modal above the square, not the menu.
    this._openCombatAttackModal(card, cell, recs, afterMove);
  };

  // Ranged Shoot. Separate activated ability (codex Ranged: "Tap → Shoot a
  // projectile that stops after 1 step. Strike the hit unit."). Candidates
  // come from the cardinal projectile lines; the pick modal reuses the
  // attack-target modal above the shooter's own cell. No defend window.
  _openShootTargetPrompt = (card) => {
    const mesh = this.meshes.get(card.id);
    if (!mesh) return;
    const cell = this._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cell) return;
    const grid = getGameGrid(this.state.spawnConfig);
    if (!grid) return;
    const { colPositions, rowPositions } = computeGridPositions(grid);
    const totalCols = colPositions.length - 1;
    const totalRows = rowPositions.length - 1;
    const range = getRangedValue(card);

    // Enemies at the shooter's OWN location are hit at t=0. There is
    // no direction to choose, so the target modal opens directly
    // (multiple co-located enemies: the controller picks which is hit).
    const startHits = getProjectileHits(card, cell, {
      meshes: this.meshes, cellAt: this._cellAtWorld, totalCols, totalRows,
    }).filter((h) => h.distance === 0);
    if (startHits.length) {
      const recs = startHits.map((h) => ({
        target: { kind: h.card.type === 'Avatar' ? 'avatar' : 'card', card: h.card },
        fromCell: null, projectile: true, shot: true,
      }));
      this._openCombatAttackModal(card, cell, recs, false);
      return;
    }

    // Direction pick. Same trail-overlay UX as Flamecaller / Avatar of
    // Fire, through the SAME engine walker (projectilePaths); the only
    // difference is maxSteps = the unit's Ranged value. Stealth and
    // off-region units can't be hit and don't block, so isHittable
    // rejects them and the walk continues past.
    const unitsByCell = new Map();
    for (const [, m] of this.meshes) {
      const c = m?.userData?.cardInstance;
      if (!c || c.id === card.id) continue;
      if (c.isSite || !countsAsUnit(c)) continue;
      const uc = this._cellAtWorld(m.position.x, m.position.z);
      if (!uc) continue;
      const key = `${uc.col},${uc.row}`;
      const list = unitsByCell.get(key) || [];
      list.push(c);
      unitsByCell.set(key, list);
    }
    const region = card.subsurface || null;
    const isHittable = (c) => (c.subsurface || null) === region && !hasActiveStealth(c);
    const paths = projectilePaths({
      startCell: cell, totalCols, totalRows, unitsByCell, isHittable,
      maxSteps: range,
    });
    if (!paths.length) {
      toast.message(t('No unit in the line of fire.'));
      return;
    }
    const validCells = new Set();
    for (const path of paths) {
      for (const c of path.pathCells) validCells.add(`${c.col},${c.row}`);
    }
    const controller = card.rotated ? 'p2' : 'p1';
    this._startCellPick(card, controller, OP.RANGED_SHOT, validCells, {
      description: range > 1 ? t('Shoot a projectile (Ranged {n})', { n: range }) : t('Shoot a projectile'),
      extra: { projectilePaths: paths },
    });
  };

  // Ranged Shoot resolver. The picked cell maps back to its direction's
  // impact cell (the player can click anywhere along the trail). The
  // unit(s) at the impact decide the target: exactly one declares the
  // shot immediately; several (codex: "the projectile's controller
  // chooses which unit the projectile hits") open the target modal
  // scoped to that cell.
  _resolveRangedShot = (shooter, impactCell) => {
    if (!impactCell) return;
    const units = hittableUnitsAt(shooter, impactCell.col, impactCell.row, this.meshes, this._cellAtWorld);
    if (!units.length) {
      this.logAction('{card}: projectile found no target (fizzles)', { card: shooter.name || msg('Shooter') });
      return;
    }
    const recs = units.map((u) => ({
      target: { kind: u.type === 'Avatar' ? 'avatar' : 'card', card: u },
      fromCell: null, projectile: true, shot: true,
    }));
    if (recs.length === 1) {
      this._declareAttackFromRec(shooter.id, recs[0]);
      return;
    }
    const mesh = this.meshes.get(shooter.id);
    const cell = mesh ? this._cellAtWorld(mesh.position.x, mesh.position.z) : impactCell;
    this._openCombatAttackModal(shooter, cell || impactCell, recs, false);
  };

  _combatTargetsFor = (card, fromCell, transitionSteps = 0) => {
    const grid = getGameGrid(this.state.spawnConfig);
    if (!grid) return [];
    const { colPositions, rowPositions } = computeGridPositions(grid);
    const totalCols = colPositions.length - 1;
    const totalRows = rowPositions.length - 1;
    const region = currentRegion(card, fromCell, this.meshes, this._cellAtWorld, this._getFullCard);
    const canTransitionTo = this._computeAttackTransitions(card, transitionSteps);
    return getMoveAndAttackTargets(card, this._getFullCard(card.cardId), {
      col: fromCell.col, row: fromCell.row, region,
    }, {
      meshes: this.meshes, cellAt: this._cellAtWorld, totalCols, totalRows,
      getFullCard: this._getFullCard, stepBudget: 0, canTransitionTo,
      occupiedCells: unitFootprintCells(card, fromCell),
    });
  };

  // Inbound combat messages. Combat is server-authoritative (declare / shot /
  // defend / intercept / assign are intents resolved server-side and folded
  // back through the authoritative mirror), so no peer combat relay remains.
  // combat:assign_preview survives. The spectate seat still mirrors the
  // acting seat's live damage split.
  _registerCombatHandlers = () => {
    this.sync.registerHandlers({
      'combat:assign_preview': (msg) => {
        const cs = this.state.combatScene;
        if (cs?.mode !== 'spectate' || cs.combatId !== msg.combatId) return;
        this.setState({ combatScene: { ...cs, liveAmounts: msg.amounts || {} } });
      },
      'game:over': (msg) => this._onRemoteGameOver(msg),
    });
  };

  // Player-life routing lives on `this.playerResources` (engine-side
  // class). Callers use `this.playerResources.loseLife(...)` for
  // generic life loss and `this.playerResources.dealAvatarDamage(...)`
  // for damage that can be a death blow. Death's Door immunity is
  // tracked on the class and cleared by GameBoard's end-of-turn step.

  _declareGameOver = (winner, reason) => {
    if (this._gameOverWinner) return;
    this._gameOverWinner = winner;
    // The killing damage resolves on ONE client (host for combat, the
    // acting client for spells). Tell the other one, or the loser
    // never enters the result flow and server settlement waits forever
    // on a report that never comes.
    try { this.sync?.emit?.('game:over', { winner, reason }); } catch { /* sends must not throw */ }
    this._presentGameOver(winner, reason);
  };

  // The other client decided the game (its damage resolution landed the
  // death blow). Adopt the verdict and run the same deferred
  // presentation so our own combat playback finishes first. log:false.
  // the declaring client's GAME OVER lines arrive via the logbook
  // mirror; logging here too would double them on both screens.
  _onRemoteGameOver = (data) => {
    if (!data?.winner || this._gameOverWinner) return;
    this._gameOverWinner = data.winner;
    this._presentGameOver(data.winner, data.reason || 'death blow', { log: false });
  };

  // A death blow lands mid-resolution, BEFORE the combat playback has
  // staged the hit. Showing the result modal instantly would cover the
  // fight on the resolving client and race ahead of the playback on the
  // other. Presentation waits until the stage is clear: no combat scene
  // up, no in-flight (non-resolved) combat record. Rechecked from every
  // teardown path that can clear the stage.
  _presentGameOver = (winner, reason, opts = {}) => {
    this._pendingGameOverPresentation = { winner, reason, log: opts.log !== false };
    this._maybePresentPendingGameOver();
  };

  _maybePresentPendingGameOver = () => {
    const g = this._pendingGameOverPresentation;
    if (!g) return;
    if (this.state.combatScene) return;
    const pc = this.state.gameState?.pendingCombat;
    if (pc && pc.phase !== 'resolved') return;
    this._pendingGameOverPresentation = null;
    this._showGameOverUI(g.winner, g.reason, { log: g.log });
  };

  _showGameOverUI = (winner, reason, { log = true } = {}) => {
    // Spectators surface the standalone end-of-match overlay instead
    // of the reward modal. Bail before any propose-phase state is
    // staged so we never try to claim rewards on their behalf.
    if (this.props.isSpectating) {
      if (log) this.logSystem('GAME OVER. ({reason})', { reason });
      this.setState({ spectatorMatchEnded: true });
      return;
    }
    if (log && (winner === 'p1' || winner === 'p2')) {
      const winLabel = this._displayNameForPlayer(winner);
      const loseLabel = this._displayNameForPlayer(winner === 'p1' ? 'p2' : 'p1');
      this.logSystem('GAME OVER. {winner} wins. ({reason})', { winner: winLabel, reason });
      this.logSystem('{name} suffered a death blow.', { name: loseLabel });
    } else if (log) {
      // No seat won (a draw, or a verdict we don't recognise). Don't hand a
      // victory to whoever happens not to be 'p1'.
      this.logSystem('GAME OVER. ({reason})', { reason });
    }
    // v1.59. Open the existing ArenaMatchResult modal with the
    // engine-determined winner pre-set so the player doesn't have to
    // click "Who won?" themselves. autoMatchWinner is local-relative:
    // 'me' if the local player won, 'opponent' if they lost, and null
    // for a draw / unknown winner, which leaves the manual picker up
    // rather than auto-filing a loss for both seats (QA#48). The modal
    // skips the propose phase for arena (auto-proposes) or jumps to
    // the rewards view directly for sandbox / solo / local-PvP.
    const localPlayer = this.state.isHost ? 'p1' : 'p2';
    const autoMatchWinner = authCombat.autoWinnerForLocal(winner, localPlayer);
    // Close any open combat UI so the player can't keep clicking once
    // the match is over.
    this.setState({
      attackTargetModal: null,
      autoMatchWinner,
      showMatchResult: true,
    });
  };

  // Local-click entry: notify the opponent that the match is being
  // ended, then run the resolution flow locally. Split from
  // `_endMatchManually` so the inbound `match:end-request` handler
  // can reuse the resolution logic without re-broadcasting and
  // looping forever.
  _clickEndMatch = () => {
    // Same gate the menu item renders on, so a caller that reaches this
    // without the button cannot mount ArenaMatchResult on a spectate seat.
    if (!gameLifecycle.canEndMatch(this)) return;
    try { this.sync?.emit('match:end-request', {}); } catch {}
    this._endMatchManually();
  };

  // Player-triggered "End Match" while the engine hasn't declared a
  // game over yet. Until the combat engine resolves death blows
  // end-to-end we infer the loser from Death's Door state: a player
  // who is at DD AND past their one-turn grace immunity has
  // effectively lost. The next damage to their avatar would be a
  // death blow regardless of who's clicking. If exactly one side is
  // in that state, we pre-set autoMatchWinner so ArenaMatchResult
  // submits the report directly. Otherwise the modal falls back to
  // a "Who won?" picker so the player can resolve it manually.
  _endMatchManually = () => {
    // Idempotent. If the modal is already open (either local
    // clicked, or we already received the opponent's request),
    // don't reopen it and clobber the in-flight resolution.
    if (this.state.showMatchResult) return;

    const localPlayer = this.state.isHost ? 'p1' : 'p2';
    const opponent = localPlayer === 'p1' ? 'p2' : 'p1';
    const pr = this.playerResources;

    const pastGrace = (p) => pr && pr.isAtDeathsDoor(p) && !pr.isImmuneToDeathBlow(p);
    const localLost = pastGrace(localPlayer);
    const opponentLost = pastGrace(opponent);

    let autoMatchWinner = null;
    if (localLost && !opponentLost) autoMatchWinner = 'opponent';
    else if (opponentLost && !localLost) autoMatchWinner = 'me';

    this.setState({
      attackTargetModal: null,
      autoMatchWinner,
      showMatchResult: true,
      showGameMenu: false,
    });
  };


  // Attack-target pick: the chosen target carries its rec; declare via a
  // combat intent to the authoritative server. Damage exchange, wards,
  // defend / intercept windows, and death sweeps all resolve server-side.
  _resolveAttackAction = (target) => {
    const modal = this.state.attackTargetModal;
    if (!modal) return;
    const rec = target?._rec;
    this._closeAttackModal();
    if (rec) this._declareAttackFromRec(modal.attackerId, rec);
  };

  // v1.57. Wraps engine/core/defend.getEligibleDefenders with the
  // reach computation (one step + Movement +N bonus, region-aware).
  _collectEligibleDefenders = (attacker, defender, defenderCell) => {
    if (!defender || !defenderCell) return [];
    const grid = getGameGrid(this.state.spawnConfig);
    if (!grid) return [];
    const { colPositions, rowPositions } = computeGridPositions(grid);
    const totalCols = colPositions.length - 1;
    const totalRows = rowPositions.length - 1;
    const getFullCard = this._getFullCard;
    const reachComputer = (card) => {
      const mesh = this.meshes.get(card.id);
      if (!mesh) return new Set();
      const meshCell = this._cellAtWorld(mesh.position.x, mesh.position.z);
      if (!meshCell) return new Set();
      const fullCard = getFullCard(card.cardId);
      const stepBudget = 1 + effectiveMovementBonus(card);
      // A multi-cell unit's range of motion is the union of the reach
      // from every cell it occupies, so it can defend an attack reachable
      // from any of them. Single-cell units iterate once (unchanged).
      const union = new Set();
      for (const occ of unitFootprintCells(card, meshCell)) {
        const fromRegion = currentRegion(card, occ, this.meshes, this._cellAtWorld, getFullCard);
        const reach = reachableLocationsInSteps(
          card, fullCard,
          { col: occ.col, row: occ.row, region: fromRegion },
          stepBudget,
          this.meshes, this._cellAtWorld, totalCols, totalRows, getFullCard,
        );
        for (const c of reach) union.add(c);
      }
      return union;
    };
    // The attack's region: a defender must reach the attack's exact location
    // (cell AND region). A site / surface unit attack is on the surface; a
    // subsurface unit carries its own region.
    const targetRegion = currentRegion(defender, defenderCell, this.meshes, this._cellAtWorld, getFullCard);
    return getEligibleDefenders(
      attacker, defender, defenderCell.col, defenderCell.row, targetRegion,
      this.meshes, this._cellAtWorld, reachComputer,
    );
  };

  // v1.63. Fire a combat trigger on the source card's own automation.
  // Distinct from _fireRealmTrigger (which finds OTHER cards watching
  // an event); this is the SELF-trigger path. The source's automation
  // entries with matching trigger run via the existing
  // applyCardAutomation pipeline; watchTarget carries the combat
  // context (defender / source / amount / projectile / killed) so
  // entry effects can scope or condition on it.
  //
  // No-op when the source has no matching trigger entries. Every
  // card in the catalog today is silent here until the parser learns
  // to emit ON_STRIKE / ON_DAMAGE_* / ON_ATTACK / ON_KILL entries.
  _fireCombatTrigger = (triggerType, sourceCard, context = {}) => {
    if (!triggerType || !sourceCard) return;
    // S3. On_kill / on_damage_taken fire from the SERVER's combat manifest
    // (the router drops client trigger intents for them); running the local
    // pass would double prompts/effects.
    if (this._authZonesEnabled() && SERVER_AUTONOMOUS_TRIGGERS.has(triggerType)) return;
    // Silenced minions fire no combat triggers. Their box text is
    // offline. (Today combat-tagged automation isn't shipped, so this
    // gate is forward-compatible insurance; it costs nothing and keeps
    // Silence's behavior consistent if/when combat triggers light up.)
    if (isCardSilenced(sourceCard)) return;
    const fullCard = this._getFullCard(sourceCard.cardId);
    const entries = fullCard?.automation || [];
    // Transient combat grants installed on this striker (Gift of the
    // Raven's one-shot lifelink) fire alongside the intrinsic
    // automation; one-shot entries are consumed after they fire.
    const transient = transientCombatEntries(sourceCard, triggerType);
    const matching = [...entries.filter((e) => e.trigger === triggerType), ...transient];
    if (matching.length === 0) return;
    // v1.97. Extract the watch-target CARD from the combat context so
    // watch_target_filter conditions can gate (Realm-Eater needs to
    // see defender.type === 'Site'). Each trigger type knows which
    // context field is "the affected card":
    //   ON_ATTACK / ON_STRIKE / ON_DAMAGE_DEALT → defender
    //   ON_DAMAGE_TAKEN → source
    //   ON_KILL → killed
    const watchTarget = context.defender || context.killed || context.source || null;
    // v1.90 / v1.97. Resolve simple instant-effect combat triggers
    // inline (no storyline prompt). Each entry either fires now
    // (silent) or is forwarded to the normal pipeline.
    // On the auth path, the SERVER resolves any entry whose ops are ALL
    // server-automation ops (status/mark/rubble/dispel). We emit one
    // automation:trigger intent for this (source, trigger). The server
    // re-derives every matching server-silent entry from the catalog and mutates
    // the draft; both clients render the resulting diff. Those entries are then
    // SKIPPED locally so the effect resolves once (server), not twice.
    const auth = this._authZonesEnabled();
    let sentAuthTrigger = false;
    let needsPipeline = false;
    const fired = [];
    for (const entry of matching) {
      const entryAllSilent = (entry.effect || []).every((e) => realmTriggers.COMBAT_SILENT_OPS.has(e.op));
      if (!entryAllSilent) { needsPipeline = true; continue; }
      // Auth cutover: a fully-server-automation entry is resolved server-side.
      // Emit the intent once and skip ALL such entries locally.
      const serverOwned = auth && (entry.effect || []).every((e) => SERVER_SILENT_OPS.has(e.op));
      if (serverOwned) {
        if (!sentAuthTrigger) {
          this._sendMatchIntent(triggerIntent(sourceCard.id, triggerType, {
            watchTargetId: watchTarget?.id || null,
            amount: context.amount ?? null,
          }));
          sentAuthTrigger = true;
        }
        continue;
      }
      if (entry.condition) {
        const ok = evaluateCondition(entry.condition, sourceCard, fullCard, this.meshes,
          { cellAt: this._cellAtWorld, watchTarget, sourceType: null, amount: context.amount });
        if (!ok) continue;
      }
      this._resolveCombatSilentEffects(sourceCard, entry, context, watchTarget);
      fired.push(entry);
    }
    consumeOneShotTransients(sourceCard, fired);
    if (needsPipeline) {
      this.applyCardAutomation(sourceCard, [triggerType], { watchTarget: context });
    }
  };

  // Inline resolution for combat-trigger entries whose effects are all
  // silent-dispatchable (no storyline confirm). Shared by the self path
  // (_fireCombatTrigger) and the watcher pass (fireCombatWatcherTrigger),
  // so Interrogator's dodge prompt fires identically whether the
  // Interrogator itself struck or an ally did.
  _resolveCombatSilentEffects = (sourceCard, entry, context, watchTarget) => {
    for (const eff of (entry.effect || [])) {
      if (eff.op === OP.HEAL_PLAYER) {
        this._resolveHealPlayerInline(sourceCard, eff, context);
      } else if (eff.op === OP.DISPEL_SELF) {
        // Sylvan Splendor: dispel when an affected site is attacked.
        // Mirrors silentOps.applyDispelSelf. Log + send the aura to
        // its cemetery (the standard leave-play path).
        if (this.meshes.has(sourceCard.id)) {
          this.logAction('{card} dispels', { card: sourceCard.name || msg('Aura') });
          this.sendCardToPile(sourceCard, 'Cemetery');
        }
      } else if (eff.op === OP.DISCARD_SPELL) {
        // Wizard's Den: "Discard a spell when this site is first attacked
        // successfully." `once` consumes a one-time flag on the first hit.
        // Reuses the discard-cost hand-pick (_pendingDiscardCost) as a
        // standalone effect (kind 'effect_only' → no follow-up on resolve).
        // Auth cutover: the SERVER fires this off the combat manifest
        // (fireOnDamageTakenOnDraft) and writes a durable hand pile prompt
        // the site's controller answers via authChoice. Skip the local pick.
        if (this._authZonesEnabled?.()) continue;
        const once = !!eff.args?.once;
        if (!(once && sourceCard._discardSpellUsed)) {
          if (once) sourceCard._discardSpellUsed = true;
          const controller = sourceCard.rotated ? 'p2' : 'p1';
          const hand = controller === 'p2' ? this.state.handCards2 : this.state.handCards;
          if ((hand || []).length > 0) {
            this._pendingDiscardCost = {
              controller,
              sourceCardId: sourceCard.id,
              kind: 'effect_only',
              filter: eff.args?.filter || {},
            };
            this.forceUpdate();
          }
        }
      } else if (eff.op === OP.CONVERT_WATCH_TARGET_TO_RUBBLE) {
        if (watchTarget?.isSite && watchTarget.cardId !== 'sorcery-rubble') {
          this.logAction('{source} destroys {target}. It becomes Rubble', { source: sourceCard.name || msg('Source'), target: watchTarget.name || msg('site') });
          this.turnToRubble(watchTarget);
        }
      } else if (eff.op === OP.GRANT_STAT_SELF_THIS_TURN) {
        const delta = eff.args?.delta || 0;
        const stat = eff.args?.stat || 'atk';
        if (delta !== 0) this._applyThisTurnStatBuff(sourceCard, delta, stat);
      } else if (eff.op === OP.MARK_USED_THIS_TURN) {
        const key = eff.args?.key;
        if (key) {
          if (!sourceCard._abilitiesUsedThisTurn) sourceCard._abilitiesUsedThisTurn = new Set();
          sourceCard._abilitiesUsedThisTurn.add(key);
        }
      } else if (eff.op === OP.INTERROGATOR_DODGE) {
        // Auth cutover: the SERVER fires this off the combat trigger manifest
        // (strikeTriggers) and writes a durable modal pendingChoice the struck
        // seat answers via authChoice. Skip the local prompt so it opens once.
        if (this._authZonesEnabled?.()) continue;
        // v1.103. Push a 2-button prompt to the OPPONENT (struck
        // Avatar's controller) asking whether to pay 3 life. On
        // decline, Interrogator's controller draws a spell.
        const sourceController = sourceCard.rotated ? 'p2' : 'p1';
        const opponent = sourceController === 'p1' ? 'p2' : 'p1';
        const promptId = `interrog-${sourceCard.id}-${Math.random().toString(36).slice(2, 8)}`;
        this._pushStorylineEvents([{
          id: promptId,
          cardInstance: sourceCard,
          cardName: sourceCard.name || 'Interrogator',
          controller: opponent,
          sourceEntry: null,
          promptType: 'interrogator_dodge',
          description: t('{name}: pay 3 life to prevent the draw, or let them draw a spell?', { name: sourceCard.name || 'Interrogator' }),
          _interrogator: { sourceController, opponent },
        }]);
      } else if (eff.op === OP.APPLY_STATUS_TO_SELF) {
        const keyword = eff.args?.keyword;
        if (keyword) {
          const changed = applyStatusToggle(sourceCard, keyword, true);
          if (changed) {
            this.rebuildStatusBadges(sourceCard.id);
            this._broadcastCardStatus(sourceCard.id, keyword, true);
            this.recomputeStaticAuras();
            this.logAction('{source} gains {keyword}', { source: sourceCard.name || msg('Source'), keyword });
          }
        }
      }
    }
  };

  _resolveHealPlayerInline = (sourceCard, effect, context) => automationRuntime.resolveHealPlayerInline(this, sourceCard, effect, context);

  _resolveSourceCell = (pt) => cardStateOps.resolveSourceCell(this, pt);

  _moveCardToCell = (card, cell, sourceLabel, verb, moveType = 'forced') => cardStateOps.moveCardToCell(this, card, cell, sourceLabel, verb, moveType);

  _placeCardAtCell = (card, cell, sourceLabel, verb) => cardStateOps.placeCardAtCell(this, card, cell, sourceLabel, verb);

  _commitCardMove = (card, worldPos, opts) => cardStateOps.commitCardMove(this, card, worldPos, opts);

  _cellCenterWorld = (col, row) => cardStateOps.cellCenterWorld(this, col, row);

  _refreshLifeHUD = (card) => cardStateOps.refreshLifeHUD(this, card);

  _refreshCounterHUD = (cardId) => cardStateOps.refreshCounterHUD(this, cardId);

  _refreshCounterAnchorFor = (counterCardId) => cardStateOps.refreshCounterAnchorFor(this, counterCardId);

  _attachLifeHUDIfMissing = (card) => cardStateOps.attachLifeHUDIfMissing(this, card);

  _detachLifeHUD = (card) => cardStateOps.detachLifeHUD(this, card);

  _refreshMovementHUD = (cardId) => cardStateOps.refreshMovementHUD(this, cardId);

  _spawnFloatingDamage = (targetMesh, amount, color) => cardStateOps.spawnFloatingDamage(this, targetMesh, amount, color);

  _removeFloatingDamage = (id) => cardStateOps.removeFloatingDamage(this, id);

  _projectCardRightEdge = (mesh) => overlayProjections.projectCardRightEdge(this, mesh);

  _setSubsurface = (cardInstance, region) => cardStateOps.setSubsurface(this, cardInstance, region);

  _voluntarySubsurfaceMove = (cardInstance, region) => cardStateOps.voluntarySubsurfaceMove(this, cardInstance, region);

  _closeCardActionMenu = () => menusAndInspector.closeCardActionMenu(this);


  _blockInvalidSubsurfacePlays = (tableMembers, freshPlayIds) => pointerInteractions.blockInvalidSubsurfacePlays(this, tableMembers, freshPlayIds);

  _blockInvalidSitePlacements = (tableMembers, freshPlayIds) => pointerInteractions.blockInvalidSitePlacements(this, tableMembers, freshPlayIds);

  _applyFreshPlayGating = (tableMembers, freshPlayIds) => pointerInteractions.applyFreshPlayGating(this, tableMembers, freshPlayIds);

  _getFullCard = (cardId) => this.props.sorceryCards?.find((c) => c.unique_id === cardId) || null;

  // Stable key for the per-turn ledger of activated abilities. Same
  // shape every site that needs it: `entry.id` (parser-emitted entry)
  // or `entry._builtin` (engine-injected entry like 'tap_draw_spell',
  // 'avatar_site', 'realm_eater_destroy') or the generic fallback.
  _oncePerTurnKey = (entry) => entry.id || entry._builtin || 'activated';

  // Pull the controller's Atlas pile + the top-of-atlas peek in one
  // call. Every atlas-top consumer (Pathfinder tap, Geomancer replace,
  // future Dragonlord pre-setup) needs the same triple. Having one
  // helper means the rotated-controller filter, the catalog closure,
  // and the topAtlasSite contract live in one place.
  //
  // Returns `{ atlas, top, getFullCard }` where `atlas` is the pile and
  // `top` is `{ instance, fullCard }` (or null when the atlas is empty
  // or the top card isn't a Site).
  _getControllerAtlas = (controllerRotated) => {
    const atlas = this.state.gameState.piles.find(
      (p) => p.name === 'Atlas' && !!p.rotated === !!controllerRotated,
    );
    const top = topAtlasSite(atlas, this._getFullCard);
    return { atlas, top, getFullCard: this._getFullCard };
  };

  _hasSiteInHand = (controller) => spawnPlacement.hasSiteInHand(this, controller);

  _findAvatarCard = (controllerRotated) => spawnPlacement.findAvatarCard(this, controllerRotated);

  _isMandatorySitePlayPending = (controller) => spawnPlacement.isMandatorySitePlayPending(this, controller);

  _anyMandatoryActionPending = (controller) => spawnPlacement.anyMandatoryActionPending(this, controller);

  _canPlayFromHand = (card) => spawnPlacement.canPlayFromHand(this, card);

  _canPlaceSiteAt = (siteCard, member) => spawnPlacement.canPlaceSiteAt(this, siteCard, member);

  _payCastingCost = (card, fullCard, destinationCell = null) => cardStateOps.payCastingCost(this, card, fullCard, destinationCell);

  _applyMovementGating = (tableMembers, freshPlayIds) => pointerInteractions.applyMovementGating(this, tableMembers, freshPlayIds);

  handleContextMenu = (event) => pointerInteractions.handleContextMenu(this, event);


  updateSpawnMarkers = (config) => trackerHud.updateSpawnMarkers(this, config);

  clearTrackerPreviews = () => trackerHud.clearTrackerPreviews(this);

  trackerMeshKey = (player, trackerKey, row) => trackerHud.trackerMeshKey(player, trackerKey, row);

  createTrackerTokens = () => trackerHud.createTrackerTokens(this);

  updateTrackerTokenPositions = () => trackerHud.updateTrackerTokenPositions(this);

  createTrackerButtons = () => trackerHud.createTrackerButtons(this);

  applyLifeButton = (opts) => trackerHud.applyLifeButton(this, opts);

  _displayNameForPlayer = (player) => trackerHud.displayNameForPlayer(this, player);

  _lifeLogText = (player, prev, next) => trackerHud.lifeLogText(this, player, prev, next);

  _buffDurationLabel = (entry) => trackerHud.buffDurationLabel(entry);

  _buffAddedLogText = (entry) => trackerHud.buffAddedLogText(entry);

  _buffClearedLogText = (entry) => trackerHud.buffClearedLogText(entry);

  _flashTrackerButton = (mesh) => trackerHud.flashTrackerButton(this, mesh);

  incrementTracker = (player, trackerKey) => trackerHud.incrementTracker(this, player, trackerKey);

  _openHudPanel = (player, kind) => trackerHud.openHudPanel(this, player, kind);

  _closeHudPanel = () => trackerHud.closeHudPanel(this);

  _canEditHudFor = (player) => trackerHud.canEditHudFor(this, player);

  _viewerPlayer = () => (this.state.isHost ? 'p1' : 'p2');

  _canvasLocalAnchor = (rect) => trackerHud.canvasLocalAnchor(rect);

  _handleHudScroll = (player, kind, event) => trackerHud.handleHudScroll(this, player, kind, event);

  _commitHudScroll = () => trackerHud.commitHudScroll(this);

  _cancelHudScroll = () => trackerHud.cancelHudScroll(this);

  _adjustLife = (player, delta) => trackerHud.adjustLife(this, player, delta);

  _addManualManaBuff = (player, amount, duration, n) => trackerHud.addManualManaBuff(this, player, amount, duration, n);

  _removeBuffEntry = (entryId) => trackerHud.removeBuffEntry(this, entryId);

  decrementTracker = (player, trackerKey) => trackerHud.decrementTracker(this, player, trackerKey);

  addTrackerPreview = (x, z) => trackerHud.addTrackerPreview(this, x, z);

  showTrackerCursorPreview = () => trackerHud.showTrackerCursorPreview(this);

  hideTrackerCursorPreview = () => trackerHud.hideTrackerCursorPreview(this);

  toggleGridEditor = () => devEditors.toggleGridEditor(this);

  clearGrid = () => devEditors.clearGrid(this);

  copyGridJSON = () => devEditors.copyGridJSON(this);

  toggleUiInversionEditor = () => devEditors.toggleUiInversionEditor(this);

  addUiInversionRegion = () => devEditors.addUiInversionRegion(this);

  deleteActiveUiInversionRegion = () => devEditors.deleteActiveUiInversionRegion(this);

  toggleComponentEditor = () => devEditors.toggleComponentEditor(this);

  copyComponentPlacementsJSON = () => devEditors.copyComponentPlacementsJSON(this);

  copyUiInversionJSON = () => devEditors.copyUiInversionJSON(this);

  toggleSpawnEditor = () => devEditors.toggleSpawnEditor(this);


  drawCard = (pileId, opts = {}) => cardPileLifecycle.drawCard(this, pileId, opts);

  // Draw choreography for server-autonomous hand additions (staggered from the
  // scheduler in authZones): the presence of this method also switches the
  // hand-additions fold from an instant add to the animated path.
  _animateDrawnCardToHand = (card, seat) => cardPileLifecycle.animateDrawnCardToHand(this, card, seat);

  _animateUnitHandAdditions = (nextUnitHands) => cardPileLifecycle.animateUnitHandAdditions(this, nextUnitHands);

  drawCardToDrag = (pileId, pileMeshHit) => cardPileLifecycle.drawCardToDrag(this, pileId, pileMeshHit);

  playCardFromHand = (cardInstance, event) => cardPileLifecycle.playCardFromHand(this, cardInstance, event);

  addCardToTable = (cardInstance, broadcast = true, opts = {}) => cardPileLifecycle.addCardToTable(this, cardInstance, broadcast, opts);

  _openPrompt = (descriptor) => realmTriggers.openPrompt(this, descriptor);

  _closePrompt = (id) => realmTriggers.closePrompt(this, id);

  _rehydratePromptStack = (descriptors) => realmTriggers.rehydratePromptStack(this, descriptors);

  _filterPromptsForLocalSeat = (prompts) => realmTriggers.filterPromptsForLocalSeat(this, prompts);

  _pushStorylineEvents = (orderedPrompts) => realmTriggers.pushStorylineEvents(this, orderedPrompts);

  _enqueueOpponentSpellNotice = (payload) => realmTriggers.enqueueOpponentSpellNotice(this, payload);

  _enqueueOpponentAbilityNotice = (payload) => realmTriggers.enqueueOpponentAbilityNotice(this, payload);

  _pushSimultaneousStorylineEvents = (prompts, activePlayer) => realmTriggers.pushSimultaneousStorylineEvents(this, prompts, activePlayer);

  _shouldRunAutomation = () => automationRuntime.shouldRunAutomation(this);

  applyCardAutomation = (cardInstance, triggers = ['on_play', 'static'], overrides = {}) => automationRuntime.applyCardAutomation(this, cardInstance, triggers, overrides);

  _grantSitePlacement = (controller, restriction, sourceName) => automationRuntime.grantSitePlacement(this, controller, restriction, sourceName);

  _grantFreeCast = (controller, sourceName) => automationRuntime.grantFreeCast(this, controller, sourceName);

  _isFreeCastActive = (controller) => automationRuntime.isFreeCastActive(this, controller);

  _consumeFreeCast = (controller) => automationRuntime.consumeFreeCast(this, controller);

  // ── Spell-casting pivot (v1.14) ──────────────────────────────────
  //
  // Sorcery rules: a Magic spell is cast BY a controlled Spellcaster
  // (or your Avatar). Every positional scope on the spell. "nearby",
  // "adjacent", "at target [scope] location." Resolves relative to
  // that caster, NOT the spell's drop point on the table. After
  // resolution the spell card goes to its controller's Cemetery; it
  // never sits on the field.
  //
  // Flow:
  //   1. Find every valid spellcaster for `controller`.
  //   2. If exactly one (typical early-game Avatar-only case) →
  //      auto-pick and cast immediately.
  //   3. If two or more → enqueue a 'pick_caster' prompt; the player's
  //      click on a halo'd unit becomes the caster.
  //   4. If none → fall back to the legacy (no-anchor) flow so the
  //      spell still resolves. Shouldn't happen since Avatars are
  //      always valid casters, but worth a safe fallback.
  //
  // Caster overrides are stored on each prompt as { sourcePos,
  // controllerRotated } and consumed by _applyEffectsForConfirm.

  // Wrappers around cardEffects/spellcaster.js. The module owns the
  // eligibility rules; GameBoard supplies the live mesh map + catalog.
  _isSpellcaster = (cardInstance, fullCard) => isSpellcaster(cardInstance, fullCard);

  _validSpellcastersFor = (controller, spell = null) => findValidSpellcasters(
    controller,
    this.meshes,
    (cardId) => this._getFullCard(cardId)|| null,
    spell ? cardThresholds(spell) : null,
  );

  _routeSpellPlay = (spellCard, fullSpell, triggers, controller) => automationRuntime.routeSpellPlay(this, spellCard, fullSpell, triggers, controller);

  _castSpellWithCaster = (spellCard, caster, fullSpell, triggers, controller) => automationRuntime.castSpellWithCaster(this, spellCard, caster, fullSpell, triggers, controller);

  _maybeDisposeFinishedSpell = (spellId, controller) => automationRuntime.maybeDisposeFinishedSpell(this, spellId, controller);

  _resolveStandaloneSpellEffects = (spellCard, controller, fullCard) => automationRuntime.resolveStandaloneSpellEffects(this, spellCard, controller, fullCard);

  // Searing Truth cross-client forced reveal-draw. The caster emits the
  // request; the target's client draws + reveals and replies the higher
  // cost, which the caster applies as Avatar damage.
  _castForcedRevealDraw = (spellCard, controller, args) => automationRuntime.castForcedRevealDraw(this, spellCard, controller, args);

  _resolveForceRevealDraw = (count) => automationRuntime.resolveForcedRevealDraw(this, count);

  _applyForceRevealDrawDamage = (requestId, higherCost) => automationRuntime.applyForcedRevealDrawDamage(this, requestId, higherCost);

  _promptOpponentPileEndSwap = (requestId, card) => automationRuntime.promptOpponentPileEndSwap(this, requestId, card);

  _resolvePileEndSwapResult = (requestId, swap) => automationRuntime.resolvePileEndSwapResult(this, requestId, swap);

  _landCastSpellOnBoard = (spellCard) => automationRuntime.landCastSpellOnBoard(this, spellCard);

  _applyEquippedArtifactEffects = (equippedCard, hostCard) => staticAuras.applyEquippedArtifactEffects(this, equippedCard, hostCard);

  _clearEquippedArtifactEffects = (equipmentId) => staticAuras.clearEquippedArtifactEffects(this, equipmentId);

  _reverseManaContribution = (sourceCardId) => staticAuras.reverseManaContribution(this, sourceCardId);

  // ── Static aura resolution (v1.2a) ─────────────────────────────
  //
  // Pure board-state recompute: walks every on-table card, finds the
  // ones with static GRANT_STAT auras, resolves their scope against
  // the grid, and writes a `_auraStatDelta` field on each affected
  // card instance. Display ATK = currentAttack + _auraStatDelta, so
  // manual status-ring adjustments (which mutate currentAttack) and
  // aura deltas stay independent.
  //
  // Recompute is cheap. Single pass over <50 meshes. So we call it
  // on every play / remove / move / passTurn. No diffing, no ledger.

  // Thin wrapper around cellAtWorld (cardEffects/scope.js) that
  // supplies the current grid from spawnConfig.
  _cellAtWorld = (x, z) => cellAtWorld(getGameGrid(this.state.spawnConfig), x, z);

  // Filtered view of `this.meshes` that omits any card currently
  // mid-drag from a staged fresh play (hand → table or pile draw).
  // A staged card has a mesh in `this.meshes` so raycast / physics
  // can drive the drag, but it is NOT yet committed to the realm.
  // the mesh position tracks the cursor, not a permanent location.
  //
  // Site-state derivers (cell element affinity, region classification,
  // void / adjacency placement legality) must ignore the in-flight
  // drag, otherwise hovering an empty cell with a Site card stamps
  // that cell with the site's affinities and flips it to "illegal"
  // (cellIsVoid thinks the site is already there) before the player
  // has even released.
  //
  // Returns `this.meshes` unchanged when no drag is in flight so the
  // common path stays allocation-free.
  _meshesExcludingStagedDrag = () => {
    const exclude = this.dragging?.freshPlayIds;
    if (!exclude || exclude.size === 0) return this.meshes;
    const filtered = new Map();
    for (const [id, mesh] of this.meshes) {
      if (!exclude.has(id)) filtered.set(id, mesh);
    }
    return filtered;
  };

  // Thin wrapper over cellsWithSites (cardEffects/locations.js) that
  // supplies the live mesh map + grid resolver.
  _cellsWithSites = () => cellsWithSites(this.meshes, this._cellAtWorld);

  // Thin wrapper around resolveAuraScope (cardEffects/scope.js) that
  // supplies this.meshes and the local cell-resolver.
  _resolveAuraScope = (sourceCard, scope, args, overrides = {}) =>
    resolveAuraScope(sourceCard, scope, args, {
      meshes: this.meshes,
      cellAt: this._cellAtWorld,
      getFullCard: this._getFullCard,
      overrides,
    });

  recomputeStaticAuras = () => staticAuras.recomputeStaticAuras(this);

  _applyOwnershipFlip = (card, mesh) => staticAuras.applyOwnershipFlip(this, card, mesh);

  _snapAuraToAnchor = (card, mesh) => staticAuras.snapAuraToAnchor(this, card, mesh);

  _snapSiteToOwnerEdge = (card, mesh) => staticAuras.snapSiteToOwnerEdge(this, card, mesh);

  _evaluateCondition = (condition, cardInstance, fullCard, extraOpts = {}) => automationRuntime.evaluateCondition(this, condition, cardInstance, fullCard, extraOpts);

  _entryFirstTargetOpHasNoMatches = (cardInstance, entry, casterOverrides, excludeId = null) => automationRuntime.entryFirstTargetOpHasNoMatches(this, cardInstance, entry, casterOverrides, excludeId);


  _startTargeting = (sourceCard, controller, op, args, casterOverrides = null, followupOps = null, options = {}) => automationRuntime.startTargeting(this, sourceCard, controller, op, args, casterOverrides, followupOps, options);

  _describeTargetFilter = (filter) => automationRuntime.describeTargetFilter(this, filter);

  _tryResolveTarget = (clickedCard) => automationRuntime.tryResolveTarget(this, clickedCard);

  // Server-authoritative automation choice (P6 cutover, Stage B): resolve a click
  // on a candidate into a chooseIntent; decline an optional prompt on ESC.
  _tryResolveServerChoice = (clickedCard) => authChoice.tryResolveServerChoice(this, clickedCard);

  _tryResolveServerCellChoice = (event) => authChoice.tryResolveServerCellChoice(this, event);

  _declineServerChoice = () => authChoice.declineServerChoice(this);

  _resolveServerModalChoice = (value) => authChoice.resolveServerModalChoice(this, value);

  _buildServerScryCastOption = (pc) => automationRuntime.buildServerScryCastOption(this, pc);

  _cancelTargeting = () => automationRuntime.cancelTargeting(this);


  _startCellTargeting = (sourceCard, controller, op, args, casterOverrides = null) => automationRuntime.startCellTargeting(this, sourceCard, controller, op, args, casterOverrides);

  _openPickPrompt = (sourceCard, controller, promptType, description) => automationRuntime.openPickPrompt(this, sourceCard, controller, promptType, description);

  _openCellPickPrompt = (sourceCard, controller, description) =>
    this._openPickPrompt(sourceCard, controller, 'pick_cell', description);

  _openTargetPickPrompt = (sourceCard, controller, description) =>
    this._openPickPrompt(sourceCard, controller, 'pick_target', description);

  _startCellPick = (sourceCard, controller, op, validCells, opts = {}) => automationRuntime.startCellPick(this, sourceCard, controller, op, validCells, opts);

  _startPercevalSitePick = (sourceCard, opponent) => automationRuntime.startPercevalSitePick(this, sourceCard, opponent);

  _dequeuePickPrompt = (promptId) => automationRuntime.dequeuePickPrompt(this, promptId);

  _dequeuePickCellPrompt = (promptId) => this._dequeuePickPrompt(promptId);

  _tryResolveCellTarget = (event) => automationRuntime.tryResolveCellTarget(this, event);

  _cancelCellTargeting = () => automationRuntime.cancelCellTargeting(this);

  _executeCellOp = (pct, cell) => automationRuntime.executeCellOp(this, pct, cell);

  _startAuraOneStep = (sourceCard, controller) => automationRuntime.startAuraOneStep(this, sourceCard, controller);

  _resolveAuraOneStep = (sourceCard, cell, sourceName) => automationRuntime.resolveAuraOneStep(this, sourceCard, cell, sourceName);

  _resolveWildfireMove = (sourceCard, cell, sourceName) => automationRuntime.resolveWildfireMove(this, sourceCard, cell, sourceName);

  // Set Perceval's remembered cell ({col,row} or null) locally and mirror it
  // to the other client. The opponent resolves the site pick on their seat
  // while the on_stop draw gate evaluates on the controller's, so both
  // instances must carry the same stamp. socketListeners' 'perceval:target'.
  _setPercevalTarget = (sourceCard, cell) => {
    sourceCard._percevalTargetCell = cell;
    if (this._authZonesEnabled()) {
      this._sendMatchCommand(authCardState.flagsCommand(sourceCard.id, { percevalTargetCell: cell ?? null }));
    }
  };

  _startAvatarOfWaterFlood = (avatar, controller) => avatarAbilities.startAvatarOfWaterFlood(this, avatar, controller);

  _computeFloodCells = (avatar, nearby) => avatarAbilities.computeFloodCells(this, avatar, nearby);

  _resolveAvatarOfWaterFlood = (avatar, controller, cell, sourceName) => avatarAbilities.resolveAvatarOfWaterFlood(this, avatar, controller, cell, sourceName);

  _doDruidFlip = (druid) => avatarAbilities.doDruidFlip(this, druid);

  _armAnimistSpirit = (avatar, controller) => avatarAbilities.armAnimistSpirit(this, avatar, controller);

  _disarmAnimistSpirit = (controller) => avatarAbilities.disarmAnimistSpirit(this, controller);

  _startPathfinderTap = (avatar, controller) => avatarAbilities.startPathfinderTap(this, avatar, controller);

  _startBladedancerStep = (avatar, controller) => avatarAbilities.startBladedancerStep(this, avatar, controller);

  _resolveBladedancerStep = (avatar, controller, cell, sourceName) => avatarAbilities.resolveBladedancerStep(this, avatar, controller, cell, sourceName);

  _startSparkmageShock = (avatar, controller) => avatarAbilities.startSparkmageShock(this, avatar, controller);

  _resolveSparkmageShock = (avatar, controller, cell, sourceName, damage) => avatarAbilities.resolveSparkmageShock(this, avatar, controller, cell, sourceName, damage);

  _resolvePersecutorChoice = (avatar, controller, choice) => avatarAbilities.resolvePersecutorChoice(this, avatar, controller, choice);

  _resolvePathfinderTap = (avatar, controller, cell, sourceName) => avatarAbilities.resolvePathfinderTap(this, avatar, controller, cell, sourceName);

  _startGeomancerReplaceRubble = (avatar, controller) => avatarAbilities.startGeomancerReplaceRubble(this, avatar, controller);

  _resolveGeomancerReplaceRubble = (avatar, controller, cell, sourceName) => avatarAbilities.resolveGeomancerReplaceRubble(this, avatar, controller, cell, sourceName);

  _spawnRubbleAtCell = (cell, rotated, sourceName) => avatarAbilities.spawnRubbleAtCell(this, cell, rotated, sourceName);

  _maybeFireDruidTawnySummon = (siteCard) => avatarAbilities.maybeFireDruidTawnySummon(this, siteCard);

  _maybeFireGeomancerEarthBonus = (siteCard) => avatarAbilities.maybeFireGeomancerEarthBonus(this, siteCard);

  _resolveWitchCurse = (sourceName, controller, opponent, choice) => avatarAbilities.resolveWitchCurse(this, sourceName, controller, opponent, choice);

  _startFlamecallerFireBlast = (avatar, controller) => avatarAbilities.startFlamecallerFireBlast(this, avatar, controller);

  _startImposterMask = (avatar, controller, manaCost = 0) => avatarAbilities.startImposterMask(this, avatar, controller, manaCost);

  _resolveMaskPick = (pickedCard) => avatarAbilities.resolveMaskPick(this, pickedCard);

  _putOnMask = (imposter, sourceCard) => avatarAbilities.putOnMask(this, imposter, sourceCard);

  _takeOffMask = (imposter) => avatarAbilities.takeOffMask(this, imposter);


  _findCardById = (cardId) => {
    const mesh = this.meshes.get(cardId);
    return mesh?.userData?.cardInstance || null;
  };

  _startArchimagoCastCopy = (avatar, controller) => avatarAbilities.startArchimagoCastCopy(this, avatar, controller);

  _resolveArchimagoBanishPick = (pickedCard) => avatarAbilities.resolveArchimagoBanishPick(this, pickedCard);

  _resolveArchimagoCopyPick = (pickedCard) => avatarAbilities.resolveArchimagoCopyPick(this, pickedCard);

  _executeDrawOp = (args, sourceCard, controller) => automationRuntime.executeDrawOp(this, args, sourceCard, controller);

  _startDeathspeakerCastCopy = (avatar, controller) => avatarAbilities.startDeathspeakerCastCopy(this, avatar, controller);

  _resolveDeathspeakerPick = (pickedCard) => avatarAbilities.resolveDeathspeakerPick(this, pickedCard);

  _resolveDragonlordSetAsidePick = (pickedCard) => avatarAbilities.resolveDragonlordSetAsidePick(this, pickedCard);

  _resolveNameSpellPick = (pickedCard) => nameSpellPrompt.resolveNameSpellPick(this, pickedCard);

  _declineDragonlordSetAside = (pending) => avatarAbilities.declineDragonlordSetAside(this, pending);

  _syncDragonlordReadyGlow = (dt) => avatarAbilities.syncDragonlordReadyGlow(this, dt);

  _disposeDragonlordReadyGlows = () => avatarAbilities.disposeDragonlordReadyGlows(this);

  _syncDragonSpiritEchoes = (dt) => avatarAbilities.syncDragonSpiritEchoes(this, dt);

  _disposeDragonSpiritEchoes = () => avatarAbilities.disposeDragonSpiritEchoes(this);

  _resolveDragonlordInvoke = (avatar, controller) => avatarAbilities.resolveDragonlordInvoke(this, avatar, controller);

  _revertDragonlordIdentity = (avatar) => avatarAbilities.revertDragonlordIdentity(this, avatar);

  _demaskOnAvatarDamage = (controller) => avatarAbilities.demaskOnAvatarDamage(this, controller);

  _resolveFlamecallerFireBlast = (avatar, controller, impactCell, sourceName, damage) => avatarAbilities.resolveFlamecallerFireBlast(this, avatar, controller, impactCell, sourceName, damage);

  _startAvatarOfFireIgnite = (avatar, controller) => avatarAbilities.startAvatarOfFireIgnite(this, avatar, controller);

  _isAvatarOfFireIgniteActive = (controller) => avatarAbilities.isAvatarOfFireIgniteActive(this, controller);

  _clearAvatarOfFireIgnite = (controller) => avatarAbilities.clearAvatarOfFireIgnite(this, controller);

  _startAvatarOfFireProjectile = (avatar, controller, sourceName) => avatarAbilities.startAvatarOfFireProjectile(this, avatar, controller, sourceName);

  _resolveAvatarOfFireIgnite = (avatar, controller, impactCell, sourceName, impactDamage, splashDamage) => avatarAbilities.resolveAvatarOfFireIgnite(this, avatar, controller, impactCell, sourceName, impactDamage, splashDamage);


  _finalizeFreshPlayCommit = (card, m) => cardStateOps.finalizeFreshPlayCommit(this, card, m);
  _castMagicFromHand = (card) => cardStateOps.castMagicFromHand(this, card);

  _castFireSiteAsFireball = (card) => avatarAbilities.castFireSiteAsFireball(this, card);


  _applyDelayedStatBuff = (card, delta, { revertPlayer, revertAtTurnNumber }) => cardStateOps.applyDelayedStatBuff(this, card, delta, { revertPlayer, revertAtTurnNumber });

  _applyGrantStatusToTarget = (card, keyword, revert) => cardStateOps.applyGrantStatusToTarget(this, card, keyword, revert);

  _startWaveshaperFlood = (avatar, controller) => avatarAbilities.startWaveshaperFlood(this, avatar, controller);

  _resolveWaveshaperFlood = (avatar, controller, cell, sourceName) => avatarAbilities.resolveWaveshaperFlood(this, avatar, controller, cell, sourceName);

  _findCardCatalogToken = (tokenName) => spawnPlacement.findCardCatalogToken(this, tokenName);

  _spawnTokenAtPoint = (cardRow, controllerRotated, point) => spawnPlacement.spawnTokenAtPoint(this, cardRow, controllerRotated, point);

  _spawnSiteAtCell = (cardRow, cell, opts = {}) => spawnPlacement.spawnSiteAtCell(this, cardRow, cell, opts);

  _summonTokenAtLocations = (sourceCard, controller, args, casterOverrides = null) => spawnPlacement.summonTokenAtLocations(this, sourceCard, controller, args, casterOverrides);

  _startSummonPlacements = (queue, tokenName) => spawnPlacement.startSummonPlacements(this, queue, tokenName);

  _tryResolveSummonPlacement = (event, cellOverride = null) => spawnPlacement.tryResolveSummonPlacement(this, event, cellOverride);

  _cancelSummonPlacements = () => spawnPlacement.cancelSummonPlacements(this);

  _startPileCardPlacement = (sourcePile, cardInstance) => spawnPlacement.startPileCardPlacement(this, sourcePile, cardInstance);

  _startManualTokenSpawn = (pileEntry) => spawnPlacement.startManualTokenSpawn(this, pileEntry);

  _iterateEachOp = (sourceCard, controller, op, args, casterOverrides = null) => automationRuntime.iterateEachOp(this, sourceCard, controller, op, args, casterOverrides);

  _executeTargetOp = (pt, target) => automationRuntime.executeTargetOp(this, pt, target);

  // Look up the FIRST structured activated entry on a card (cost +
  // non-reminder effect). Returns null if the card has no activated
  // automation or only the generic REMINDER fallback. Used by the
  // context menu to decide whether to show "Activate ability".
  // Thin wrapper around hasActiveWard (cardEffects/wards.js).
  _hasActiveWard = (card) => hasActiveWard(card);

  _sweepDeadMinions = () => damagePipeline.sweepDeadMinions(this);

  _applyDamageReplacement = (target, source, amount, sourceType = null) => damagePipeline.applyDamageReplacement(this, target, source, amount, sourceType);

  _applyDamageToTarget = (target, amount, opts = {}) => damagePipeline.applyDamageToTarget(this, target, amount, opts);

  _applyRedirectedDamage = (target, amount) => damagePipeline.applyRedirectedDamage(this, target, amount);

  _applyThisTurnStatBuff = (card, delta, stat = 'atk') => cardStateOps.applyThisTurnStatBuff(this, card, delta, stat);

  _attemptPlayerDeathReplacement = (controller, sourceLabel) => damagePipeline.attemptPlayerDeathReplacement(this, controller, sourceLabel);

  _pickSacrificeHere = (source, filter = null) => damagePipeline.pickSacrificeHere(this, source, filter);

  _applyDeathReplacement = (dyingCard) => damagePipeline.applyDeathReplacement(this, dyingCard);

  _consumeWard = (card) => damagePipeline.consumeWard(this, card);

  _activatedEntriesFor = (cardInstance) => automationRuntime.activatedEntriesFor(this, cardInstance);

  _canActivateEntry = (card, entry) => automationRuntime.canActivateEntry(this, card, entry);

  // Imposter mask helpers. Pure pile inspection. Lives next to the
  // _canActivateEntry / _activateAbility pair so the gate logic and
  // the resolve handler read from the same source of truth.
  _collectionPileFor = (controller) => {
    const playerNum = controller === 'p2' ? 2 : 1;
    return this.findPileByNameForPlayer('Collection', playerNum) || null;
  };

  _collectionHasAvatar = (controller) => {
    const pile = this._collectionPileFor(controller);
    if (!pile || pile.cards.length === 0) return false;
    const cards = this.props.sorceryCards || [];
    return pile.cards.some((entry) => {
      const full = cards.find((c) => c.unique_id === entry.cardId);
      return isLegalMaskTarget(full);
    });
  };

  // Archimago helper. Pure cemetery inspection. Mirrors the
  // _collectionHasAvatar pattern: gate logic + resolve path read from
  // one source of truth so the action menu only surfaces the ability
  // when the cost can actually be paid.
  _cemeteryHasThreeMagic = (controller) => {
    const ownerRotated = controller === 'p2';
    const cemetery = this.state.gameState.piles.find(
      (p) => p.name?.startsWith('Cemetery') && !!p.rotated === ownerRotated,
    );
    if (!cemetery || cemetery.cards.length < 3) return false;
    return cemeteryHasEnoughMagic(cemetery.cards, this.props.sorceryCards || [], 3);
  };

  // Deathspeaker gate. At least one dead Minion in the controller's
  // Cemetery. Same source of truth as the pick resolver.
  _cemeteryHasMinion = (controller) => {
    const ownerRotated = controller === 'p2';
    const cemetery = this.state.gameState.piles.find(
      (p) => p.name?.startsWith('Cemetery') && !!p.rotated === ownerRotated,
    );
    if (!cemetery || cemetery.cards.length === 0) return false;
    return cemeteryHasMinion(cemetery.cards, this.props.sorceryCards || []);
  };

  _activateAbility = (cardInstance, entry) => automationRuntime.activateAbility(this, cardInstance, entry);

  _resolveActivatedAbility = (cardInstance, entry) => automationRuntime.resolveActivatedAbility(this, cardInstance, entry);

  _resolveDiscardCostPick = (handCard) => automationRuntime.resolveDiscardCostPick(this, handCard);

  _togglePileSelectCard = (card) => pileMultiSelect.togglePileSelectCard(this, card);

  _confirmPileSelect = () => pileMultiSelect.confirmPileSelect(this);

  _cancelPileSelect = () => pileMultiSelect.cancelPileSelect(this);

  _cancelDiscardCostPick = () => automationRuntime.cancelDiscardCostPick(this);

  _resolveSacrificeCostPick = (controller) => automationRuntime.resolveSacrificeCostPick(this, controller);

  handlePromptConfirm = (promptId, choiceValue) => automationRuntime.handlePromptConfirm(this, promptId, choiceValue);

  _applyEffectsForConfirm = (prompt, sourceEntry, effects, cardInstance, controller) => automationRuntime.applyEffectsForConfirm(this, prompt, sourceEntry, effects, cardInstance, controller);

  handlePromptCancel = (promptId) => automationRuntime.handlePromptCancel(this, promptId);

  _maybeEnqueuePlacementPrompt = (cardInstance) => realmTriggers.maybeEnqueuePlacementPrompt(this, cardInstance);

  applySitePlayEffects = (cardInstance) => realmTriggers.applySitePlayEffects(this, cardInstance);

  _fireOnEdgeTraverseAt = (unit, fromCell, toCell) => realmTriggers.fireOnEdgeTraverseAt(this, unit, fromCell, toCell);

  _fireOnEnterLocationAt = (enteringUnit, cell, reason = 'move') => realmTriggers.fireOnEnterLocationAt(this, enteringUnit, cell, reason);

  _fireOnSpellCast = (spellCard) => realmTriggers.fireOnSpellCast(this, spellCard);

  _fireOnDraw = (player) => realmTriggers.fireOnDraw(this, player);

  _fireOnTap = (tappedCard) => realmTriggers.fireOnTap(this, tappedCard);

  _fireOnSummonHere = (summonedUnit, cell) => realmTriggers.fireOnSummonHere(this, summonedUnit, cell);

  _fireRealmTrigger = (triggerType, watchTargetCard) => realmTriggers.fireRealmTrigger(this, triggerType, watchTargetCard);

  _resolveSilentRealmEntries = (watcher, triggerType, watchTargetCard) => realmTriggers.resolveSilentRealmEntries(this, watcher, triggerType, watchTargetCard);

  _applyWatchTargetDamage = (target, source, amount) => realmTriggers.applyWatchTargetDamage(this, target, source, amount);

  _fireDeathriteOnLeave = (cardInstance, destinationPileName) => realmTriggers.fireDeathriteOnLeave(this, cardInstance, destinationPileName);

  _disposeCardEffectsOnLeave = (cardInstance, broadcast = true) => realmTriggers.disposeCardEffectsOnLeave(this, cardInstance, broadcast);

  removeCardFromTable = (cardInstance, broadcast = true, opts = {}) => cardPileLifecycle.removeCardFromTable(this, cardInstance, broadcast, opts);

  sendToHand = (cardInstance) => cardPileLifecycle.sendToHand(this, cardInstance);

  _returnCardToOwnerHand = (cardInstance) => cardPileLifecycle.returnCardToOwnerHand(this, cardInstance);

  banishCard = (cardInstance) => cardPileLifecycle.banishCard(this, cardInstance);

  deleteToken = (tokenInstance) => cardPileLifecycle.deleteToken(this, tokenInstance);

  spawnDice = (dieType, point) => tokensAndSearch.spawnDice(this, dieType, point);

  _setDiceTrayRig = (rig) => { this._diceTrayRig = rig; };

  beginDiceTrayDrag = (dieType, event) => diceTrayDrag.beginDiceTrayDrag(this, dieType, event);

  moveDiceTrayDrag = (event) => diceTrayDrag.moveDiceTrayDrag(this, event);

  endDiceTrayDrag = (event, opts) => diceTrayDrag.endDiceTrayDrag(this, event, opts);

  rollDice = (diceInstance) => tokensAndSearch.rollDice(this, diceInstance);

  // ── Site-based resource tracking ────────────────────────────────
  //
  // Walks this.meshes to find all site cards owned by `player`, then
  // sets the elemental affinity trackers (earth, water, fire, wind) to
  // the sum of those sites' thresholds. Called whenever a site enters
  // or leaves the table.
  //
  // Mana is NOT touched here. It resets only on turn start (inside
  // passTurn) so that mid-turn spending isn't overwritten by placing a
  // new site.

  // Check whether a site card actually produces mana. In Sorcery TCG
  // every site provides 1 mana unless its rules text begins with
  // "Provides no mana." or "Provides no mana,..." (Rubble, Wedding
  // Hall, ...). Sites with "Provides no mana or threshold unless/if..."
  // (Pristine Paradise, The Colour Out of Space) are conditional and
  // handled by their own automations, not this default gate.
  _siteProvidesMana = (cardInstance) => {
    const fullCard = this._getFullCard(cardInstance.cardId);
    const rules = (fullCard?.functional_text_plain || fullCard?.functional_text || '').trim();
    return !/^Provides no mana[.,]/i.test(rules);
  };

  recalculateSiteAffinities = (player) => realmTriggers.recalculateSiteAffinities(this, player);

  countSitesForPlayer = (player) => realmTriggers.countSitesForPlayer(this, player);

  _runPhaseSteps = (phase, player) => turnPhaseRunner.runPhaseSteps(this, phase, player);

  _runPhaseStep = (phase, step, player) => turnPhaseRunner.runPhaseStep(this, phase, step, player);


  _stepUntapAll = (player) => turnPhaseRunner.stepUntapAll(this, player);

  _stepProvideMana = (player) => turnPhaseRunner.stepProvideMana(this, player);

  _stepFireTriggers = (trigger, player) => turnPhaseRunner.stepFireTriggers(this, trigger, player);

  _resolveSilentTurnTriggerEntries = (card, trigger, player, silentOpSet) => turnPhaseRunner.resolveSilentTurnTriggerEntries(this, card, trigger, player, silentOpSet);

  _stepDrawCard = (player) => turnPhaseRunner.stepDrawCard(this, player);

  _rehydrateDrawPhaseChoice = (descriptor) => turnPhaseRunner.rehydrateDrawPhaseChoice(this, descriptor);

  _stepHealMinions = (_player) => turnPhaseRunner.stepHealMinions(this, _player);

  _stepPruneExpiredEffects = (player) => turnPhaseRunner.stepPruneExpiredEffects(this, player);

  _stepEndUntilNextTurn = (player) => turnPhaseRunner.stepEndUntilNextTurn(this, player);

  passTurn = () => turnPhaseRunner.passTurn(this);

  _maybeResumeDeferredTurnPass = () => turnPhaseRunner.maybeResumeDeferredTurnPass(this);


  getLocalCemeterySnapPoint = () => cardPileLifecycle.getLocalCemeterySnapPoint(this);

  getLocalDeckSnapPoints = () => cardPileLifecycle.getLocalDeckSnapPoints(this);

  getLocalDeckDropTarget = (x, z) => cardPileLifecycle.getLocalDeckDropTarget(this, x, z);

  isWithinLocalCemeteryDropZone = (x, z) => cardPileLifecycle.isWithinLocalCemeteryDropZone(this, x, z);

  getLocalCemeteryPileMesh = () => cardPileLifecycle.getLocalCemeteryPileMesh(this);

  updateCemeteryDragHover = (worldX, worldZ) => cardPileLifecycle.updateCemeteryDragHover(this, worldX, worldZ);

  clearCemeteryDragHover = () => cardPileLifecycle.clearCemeteryDragHover(this);

  getLocalDeckPileMesh = (deckName) => cardPileLifecycle.getLocalDeckPileMesh(this, deckName);

  updateDeckDragHover = (worldX, worldZ) => cardPileLifecycle.updateDeckDragHover(this, worldX, worldZ);

  clearDeckDragHover = () => cardPileLifecycle.clearDeckDragHover(this);

  findOrCreateCemetery = (rotated) => cardPileLifecycle.findOrCreateCemetery(this, rotated);

  findOrCreateCollection = (rotated = null) => cardPileLifecycle.findOrCreateCollection(this, rotated);

  findOrCreateBanished = (rotated) => cardPileLifecycle.findOrCreateBanished(this, rotated);


  turnToRubble = (cardInstance, opts = {}) => cardPileLifecycle.turnToRubble(this, cardInstance, opts);

  deleteDice = (diceInstance) => tokensAndSearch.deleteDice(this, diceInstance);

  setDiceValue = (diceInstance, value) => tokensAndSearch.setDiceValue(this, diceInstance, value);

  findPileByName = (name) => cardPileLifecycle.findPileByName(this, name);

  // Authoritative "is the local user currently playing the rotated
  // (P2) side?" In multiplayer this is always tied to isHost. In
  // casual sandbox the local user plays both sides and Tab switches
  // which one is currently active; every drop-routing / "local" helper
  // reads through this so drag-to-cemetery-etc targets the correct
  // side based on whichever player the user is controlling right now.
  getActivePlayerRotated = () => {
    if (this.props.isCasualSandbox) return this.state.activePlayerNum === 2;
    return !this.state.isHost;
  };

  // v1.104. "is the CAMERA rotated 180° right now?" Different from
  // getActivePlayerRotated specifically in casual sandbox: the sandbox
  // camera stays fixed at P1's seat regardless of Tab, so any visual
  // affordance that needs to land on the player's near edge (element
  // pips at cell-bottom) should anchor to the CAMERA's perspective,
  // not the active-turn player. In MP both values agree.
  _localCameraRotated = () => {
    if (this.props.isCasualSandbox) return false;
    return !this.state.isHost;
  };

  findLocalPileByName = (name) => cardPileLifecycle.findLocalPileByName(this, name);

  findPileByNameForPlayer = (name, playerNum) => cardPileLifecycle.findPileByNameForPlayer(this, name, playerNum);

  findDestinationPile = (cardInstance, pileName) => cardPileLifecycle.findDestinationPile(this, cardInstance, pileName);

  sendCardToPile = (cardInstance, pileName, shouldShuffle = false, position = 'top', opts = {}) => cardPileLifecycle.sendCardToPile(this, cardInstance, pileName, shouldShuffle, position, opts);

  _drainPileDepartures = () => cardPileLifecycle.drainPileDepartures(this);

  sendHandCardToPile = (cardInstance, pileName, shouldShuffle = false, position = 'top') => cardPileLifecycle.sendHandCardToPile(this, cardInstance, pileName, shouldShuffle, position);

  toggleHandCardReveal = (cardInstance) => cardPileLifecycle.toggleHandCardReveal(this, cardInstance);

  revealEntireHand = (cardInstance) => cardPileLifecycle.revealEntireHand(this, cardInstance);

  toggleHandCardFaceDown = (cardInstance) => cardPileLifecycle.toggleHandCardFaceDown(this, cardInstance);

  shufflePileAction = (pile) => cardPileLifecycle.shufflePileAction(this, pile);

  toggleCollectionRevealed = () => cardPileLifecycle.toggleCollectionRevealed(this);

  // Some avatars (Druid) flip as a one-way gameplay state change
  // triggered by an activated ability. Hotkey + context-menu flips
  // must NOT fire on those. Accidentally enabling a back-face aura
  // is bad. Returns true when the card may flip via the F hotkey,
  // bulk-flip, or context-menu Flip option.
  _canFlipByHotkey = (cardInstance) => {
    if (cardInstance?.type !== 'Avatar') return true;
    const rules = resolveAvatarRules(cardInstance);
    return rules?.flipPolicy !== 'ability_only';
  };

  _toastFlipLocked = (cardInstance) => {
    toast.message(t('{name} flips via its activated ability, not by hand.', { name: cardInstance?.name || t('This avatar') }));
  };

  flipCard = (cardInstance, mesh, opts = {}) => cardStateOps.flipCard(this, cardInstance, mesh, opts);

  // ── Board mesh reconciliation (scene = f(authoritative card state)) ──
  _disposeCardMesh = (mesh) => boardReconciler.disposeCardMesh(this, mesh);
  _removeCardBody = (mesh) => removeCardBody(this.physicsWorld, mesh);
  _reconcileBoardMeshes = () => boardReconciler.reconcileBoardMeshes(this);
  _requestBoardReconcile = () => { this._boardReconcileDirty = true; };
  _reconcileOnSnapshot = (ids, cards) => boardReconciler.reconcileOnSnapshot(this, ids, undefined, cards);

  _applyControllerFlip = (card, mesh, nextRotated, opts = {}) => cardStateOps.applyControllerFlip(this, card, mesh, nextRotated, opts);

  switchCardOwnership = (cardInstance, mesh) => cardStateOps.switchCardOwnership(this, cardInstance, mesh);

  duplicateCard = (cardInstance) => cardPileLifecycle.duplicateCard(this, cardInstance);

  _applyTap = (card, mesh, nextValue, opts = {}) => cardStateOps.applyTap(this, card, mesh, nextValue, opts);

  tapCard = (cardInstance, mesh) => cardStateOps.tapCard(this, cardInstance, mesh);


  toggleCardStatus = (cardInstance, statusKey) => cardStateOps.toggleCardStatus(this, cardInstance, statusKey);


  getValidEquipTargets = (sourceCard) => equipActions.getValidEquipTargets(this, sourceCard);


  registerEquipOverlayRef = (id, kind, el) => overlayProjections.registerEquipOverlayRef(this, id, kind, el);

  registerCellTargetOverlayRef = (cellKey, el) => overlayProjections.registerCellTargetOverlayRef(this, cellKey, el);

  registerCellMentionOverlayRef = (cellKey, el) => overlayProjections.registerCellMentionOverlayRef(this, cellKey, el);

  registerProjectilePathOverlayRef = (refKey, el) => overlayProjections.registerProjectilePathOverlayRef(this, refKey, el);

  registerCellPipOverlayRef = (stripKey, el) => overlayProjections.registerCellPipOverlayRef(this, stripKey, el);

  registerMovementReachOverlayRef = (cellKey, el) => overlayProjections.registerMovementReachOverlayRef(this, cellKey, el);

  buildMovementReachCells = () => overlayProjections.buildMovementReachCells(this);

  buildHarbingerReachCells = () => overlayProjections.buildHarbingerReachCells(this);

  buildDragonReachCells = () => overlayProjections.buildDragonReachCells(this);
  buildTownReachCells = () => overlayProjections.buildTownReachCells(this);

  buildPercevalTargetCells = () => overlayProjections.buildPercevalTargetCells(this);

  _projectCellQuad = (grid, col, row, camera, rect) => overlayProjections.projectCellQuad(this, grid, col, row, camera, rect);

  _applyCellQuadStyle = (el, projected, pad = 1) => overlayProjections.applyCellQuadStyle(this, el, projected, pad);

  updateMovementReachOverlays = () => overlayProjections.updateMovementReachOverlays(this);

  buildRegionViewCells = () => overlayProjections.buildRegionViewCells(this);

  _countSubsurfaceCells = () => viewTransitions.countSubsurfaceCells(this);

  _syncRegionViewTints = () => viewTransitions.syncRegionViewTints(this);

  _disposeRegionViewTints = () => viewTransitions.disposeRegionViewTints(this);

  _syncSubsurfaceSceneState = () => viewTransitions.syncSubsurfaceSceneState(this);

  _animateHarbingerCeremony = (opts) => harbingerCeremony.animateHarbingerCeremony(this, opts);

  _settleHarbingerCeremony = () => harbingerCeremony.settleHarbingerCeremony(this);

  acceptHarbingerCeremony = () => harbingerCeremony.acceptHarbingerCeremony(this);

  cancelHarbingerCeremony = () => harbingerCeremony.cancelHarbingerCeremony(this);

  _harbingerPortraitUrl = (avatarCard) => harbingerCeremony.harbingerPortraitUrl(this, avatarCard);

  _syncHarbingerCircles = () => harbingerCeremony.syncHarbingerCircles(this);

  _maybeFireHarbingerSummonFlash = (card, fullCard, dropCell) => harbingerCeremony.maybeFireHarbingerSummonFlash(this, card, fullCard, dropCell);

  _disposeHarbingerCircles = () => harbingerCeremony.disposeHarbingerCircles(this);

  buildCellPipEntries = () => overlayProjections.buildCellPipEntries(this);

  updateCellElementPipOverlays = () => overlayProjections.updateCellElementPipOverlays(this);

  // S3. Within-cell stack + fan interaction.
  buildCellStackBadges = () => cellStackFan.buildCellStackBadges(this);

  registerCellStackBadgeOverlayRef = (cellKey, el) => cellStackFan.registerCellStackBadgeOverlayRef(this, cellKey, el);

  updateCellStackBadgeOverlays = () => cellStackFan.updateCellStackBadgeOverlays(this);

  // D5/S3. Focused-cell fan (cell → fan → act).
  updateCellHoverHighlight = () => cellFocus.updateCellHoverHighlight(this);

  updateCellFanChrome = () => cellFocus.updateCellFanChrome(this);

  registerFanChromeRef = (name, el) => {
    if (!el) this.fanChromeRefs.delete(name);
    else this.fanChromeRefs.set(name, el);
  };

  _refreshCellFocus = () => cellFocus.refreshCellFocus(this);

  _closeCellFocus = (opts) => cellFocus.closeCellFocus(this, opts);

  _openCellFocus = (cellKey) => cellFocus.openCellFocus(this, cellKey);

  _tickPoseGlides = () => cellFocus.tickPoseGlides(this);

  // Re-fold every board pose to server truth (fan collapse flies cards home).
  _refoldPositions = () => zoneCmd.applyLivePositionsFromMirror(this, this._authMirror?.getState?.());


  _resolveEquipTargetType = () => equipActions.resolveEquipTargetType(this);

  getEquipTargetIds = () => equipActions.getEquipTargetIds(this);

  toggleEquipPending = (cardInstance) => equipActions.toggleEquipPending(this, cardInstance);

  clearEquipPending = () => equipActions.clearEquipPending(this);

  commitEquipPending = (targetCard) => equipActions.commitEquipPending(this, targetCard);

  updateEquipPendingOverlays = () => overlayProjections.updateEquipPendingOverlays(this);

  _syncTargetHaloMeshes = () => selectionGroups.syncTargetHaloMeshes(this);

  updateCellTargetOverlays = () => overlayProjections.updateCellTargetOverlays(this);

  updateCellMentionOverlays = () => overlayProjections.updateCellMentionOverlays(this);

  updateProjectilePathOverlays = () => overlayProjections.updateProjectilePathOverlays(this);

  updateInvalidCellHoverOverlay = () => overlayProjections.updateInvalidCellHoverOverlay(this);


  openEquipModal = (sourceCard) => equipActions.openEquipModal(this, sourceCard);

  closeEquipModal = () => this.setState({ equipModal: null });

  equipCardTo = (sourceCard, targetSummary) => equipActions.equipCardTo(this, sourceCard, targetSummary);

  _toggleLanceEquip = (cardInstance) => equipActions.toggleLanceEquip(this, cardInstance);
  _grantLanceOnEntry = (cardInstance) => equipActions.grantLanceOnEntry(this, cardInstance);

  detachEquipment = (hostCard, equippedId) => equipActions.detachEquipment(this, hostCard, equippedId);

  _detachAllEquipmentToField = (hostCard, anchor, opts) => equipActions.detachAllEquipmentToField(this, hostCard, anchor, opts);

  _materializeCarried = (card) => equipActions.materializeCarried(this, card);

  clearAllStatuses = (cardInstance) => cardStateOps.clearAllStatuses(this, cardInstance);

  rebuildStatusBadges = (cardId) => cardStateOps.rebuildStatusBadges(this, cardId);

  _syncCardEffects = (cardId, statuses) => cardStateOps.syncCardEffects(this, cardId, statuses);

  _resyncAllCardEffects = () => cardStateOps.resyncAllCardEffects(this);

  _removeAllCardEffects = (cardId) => cardStateOps.removeAllCardEffects(this, cardId);

  _tickCardEffects = (dt) => cardStateOps.tickCardEffects(this, dt);


  _queueOpponentSpawn = (deck, playerNum) => gameLifecycle.queueOpponentSpawn(this, deck, playerNum);

  _spawnQueuedOpponent = () => gameLifecycle.spawnQueuedOpponent(this);

  spawnSelectedDeck = (deck, playerNum = 1) => gameLifecycle.spawnSelectedDeck(this, deck, playerNum);

  _spawnOpponentCounts = (counts, playerNum = 2, avatarCard = null) => gameLifecycle.spawnOpponentCounts(this, counts, playerNum, avatarCard);

  updatePileMeshes = () => cardPileLifecycle.updatePileMeshes(this);


  updateOpponentHandMeshes = () => opponentPresence.updateOpponentHandMeshes(this);


  _createBadgeSprite = (name, avatarUrl, borderColor) => opponentPresence.createBadgeSprite(this, name, avatarUrl, borderColor);

  _disposeBadge = (mesh) => opponentPresence.disposeBadge(this, mesh);

  updatePlayerBadges = () => opponentPresence.updatePlayerBadges(this);

  applySeatPerspective = (isHost) => opponentPresence.applySeatPerspective(this, isHost);


  beginHandDrag = (event, cardInstance, handPosition) => handState.beginHandDrag(this, event, cardInstance, handPosition);

  onHandDragMove = (event) => handState.onHandDragMove(this, event);

  onHandDragEnd = () => handState.onHandDragEnd(this);

  commitHandDragToTable = (event) => handState.commitHandDragToTable(this, event);

  handOffDragToHand = (cardInstance, event) => handState.handOffDragToHand(this, cardInstance, event);

  // --- Render ---

  // Flat map of the orchestration callbacks the GameContextMenu component
  // needs. Re-created per render (cheap) so the component receives the
  // latest references without us having to worry about stale closures.
  buildContextMenuActions = () => ({
    tapCard: (cardInstance, mesh) => { this.tapCard(cardInstance, mesh); this.closeContextMenu(); },
    flipCard: (cardInstance, mesh) => { this.flipCard(cardInstance, mesh); this.closeContextMenu(); },
    switchCardOwnership: (cardInstance, mesh) => { this.switchCardOwnership(cardInstance, mesh); this.closeContextMenu(); },
    duplicateCard: (cardInstance) => { this.duplicateCard(cardInstance); this.closeContextMenu(); },
    sendToHand: this.sendToHand,
    sendCardToPile: this.sendCardToPile,
    banishCard: this.banishCard,
    drawCard: this.handleDrawCardFromPile,
    shufflePile: this.shufflePileAction,
    openPileSearch: this.openPileSearch,
    drawPileToHand: this.drawPileToHand,
    openScry: this.openScry,
    sendHandCardToPile: this.sendHandCardToPile,
    toggleHandCardReveal: this.toggleHandCardReveal,
    revealEntireHand: this.revealEntireHand,
    toggleHandCardFaceDown: this.toggleHandCardFaceDown,
    deleteToken: this.deleteToken,
    rollDice: this.handleRollDiceAndClose,
    setDiceValue: this.setDiceValue,
    deleteDice: this.deleteDice,
    groupSelected: this.handleGroupSelected,
    ungroup: this.handleUngroup,
    toggleCardStatus: (cardInstance, statusKey) => { this.toggleCardStatus(cardInstance, statusKey); },
    clearAllStatuses: (cardInstance) => { this.clearAllStatuses(cardInstance); },
    equipTo: this.equipCardTo,
    openEquipModal: this.openEquipModal,
    takeOffMask: (cardInstance) => { this._takeOffMask(cardInstance); this.closeContextMenu(); },
    pileCardTakeToHand: (pile, cardInstance) => { this.takeCardFromPile(pile, cardInstance); this.closeContextMenu(); },
    pileCardSpawnOnField: (pile, cardInstance) => { this._startPileCardPlacement(pile, cardInstance); this.closeContextMenu(); },
    pileCardBanish: (pile, cardInstance) => { this.banishCardFromPile(pile, cardInstance); },
    // v1.66. Predicate so the pile context menu can hide / disable
    // freeform draw items in real matches. Sandbox + offline keep them.
    canFreeformDraw: () => this._canFreeformDraw(),
  });

  handleGroupSelected = (clickedCardInstance) => selectionGroups.handleGroupSelected(this, clickedCardInstance);

  handleUngroup = (clickedCardInstance) => selectionGroups.handleUngroup(this, clickedCardInstance);

  takeCardFromPile = (pile, cardInstance, opts) => cardPileLifecycle.takeCardFromPile(this, pile, cardInstance, opts);

  renderCardInspector = () => boardRender.renderCardInspector(this);

  pushInspector = (equippedCard) => menusAndInspector.pushInspector(this, equippedCard);

  openOpponentHandInspector = (cardData) => menusAndInspector.openOpponentHandInspector(this, cardData);

  popInspector = () => menusAndInspector.popInspector(this);

  // --- Context menu action wrappers ---
  // Thin orchestration helpers that the GameContextMenu component calls so
  // it can stay purely presentational.

  closeContextMenu = () => this.setState({ contextMenu: null });

  addAdvisoryLog = (text, card, values) => logbookChat.addAdvisoryLog(this, text, card, values);

  logAction = (text, values, kind) => logbookChat.logAction(this, text, values, kind);

  logSystem = (text, values, kind) => logbookChat.logSystem(this, text, values, kind);

  addLogEntry = (text, actor = 'opponent', kind = 'action') => logbookChat.addLogEntry(this, text, actor, kind);

  setLogbookScrollRef = (el) => logbookChat.setLogbookScrollRef(this, el);

  handleLogbookMentionHover = (cardId, element) => logbookChat.handleLogbookMentionHover(this, cardId, element);

  handleLogbookMentionHoverEnd = () => logbookChat.handleLogbookMentionHoverEnd(this);

  setMentionCellHighlights = (cardIds) => logbookChat.setMentionCellHighlights(this, cardIds);

  handleOpenLogbookMention = (cardId) => logbookChat.handleOpenLogbookMention(this, cardId);

  sendChatMessage = () => logbookChat.sendChatMessage(this);

  updateLogbookThumb = () => logbookChat.updateLogbookThumb(this);

  closeAllMenus = () => this.setState({
    showGameMenu: false,
    showDiceMenu: false,
    showAmbienceMenu: false,
    showPlaymatPicker: false,
    showDeckPicker: false,
    showCodex: false,
    contextMenu: null,
    ringMenu: null,
  });

  renderLightingControls = () => boardRender.renderLightingControls(this);

  hasOpenOverlay = () => menusAndInspector.hasOpenOverlay(this);

  // Opening draw: each player automatically draws 3 from their Atlas
  // (site pile) and 3 from their Spellbook at the start of a match.
  // Staggered with short delays so the cards fan into the hand one at a
  // time instead of appearing all at once. Only called for competitive
  // and friend matches. Never for solo/offline sessions.
  // Resolve the current local avatar's rule overrides. Centralized so
  // every rule-consuming call site reads from one source and a future
  // rule (e.g. format-specific override, Draft vs Constructed) can be
  // layered here without touching each consumer.
  getAvatarRules = () => resolveAvatarRules(this.state.gameState?.avatarCard);

  _mulliganKeys(playerNum) {
    return mulliganController.mulliganKeys(this, playerNum);
  }

  _localPlayerNum() {
    return mulliganController.localPlayerNum(this);
  }

  acceptMulligan = () => mulliganController.acceptMulligan(this);

  _findAvatarForPlayer(playerNum) {
    return mulliganController.findAvatarForPlayer(this, playerNum);
  }

  toggleMulliganFlag = (cardInstance) => mulliganController.toggleMulliganFlag(this, cardInstance);

  executeMulligan = () => mulliganController.executeMulligan(this);

  keepHand = () => mulliganController.keepHand(this);

  _afterMulliganComplete = (player) => mulliganController.afterMulliganComplete(this, player);

  _replayDeferredDrawPrompt = (player) => mulliganController.replayDeferredDrawPrompt(this, player);

  drawPileToHand = (pile) => cardPileLifecycle.drawPileToHand(this, pile);

  _buildCatalogTokenEntries = () => tokensAndSearch.buildCatalogTokenEntries(this);

  // Every spawnable token in one list. Catalog game tokens followed by
  // the player's cosmetic custom tokens. Backs both the B-key Token
  // Browser (`buildTokenPile`) and the left-edge quick-access token
  // drawer, so the two always offer an identical set.
  _allTokenEntries = () => [...this._buildCatalogTokenEntries(), ...this.buildCustomTokenEntries()];

  // Virtual pile containing every token card in the game, flagged
  // `infinite: true` so the take-to-hand / take-to-field callbacks clone
  // each pick instead of splicing it out of the array.
  buildTokenPile = () => ({
    id: 'token-pile',
    name: k('Tokens'),
    cards: this._allTokenEntries(),
    infinite: true,
  });

  buildCustomTokenEntries = () => tokensAndSearch.buildCustomTokenEntries(this);

  openPileSearch = (pile) => tokensAndSearch.openPileSearch(this, pile);

  _startSearchEffect = (sourceCard, controller, args, sourceName) => tokensAndSearch.startSearchEffect(this, sourceCard, controller, args, sourceName);

  _searchEffectMatches = (cardInstance, filter) => tokensAndSearch.searchEffectMatches(this, cardInstance, filter);

  _resolveSearchPick = (cardInstance) => tokensAndSearch.resolveSearchPick(this, cardInstance);

  // ── Scry ───────────────────────────────────────────────────────
  // Sandbox scrying: right-click a pile → "Scry from top/bottom".
  // Peeks are non-destructive. We track the peeked card IDs in state
  // and don't mutate the pile until the player commits (Done or picks
  // a destination for the current card). Cancel/Escape is a no-op.

  _computePileAnchor = (pile) => scryController.computePileAnchor(this, pile);

  openScry = (pile, from, opts = {}) => scryController.openScry(this, pile, from, opts);

  scryAssignCurrent = (destination) => scryController.scryAssignCurrent(this, destination);

  scryPeekNext = () => scryController.scryPeekNext(this);

  scrySetActive = (idx) => scryController.scrySetActive(this, idx);

  scryKeepAndDone = () => scryController.scryKeepAndDone(this);

  scryCancel = () => scryController.scryCancel(this);

  scryCastFromTop = () => scryController.scryCastFromTop(this);

  _resolveCastTopOfSpellbook = (cardInstance, entry, controller) => automationRuntime.resolveCastTopOfSpellbook(this, cardInstance, entry, controller);

  playerDrawFromPile = (pileId) => cardPileLifecycle.playerDrawFromPile(this, pileId);

  handleDrawCardFromPile = (pileId) => {
    this.playerDrawFromPile(pileId);
    this.setState({ contextMenu: null });
  };

  handleRollDiceAndClose = (diceInstance) => {
    this.rollDice(diceInstance);
    this.setState({ contextMenu: null });
  };

  handlePileSearchClose = () => tokensAndSearch.handlePileSearchClose(this);

  handlePileSearchQueryChange = (value) => tokensAndSearch.handlePileSearchQueryChange(this, value);

  spawnCardFromPile = (sourcePile, card) => tokensAndSearch.spawnCardFromPile(this, sourcePile, card);

  spawnTokenFromDrawer = (token, opts = {}) => tokensAndSearch.spawnTokenFromDrawer(this, token, opts);

  handlePileSearchCardContextMenu = (cardInstance, event) => tokensAndSearch.handlePileSearchCardContextMenu(this, cardInstance, event);

  banishCardFromPile = (sourcePile, cardInstance, opts) => tokensAndSearch.banishCardFromPile(this, sourcePile, cardInstance, opts);

  renderDeckPicker = () => boardRender.renderDeckPicker(this);

  loadAndSpawnDeck = (deckId, playerNum = 1) => gameLifecycle.loadAndSpawnDeck(this, deckId, playerNum);

  confirmSpawnDeck = () => gameLifecycle.confirmSpawnDeck(this);

  _resolveFirstPlayerRoll = (opts = {}) => gameLifecycle.resolveFirstPlayerRoll(this, opts);

  _onFirstPlayerRollStateChange = (rollState) => gameLifecycle.onFirstPlayerRollStateChange(this, rollState);

  clearBoard = () => gameLifecycle.clearBoard(this);

  _resolveDeck = (deckId) => gameLifecycle.resolveDeck(this, deckId);

  doSpawnDeck = (deckId, playerNum) => gameLifecycle.doSpawnDeck(this, deckId, playerNum);


  initController = () => gamepadController.initController(this);

  teardownController = () => gamepadController.teardownController(this);

  handleGamepadButtonDown = (detail) => gamepadController.handleGamepadButtonDown(this, detail);

  handleGamepadButtonUp = (detail) => gamepadController.handleGamepadButtonUp(this, detail);

  handleGamepadAxis = (detail) => gamepadController.handleGamepadAxis(this, detail);

  controllerTick = () => gamepadController.controllerTick(this);

  controllerStartGrab = () => gamepadController.controllerStartGrab(this);

  controllerDropGrab = () => gamepadController.controllerDropGrab(this);

  controllerCancelGrab = () => gamepadController.controllerCancelGrab(this);

  dispatchControllerAction = (actionId) => gamepadController.dispatchControllerAction(this, actionId);


  render() {
    // Wraps the actual render body so we can measure how often Preact
    // re-runs this component (often due to setState in mousemove
    // handlers) and how long each render takes. Total render time per
    // window = avg * count, and that's what shows up as missed frames.
    perf.count('gameboard.render');
    const mark = perf.beginMark('gameboard.render');
    const out = this._renderBody();
    perf.endMark(mark);
    return out;
  }

  _renderBody = () => boardRender.renderBody(this);

  renderHand = (cards, position) => boardRender.renderHand(this, cards, position);

}
